import {
  ChangeEvent,
  FormEvent,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  BadgeCheck,
  Bell,
  BatteryMedium,
  Camera,
  Car,
  Check,
  ChevronDown,
  ChevronRight,
  CircleUserRound,
  ClipboardCheck,
  CloudDownload,
  Compass,
  Download,
  Droplets,
  Eye,
  EyeOff,
  FileArchive,
  FileUp,
  Footprints,
  Gauge,
  Heart,
  ImagePlus,
  Info,
  KeyRound,
  Layers3,
  LocateFixed,
  LockKeyhole,
  MapPinned,
  MapPin,
  Mail,
  Menu,
  Mountain,
  MoveHorizontal,
  Navigation,
  Pause,
  Play,
  Route,
  RefreshCw,
  Radar,
  Save,
  Search,
  Settings,
  Share2,
  ShieldCheck,
  Signal,
  Smartphone,
  Sparkles,
  Sun,
  Square,
  TentTree,
  Timer,
  Trash2,
  Upload,
  UserCheck,
  UserRoundPlus,
  Users,
  WifiOff,
  X,
} from "lucide-react";

import { Button, Progress, Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui";
import { TheraMapLibre } from "./components/TheraMapLibre";
import { MiMapaPage } from "./modules/mi-mapa/MiMapaPage";
import { setLocalBookmark } from "./modules/mi-mapa/local";
import type { MiMapaMapRenderProps } from "./modules/mi-mapa/map-contract";
import { Panorama360Viewer } from "./components/Panorama360Viewer";
import { RouteWeatherPanel } from "./components/RouteWeatherPanel";
import { fetchPointWeather, fetchRouteWeather, weatherConditionLabel, type RouteWeather } from "./lib/route-weather";
import { TheraMountainAIPage } from "./modules/thera-mountain-ai/TheraMountainAIPage";
import type { TheraRuntimeSnapshot } from "./modules/thera-mountain-ai/integration/TheraIntegrationInput";
import { selectMountainAIWeatherTarget } from "./modules/thera-mountain-ai/integration/MountainAIWeatherTarget";
import { TheraARExplorePage } from "./modules/thera-ar-explore/TheraARExplorePage";
import { NatureR03AppBridge } from "./modules/thera-ar-explore/nature-vision/r03-app-bridge/NatureR03AppBridge";
import {
  appendFieldRouteSample,
  finalizeFieldSession,
  type FieldRouteContext,
} from "./modules/thera-ar-explore/geo-ar/field/fieldSessionLogger";
import { Thera360RoutesPage } from "./modules/thera-360-routes/Thera360RoutesPage";
import { AdminArchitecturePanel } from "./modules/admin/AdminArchitecturePanel";
import { FieldSurveyContributionAdminPanel } from "./modules/admin/FieldSurveyContributionAdminPanel";
import { registerActivityFieldSurveyContribution, requestAccessWithConsent } from "./modules/data-foundation/consent-gate";
import { SurveyConsentSettings } from "./modules/data-foundation/SurveyConsentSettings";
import { isTerrainSenseEntryAvailable } from "./modules/terrain-sense/core/TerrainSenseFeatureFlags";
import { currentUiPlatform } from "./platform-ui/ui-platform";
import "./modules/product-skeleton.css";
import "./platform-ui/android-ui.css";
import "./platform-ui/pwa-ui.css";
import "./platform-ui/web-ui.css";
import {
  clearActivityDraft,
  loadActivityDraft,
  saveActivityDraft,
  type TrackPoint,
} from "./lib/offline-draft";
import {
  climbingPlaces,
  communityActivity,
  offlinePacks,
  routes,
  type ActivityKind,
  type RouteItem,
} from "./lib/thera-data";
import {
  getCurrentProfile,
  isSupabaseConfigured,
  listPendingMembers,
  observeSession,
  requestPasswordReset,
  reviewMember,
  signIn,
  signOut,
  updatePassword,
  type PendingMember,
  type TheraProfile,
} from "./lib/supabase";
import { contourFileFor, downloadMapPack, getRemoteMapInfo, hasStoredMapPack, removeStoredMapPack, terrainFileFor } from "./lib/offline-maps";
import {
  backgroundRecordingAvailable,
  discardNativeTrackingActivity,
  finalizeNativeTracking,
  getCurrentTheraPosition,
  getNativeTrackingSnapshot,
  pauseLocationRecorder,
  recoverNativeLocationPoints,
  reattachLocationRecorder,
  resumeLocationRecorder,
  startLocationRecorder,
  stopLocationRecorder,
  type RecorderTrackPoint,
} from "./lib/location-recorder";
import { cursorFromTrack, type NativeRecoveryCursor, type NativeTrackingSnapshot } from "./lib/native-tracking-recovery";
import {
  deleteLocalRouteDraft,
  listLocalRouteDrafts,
  saveLocalRouteDraft,
  type LocalRouteDraft,
  type LocalRouteFile,
  type LocalRoutePanorama,
} from "./lib/route-submission-store";
import {
  currentPlatform,
  listOwnRoutes,
  listPendingRoutes,
  listPublishedRoutes,
  reviewRoute,
  submitRoute,
  syncRouteDraft,
  type CloudRoute,
  type PendingRoute,
  type RouteGeoJson,
} from "./lib/route-workflow";
import {
  acceptedTrackPoint,
  activitySplits,
  activityGeoJson,
  hasReachedRouteStart,
  haversineKm,
  navigationStats,
  nearestRoutePosition,
  planRouteSegment,
  ROUTE_START_REACHED_M,
  routeCoordinates,
  routeWaypoints,
  trimTrackEndpoints,
  type ActivityMarker,
  type RouteWaypoint,
} from "./lib/navigation-core";
import {
  clearActiveNavigation,
  listActivityHistory,
  listPendingActivitySyncBatch,
  loadActiveNavigation,
  saveActiveNavigation,
  saveActivityHistory,
  updateActivitySyncStatus,
  type NavigationSession,
} from "./lib/navigation-session-store";
import { syncNavigationActivity } from "./lib/activity-sync";
import {
  createActivityShareCard,
  type ShareCardBackground,
  type ShareCardFormat,
} from "./lib/activity-share";
import { LIVE_SHARE_ENABLED } from "./lib/live-share";
import { isNativeAndroidPlaceSearch, searchAndroidPlaces } from "./lib/android-place-search";
import { removeMapLabelIndex, scheduleDownloadedMapLabelIndexes, scheduleMapLabelIndexForPack } from "./lib/android-map-label-index";
import { loadPublishedRouteCache, savePublishedRouteCache } from "./lib/route-cache";
import { clearCachedProfile, loadCachedProfile, saveCachedProfile } from "./lib/profile-cache";
import {
  fetchRoadApproachRoute,
  roadApproachProgress,
  type ApproachMode,
  type RoadApproachRoute,
} from "./lib/route-approach";
import {
  ECUADOR_MAP_PACKS,
  mapPackById,
  mapPackForPoint,
  mapPackForText,
} from "../shared/ecuador-map-packs";

const TerrainSensePage = lazy(() => import("./modules/terrain-sense/TerrainSensePage").then((module) => ({
  default: module.TerrainSensePage,
})));

type TabName =
  | "explorar"
  | "planificar"
  | "grabar"
  | "escalada"
  | "comunidad"
  | "offline"
  | "ai"
  | "ar"
  | "360"
  | "perfil"
  | "terrain-sense"
  | "admin"
  | "mi-mapa";

const WEB_OFFLINE_GROUPS = ["Nacional"] as const;
const ANDROID_OFFLINE_GROUPS = ["Provincia", "Sierra", "Costa", "Amazonía", "Insular"] as const;
const ANDROID_OFFLINE_PACKS = offlinePacks.filter((pack) => !pack.overview);
const WEB_ONLINE_PACKS = offlinePacks.filter((pack) => pack.overview);
type PhotoResult = {
  id: string;
  url: string;
  name: string;
  before: string;
  after: string;
  blob: Blob;
  width: number;
  height: number;
  altText: string;
};
type PanoramaResult = LocalRoutePanorama & { url: string };
type RouteFormState = {
  name: string;
  area: string;
  kind: ActivityKind;
  description: string;
  recommendations: string;
  estimatedHours: string;
};
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};
type WakeLockSentinelLike = { release: () => Promise<void> };
type RecordingMode = "selected-route" | "free";
type PlannerTool = "start" | "waypoint" | "finish" | null;
type PlannerPoint = {
  id: string;
  latitude: number;
  longitude: number;
  elevation: number | null;
  kind: "start" | "waypoint" | "finish";
};
type PlannerLeg = {
  id: string;
  fromId: string;
  toId: string;
  coordinates: number[][];
  distanceKm: number;
  durationSeconds: number | null;
  source: "routing" | "cache" | "direct";
};
type DeviationAlertMode = "none" | "vibration" | "message" | "both";
type PasswordFlow = "login" | "request" | "update";

type NativePendingRecovery = {
  snapshot: NativeTrackingSnapshot;
  points: RecorderTrackPoint[];
  cursor: NativeRecoveryCursor;
  session?: NavigationSession;
};

function filteredNativeRecoveryTrack(
  nativePoints: RecorderTrackPoint[],
  fallbackPoints: TrackPoint[] = [],
): TrackPoint[] {
  const source = nativePoints.length ? nativePoints : fallbackPoints;
  const ordered = [...source].sort((a, b) => a.timestamp - b.timestamp);
  const accepted: TrackPoint[] = [];
  for (const rawPoint of ordered) {
    const activeMilliseconds = "activeMilliseconds" in rawPoint && typeof rawPoint.activeMilliseconds === "number"
      ? rawPoint.activeMilliseconds
      : undefined;
    const activeSeconds = typeof activeMilliseconds === "number"
      ? Math.max(0, Math.floor(activeMilliseconds / 1000))
      : rawPoint.activeSeconds;
    const point: TrackPoint = {
      latitude: rawPoint.latitude,
      longitude: rawPoint.longitude,
      altitude: rawPoint.altitude ?? null,
      accuracy: rawPoint.accuracy,
      timestamp: rawPoint.timestamp,
      activeSeconds,
    };
    if (acceptedTrackPoint(accepted.at(-1), point)) accepted.push(point);
  }
  return accepted;
}

const LAST_AUTH_USER_KEY = "thera-last-auth-user-id";

function loadRememberedUserId() {
  try {
    return window.localStorage.getItem(LAST_AUTH_USER_KEY);
  } catch {
    return null;
  }
}

function rememberUserId(userId: string) {
  try {
    window.localStorage.setItem(LAST_AUTH_USER_KEY, userId);
  } catch {
    // El modo offline sigue funcionando aunque el almacenamiento del navegador falle.
  }
}

function clearRememberedUserId() {
  try {
    window.localStorage.removeItem(LAST_AUTH_USER_KEY);
  } catch {
    // Sin acción adicional.
  }
}
type PlaceSearchResult = {
  id: string;
  name: string;
  label: string;
  locality: string;
  region: string;
  country: string;
  kind: string;
  longitude: number;
  latitude: number;
  mapPackId?: string;
};

const fallbackRoute = routes[0];
const emptyRouteForm: RouteFormState = {
  name: "",
  area: "",
  kind: "senderismo",
  description: "",
  recommendations: "",
  estimatedHours: "",
};

function formatDuration(seconds: number) {
  const hours = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${hours}:${minutes}:${secs}`;
}

function formatDistance(kilometers: number) {
  return kilometers < 10
    ? kilometers.toFixed(2).replace(".", ",")
    : kilometers.toFixed(1).replace(".", ",");
}

function formatBytes(bytes: number) {
  if (!bytes) return "Tamaño pendiente";
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1).replace(".", ",")} GB`;
  return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
}

function compassDirection(degrees: number | null) {
  if (degrees === null || !Number.isFinite(degrees)) return "—";
  const directions = ["N", "NE", "E", "SE", "S", "SO", "O", "NO"];
  return directions[Math.round(degrees / 45) % directions.length];
}

function calculateDistance(
  points: Array<{ latitude: number; longitude: number }>,
) {
  return points
    .slice(1)
    .reduce(
      (total, point, index) => total + haversineKm(points[index], point),
      0,
    );
}

function calculateAscent(
  points: Array<{ elevation: number | null }>,
) {
  return Math.round(
    points.slice(1).reduce((total, point, index) => {
      const previous = points[index].elevation;
      if (previous === null || point.elevation === null) return total;
      const difference = point.elevation - previous;
      return difference > 0 && difference < 250 ? total + difference : total;
    }, 0),
  );
}

function routeGeoJson(
  points: Array<{ latitude: number; longitude: number; elevation: number | null }>,
  waypoints: RouteWaypoint[] = [],
): RouteGeoJson {
  const maximumPoints = 5000;
  const step = Math.max(1, Math.ceil(points.length / maximumPoints));
  const simplified = points.filter(
    (_point, index) => index % step === 0 || index === points.length - 1,
  );
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: { source: "THERA" },
        geometry: {
          type: "LineString",
          coordinates: simplified.map((point) =>
            point.elevation === null
              ? [point.longitude, point.latitude]
              : [point.longitude, point.latitude, point.elevation],
          ),
        },
      },
      ...waypoints.map((waypoint) => ({
        type: "Feature" as const,
        properties: {
          id: waypoint.id,
          name: waypoint.name,
          kind: waypoint.kind,
          description: waypoint.description ?? "",
        },
        geometry: {
          type: "Point" as const,
          coordinates: waypoint.elevation === null
            ? [waypoint.longitude, waypoint.latitude]
            : [waypoint.longitude, waypoint.latitude, waypoint.elevation],
        },
      })),
    ],
  };
}

function pointsFromGeoJson(value: RouteGeoJson) {
  return routeCoordinates(value).map((point) => ({
    longitude: point.longitude,
    latitude: point.latitude,
    elevation: point.elevation,
  }));
}

function xmlText(value: string) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}

function trackToGpx(points: TrackPoint[], name: string, markers: ActivityMarker[] = []) {
  const content = points
    .map(
      (point) =>
        `<trkpt lat="${point.latitude}" lon="${point.longitude}">${point.altitude === null ? "" : `<ele>${point.altitude}</ele>`}<time>${new Date(point.timestamp).toISOString()}</time></trkpt>`,
    )
    .join("");
  const waypointContent = markers.map((marker) =>
    `<wpt lat="${marker.latitude}" lon="${marker.longitude}">${marker.elevation === null ? "" : `<ele>${marker.elevation}</ele>`}<time>${new Date(marker.timestamp).toISOString()}</time><name>${xmlText(marker.name)}</name><type>${marker.kind}</type>${marker.description ? `<desc>${xmlText(marker.description)}</desc>` : ""}</wpt>`,
  ).join("");
  return new Blob(
    [
      `<?xml version="1.0" encoding="UTF-8"?><gpx version="1.1" creator="THERA" xmlns="http://www.topografix.com/GPX/1/1">${waypointContent}<trk><name>${xmlText(name)}</name><trkseg>${content}</trkseg></trk></gpx>`,
    ],
    { type: "application/gpx+xml" },
  );
}

function cloudRouteToItem(route: CloudRoute): RouteItem {
  const hours = Math.floor((route.estimated_seconds ?? 0) / 3600);
  const minutes = Math.round(((route.estimated_seconds ?? 0) % 3600) / 60);
  return {
    id: route.id,
    name: route.name,
    area: route.area,
    kind: route.kind,
    distance:
      route.distance_m === null
        ? "Por validar"
        : `${formatDistance(route.distance_m / 1000)} km`,
    time:
      route.estimated_seconds === null
        ? "Por validar"
        : `${hours ? `${hours} h ` : ""}${minutes} min`,
    ascent: route.ascent_m === null ? "Por validar" : `+${route.ascent_m} m`,
    difficulty: "Información validada",
    points: "22,94 67,72 117,56 171,40 226,35 285,42 346,34",
    tags: [route.kind, "Comunidad THERA"],
    status: "Publicada",
    geojson: route.path_geojson ?? undefined,
  };
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 500);
}

function getErrorMessage(error: unknown, fallback: string) {
  if (typeof error === "object" && error !== null && "message" in error) {
    return String(error.message);
  }
  return fallback;
}

function geoJsonToOverlayPoints(value?: RouteGeoJson) {
  const coordinates = routeCoordinates(value).map((point) => [point.longitude, point.latitude]);
  if (coordinates.length < 2) return null;
  const longitudes = coordinates.map((coordinate) => coordinate[0]);
  const latitudes = coordinates.map((coordinate) => coordinate[1]);
  const minLongitude = Math.min(...longitudes);
  const maxLongitude = Math.max(...longitudes);
  const minLatitude = Math.min(...latitudes);
  const maxLatitude = Math.max(...latitudes);
  const longitudeRange = Math.max(maxLongitude - minLongitude, 0.000001);
  const latitudeRange = Math.max(maxLatitude - minLatitude, 0.000001);
  const step = Math.max(1, Math.ceil(coordinates.length / 70));
  return coordinates
    .filter((_coordinate, index) => index % step === 0 || index === coordinates.length - 1)
    .map((coordinate) => {
      const x = 22 + ((coordinate[0] - minLongitude) / longitudeRange) * 324;
      const y = 94 - ((coordinate[1] - minLatitude) / latitudeRange) * 76;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

function hasValidRouteTrace(route: RouteItem) {
  const coordinates = routeCoordinates(route.geojson).map((point) => [point.longitude, point.latitude]);
  return coordinates.length >= 2 && coordinates.every((coordinate) => {
    const longitude = Number(coordinate[0]);
    const latitude = Number(coordinate[1]);
    return Number.isFinite(longitude)
      && Number.isFinite(latitude)
      && longitude >= -180
      && longitude <= 180
      && latitude >= -90
      && latitude <= 90;
  });
}

function TopographicMap({
  route,
  mapPackId,
  recording = false,
  climbing = false,
  recordedTrack = [],
  currentPosition,
  approachTarget,
  roadApproachCoordinates,
  followUser = false,
  recenterSignal = 0,
  searchTarget,
  allowGuestViewer = false,
  persistDownloadedMaps = true,
  showMissingTraceNotice = true,
  onSnapshotProvider,
  plannerPoints = [],
  plannerRouteCoordinates = [],
  plannerRouteHasFallback = false,
  plannerActive = false,
  onPlannerMapClick,
}: {
  route: RouteItem;
  mapPackId?: string;
  recording?: boolean;
  climbing?: boolean;
  recordedTrack?: TrackPoint[];
  currentPosition?: TrackPoint;
  approachTarget?: [number, number];
  roadApproachCoordinates?: number[][];
  followUser?: boolean;
  recenterSignal?: number;
  searchTarget?: { longitude: number; latitude: number; label: string };
  allowGuestViewer?: boolean;
  persistDownloadedMaps?: boolean;
  showMissingTraceNotice?: boolean;
  onSnapshotProvider?: (provider: (() => string | null) | null) => void;
  plannerPoints?: PlannerPoint[];
  plannerRouteCoordinates?: number[][];
  plannerRouteHasFallback?: boolean;
  plannerActive?: boolean;
  onPlannerMapClick?: (coordinate: { latitude: number; longitude: number }) => void;
}) {
  const useRegionalMap = import.meta.env.VITE_REAL_MAPS === "true" && !climbing;
  const hasTrace = hasValidRouteTrace(route);
  if (useRegionalMap) {
    const firstCoordinate = routeCoordinates(route.geojson)[0];
    const selectedPack = mapPackById(mapPackId)
      ?? mapPackForText(route.area)
      ?? (firstCoordinate ? mapPackForPoint(firstCoordinate.longitude, firstCoordinate.latitude, route.area) : null);
    const mapConfig = selectedPack
      ? { file: selectedPack.file, center: selectedPack.center, zoom: selectedPack.zoom }
      : null;
    if (mapConfig) {
      return (
        <div className="route-map-shell">
          <TheraMapLibre
            key={`${route.id}-${mapConfig.file}`}
            file={mapConfig.file}
            center={mapConfig.center}
            zoom={mapConfig.zoom}
            routeGeojson={hasTrace ? route.geojson : undefined}
            recordedTrack={recordedTrack}
            currentPosition={currentPosition}
            approachTarget={approachTarget}
            roadApproachCoordinates={roadApproachCoordinates}
            followUser={followUser}
            recenterSignal={recenterSignal}
            searchTarget={searchTarget}
            allowGuestViewer={allowGuestViewer}
            persistDownloadedMaps={persistDownloadedMaps}
            onSnapshotProvider={onSnapshotProvider}
            plannerPoints={plannerPoints}
            plannerRouteCoordinates={plannerRouteCoordinates}
            plannerRouteHasFallback={plannerRouteHasFallback}
            plannerActive={plannerActive}
            onPlannerMapClick={onPlannerMapClick}
          />
          {!hasTrace && showMissingTraceNotice && !recording && (
            <div className="route-track-notice" role="status">
              <Info />
              <span>
                <strong>Ruta GPS pendiente</strong>
                <small>El mapa regional está disponible, pero esta ficha todavía no contiene un GPX o GeoJSON validado.</small>
              </span>
            </div>
          )}
        </div>
      );
    }
  }
  if (!hasTrace && !showMissingTraceNotice) {
    return (
      <div className="map-stage map-stage-empty" aria-label="Mapa sin una ruta cargada">
        <svg className="terrain-lines" viewBox="0 0 760 520" aria-hidden="true">
          <path d="M-40 148C90 37 195 238 340 105s260 74 480-34" />
          <path d="M-20 215C129 89 220 282 384 164s281 56 421-19" />
          <path d="M-60 356C105 221 204 418 382 286s270 45 443-72" />
          <path d="M38 486C170 359 260 502 442 403s215-12 330-75" />
        </svg>
        <div className="map-label label-country">THERA · GPS</div>
        {recording && <div className="live-pulse"><span />Grabación libre activa</div>}
      </div>
    );
  }
  const overlayPoints = geoJsonToOverlayPoints(route.geojson) ?? route.points;
  const startY = overlayPoints.split(" ")[0].split(",")[1];
  const endY = overlayPoints.split(" ").at(-1)?.split(",")[1];
  return (
    <div
      className="map-stage"
      aria-label={`Mapa de referencia de ${route.area}`}
    >
      <svg
        className="terrain-lines"
        viewBox="0 0 760 520"
        role="img"
        aria-label="Relieve y trazado referencial"
      >
        <path d="M-40 148C90 37 195 238 340 105s260 74 480-34" />
        <path d="M-20 180C112 58 201 261 360 132S618 202 805 98" />
        <path d="M-20 215C129 89 220 282 384 164s281 56 421-19" />
        <path d="M-60 356C105 221 204 418 382 286s270 45 443-72" />
        <path d="M-44 395C109 266 230 447 401 326s265 26 430-58" />
        <path d="M38 486C170 359 260 502 442 403s215-12 330-75" />
        <path d="M480-20c-80 77-67 173 28 205s157-41 226 11" />
      </svg>
      <div className="map-label label-country">ECUADOR</div>
      <div className="map-label label-area">{route.area}</div>
      <div className="map-water water-one" />
      <div className="map-water water-two" />
      <div className="map-water water-three" />
      {!climbing && (
        <svg className="route-overlay" viewBox="0 0 370 112" aria-hidden="true">
          <polyline
            points={overlayPoints}
            fill="none"
            stroke="rgba(27,49,72,.30)"
            strokeWidth="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <polyline
            points={overlayPoints}
            fill="none"
            stroke="#FF7F00"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle
            cx="22"
            cy={startY}
            r="7"
            fill="#1B3148"
            stroke="#fff"
            strokeWidth="3"
          />
          <circle
            cx="346"
            cy={endY}
            r="7"
            fill="#FF7F00"
            stroke="#fff"
            strokeWidth="3"
          />
        </svg>
      )}
      {climbing &&
        climbingPlaces.map((place) => (
          <button
            className="climbing-marker"
            style={place.marker}
            key={place.id}
            aria-label={`Ver ${place.name}`}
          >
            <Mountain />
          </button>
        ))}
      <div className="map-tools" aria-label="Herramientas del mapa">
        <button aria-label="Buscar">
          <Search />
        </button>
        <button aria-label="Centrar ubicación">
          <LocateFixed />
        </button>
        <button aria-label="Cambiar capas">
          <Layers3 />
        </button>
      </div>
      {recording && (
        <div className="live-pulse">
          <span /> GPS activo
        </div>
      )}
      <div className="map-attribution">
        © OpenStreetMap contributors · visualización referencial
      </div>
    </div>
  );
}

function RouteStats({ route }: { route: RouteItem }) {
  return (
    <div className="stats-grid">
      <div>
        <Route />
        <strong>{route.distance}</strong>
        <span>Distancia</span>
      </div>
      <div>
        <Timer />
        <strong>{route.time}</strong>
        <span>Tiempo estimado</span>
      </div>
      <div>
        <Mountain />
        <strong>{route.ascent}</strong>
        <span>Desnivel</span>
      </div>
    </div>
  );
}

function RouteWaypointDistances({ route }: { route: RouteItem }) {
  const coordinates = routeCoordinates(route.geojson);
  if (coordinates.length < 2) return null;
  const waypoints = routeWaypoints(route.geojson)
    .map((waypoint) => ({ waypoint, position: nearestRoutePosition(waypoint, coordinates) }))
    .filter((entry) => entry.position !== null)
    .sort((a, b) => a.position!.alongKm - b.position!.alongKm);
  if (waypoints.length < 2) return null;
  return (
    <details className="route-waypoint-distances">
      <summary><MapPinned />Distancias entre puntos de la ruta <ChevronRight /></summary>
      <div>
        {waypoints.slice(1).map((entry, index) => {
          const previous = waypoints[index];
          const segmentKm = entry.position!.alongKm - previous.position!.alongKm;
          return (
            <article key={`${previous.waypoint.id}-${entry.waypoint.id}`}>
              <span><i />{previous.waypoint.name}</span>
              <b>{formatDistance(segmentKm)} km</b>
              <span><i />{entry.waypoint.name}</span>
            </article>
          );
        })}
      </div>
      {waypoints.length === 2 && <small>El GPX no contiene todavía lugares intermedios con nombre; se muestra inicio–final.</small>}
    </details>
  );
}

function estimatedSegmentSeconds(route: RouteItem, segmentKm: number) {
  const distanceKm = Number(route.distance.replace(",", ".").match(/[\d.]+/)?.[0] ?? 0);
  const hours = Number(route.time.match(/(\d+(?:[.,]\d+)?)\s*h/)?.[1]?.replace(",", ".") ?? 0);
  const minutes = Number(route.time.match(/(\d+)\s*min/)?.[1] ?? 0);
  const totalSeconds = Math.round(hours * 3600 + minutes * 60);
  return distanceKm > 0 && totalSeconds > 0 ? Math.round(totalSeconds * segmentKm / distanceKm) : null;
}

function RoutePointPlanner({
  route,
  onPreview,
  onStart,
}: {
  route: RouteItem;
  onPreview: (route: RouteItem | null) => void;
  onStart: (route: RouteItem) => void;
}) {
  const orderedWaypoints = useMemo(() => {
    const coordinates = routeCoordinates(route.geojson);
    return routeWaypoints(route.geojson)
      .map((waypoint) => ({ waypoint, position: nearestRoutePosition(waypoint, coordinates) }))
      .filter((entry) => entry.position !== null)
      .sort((a, b) => a.position!.alongKm - b.position!.alongKm)
      .map((entry) => entry.waypoint);
  }, [route.geojson]);
  const [fromId, setFromId] = useState(orderedWaypoints[0]?.id ?? "");
  const [toId, setToId] = useState(orderedWaypoints.at(-1)?.id ?? "");
  const [previewEnabled, setPreviewEnabled] = useState(true);
  const plan = useMemo(
    () => planRouteSegment(route.geojson, fromId, toId),
    [route.geojson, fromId, toId],
  );
  const previewRoute = useMemo<RouteItem | null>(() => {
    if (!plan) return null;
    const estimated = estimatedSegmentSeconds(route, plan.distanceKm);
    return {
      ...route,
      name: `${plan.from.name} → ${plan.to.name}`,
      distance: `${formatDistance(plan.distanceKm)} km`,
      time: estimated === null ? "Tiempo por validar" : `${Math.floor(estimated / 3600) ? `${Math.floor(estimated / 3600)} h ` : ""}${Math.round((estimated % 3600) / 60)} min estimado`,
      ascent: `+${plan.ascentM} m · -${plan.descentM} m`,
      geojson: plan.geojson,
      tags: [...route.tags, "Tramo elegido"],
    };
  }, [plan, route]);

  useEffect(() => {
    setFromId(orderedWaypoints[0]?.id ?? "");
    setToId(orderedWaypoints.at(-1)?.id ?? "");
    setPreviewEnabled(true);
  }, [route.id, route.geojson, orderedWaypoints]);

  useEffect(() => {
    onPreview(previewEnabled ? previewRoute : null);
    return () => onPreview(null);
  }, [previewEnabled, previewRoute, onPreview]);

  if (orderedWaypoints.length < 2) return null;
  const startElevation = plan?.from.elevation;
  const finishElevation = plan?.to.elevation;
  return (
    <section className="route-point-planner" aria-labelledby="route-point-planner-title">
      <header>
        <span><MapPinned /></span>
        <div>
          <p>PLANIFICAR POR PUNTOS</p>
          <h3 id="route-point-planner-title">Elige el tramo que quieres recorrer</h3>
          <small>La línea naranja, el clima y el inicio se actualizarán al primer punto elegido.</small>
        </div>
      </header>
      <div className="route-point-selectors">
        <label>
          <span>1 · Punto inicial</span>
          <select value={fromId} onChange={(event) => { setPreviewEnabled(true); setFromId(event.target.value); }}>
            {orderedWaypoints.map((waypoint) => <option value={waypoint.id} key={`from-${waypoint.id}`}>{waypoint.name}</option>)}
          </select>
        </label>
        <ArrowUpRight aria-hidden="true" />
        <label>
          <span>2 · Punto final</span>
          <select value={toId} onChange={(event) => { setPreviewEnabled(true); setToId(event.target.value); }}>
            {orderedWaypoints.map((waypoint) => <option value={waypoint.id} key={`to-${waypoint.id}`}>{waypoint.name}</option>)}
          </select>
        </label>
      </div>
      {plan && previewRoute && previewEnabled ? <>
        <div className="route-point-metrics">
          <span><Route /><b>{formatDistance(plan.distanceKm)} km</b><small>por el trazado</small></span>
          <span><Mountain /><b>+{plan.ascentM} m</b><small>ascenso</small></span>
          <span><ArrowUpRight /><b>-{plan.descentM} m</b><small>descenso</small></span>
          <span><Timer /><b>{previewRoute.time}</b><small>según la ficha</small></span>
          <span><MapPin /><b>{startElevation == null ? "—" : `${Math.round(startElevation)} m`}</b><small>altitud inicial</small></span>
          <span><LocateFixed /><b>{finishElevation == null ? "—" : `${Math.round(finishElevation)} m`}</b><small>altitud final</small></span>
        </div>
        <p className="route-point-detail">
          Rango de altitud {plan.minimumElevationM === null ? "no disponible" : `${plan.minimumElevationM}–${plan.maximumElevationM} m`}
          {` · ${plan.intermediatePoints} ${plan.intermediatePoints === 1 ? "punto intermedio" : "puntos intermedios"}`}
          {` · ${formatDistance(plan.directDistanceKm)} km en línea directa`}.
        </p>
        <div className="route-point-actions">
          <button type="button" className="cancel" onClick={() => {
            setPreviewEnabled(false);
          }}><X /> Cancelar</button>
          <button type="button" className="start" onClick={() => onStart(previewRoute)}><Navigation /> Iniciar este tramo</button>
        </div>
      </> : <p className="route-point-invalid"><AlertTriangle />{previewEnabled ? "Elige dos puntos diferentes para calcular el tramo." : "Selección cancelada. Cambia cualquiera de los puntos para calcular otro tramo."}</p>}
    </section>
  );
}

function formatSplitTime(value: number) {
  const totalSeconds = Math.max(0, Math.round(value));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours
    ? `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
    : `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function ActivitySplitsReport({ points, compact = false }: { points: TrackPoint[]; compact?: boolean }) {
  const splits = activitySplits(points);
  const usesActiveClock = points.length > 1 && points.slice(1).every((point, index) =>
    Number.isFinite(point.activeSeconds) && Number.isFinite(points[index].activeSeconds),
  );
  const fastest = splits
    .filter((split) => split.complete && split.durationSeconds > 0)
    .reduce<(typeof splits)[number] | null>((best, split) =>
      !best || split.paceSecondsPerKm < best.paceSecondsPerKm ? split : best, null);

  return (
    <section className={`activity-splits${compact ? " activity-splits-compact" : ""}`}>
      <header>
        <span><strong>Parciales por kilómetro</strong><small>{usesActiveClock ? "Tiempo activo · pausas excluidas" : "Estimación compatible con horas GPS"}</small></span>
      </header>
      {splits.length ? (
        <div className="activity-splits-table" role="table" aria-label="Ritmo por kilómetro">
          <div className="activity-split-head" role="row"><b>Tramo</b><b>Tiempo</b><b>Ritmo</b><b>Velocidad</b></div>
          {splits.map((split) => (
            <div className={fastest?.index === split.index ? "activity-split-fastest" : ""} role="row" key={`${split.index}-${split.complete}`}>
              <strong>{split.complete ? `${split.index} km` : `Final ${formatDistance(split.distanceKm)} km`}{fastest?.index === split.index ? <small>Mejor</small> : null}</strong>
              <span>{formatSplitTime(split.durationSeconds)}</span>
              <span>{formatSplitTime(split.paceSecondsPerKm)} /km</span>
              <span>{split.averageSpeedKmh.toFixed(1).replace(".", ",")} km/h</span>
            </div>
          ))}
        </div>
      ) : <p>El primer parcial aparecerá al registrar al menos 100 m.</p>}
    </section>
  );
}

export default function Home() {
  const [mobileHeaderMenuOpen, setMobileHeaderMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabName>(() =>
    typeof window !== "undefined" && new URL(window.location.href).searchParams.get("password-recovery") === "1"
      ? "perfil"
      : "explorar",
  );
  const [activityFilter, setActivityFilter] =
    useState<ActivityKind>("senderismo");
  // Android solamente: fuerza un recentrado del mapa GPS al volver a Explorar.
  // No cambia la captura GPS ni el motor cartografico; solo envia una senal visual
  // cuando la vista movil de actividad vuelve a montarse despues de visitar otra seccion.
  const [mobileMapRecenterSignal, setMobileMapRecenterSignal] = useState(0);
  const [selectedRoute, setSelectedRoute] = useState<RouteItem>(fallbackRoute);
  const [routeChosen, setRouteChosen] = useState(() =>
    typeof window !== "undefined" && new URL(window.location.href).searchParams.has("route"),
  );
  const [routesExpanded, setRoutesExpanded] = useState(false);
  const [routeSearch, setRouteSearch] = useState("");
  const [mapRegionPickerOpen, setMapRegionPickerOpen] = useState(false);
  // Android solamente: estado visual del panel plegable de accesos de Explorar.
  // No participa en GPS, tracking, navegación ni persistencia de rutas.
  const [androidExplorePanelOpen, setAndroidExplorePanelOpen] = useState(false);
  const [androidExplorePanelSection, setAndroidExplorePanelSection] = useState<"routes" | "planner" | "activity" | null>(null);
  // Android solamente: controla la expansion visual del buscador en Home.
  // Search V4 y su logica permanecen intactos; solo cambia la presentacion.
  const [androidSearchOpen, setAndroidSearchOpen] = useState(false);
  // Android solamente: abre/cierra el clima como panel visual flotante sobre el mapa.
  // RouteWeatherPanel conserva exactamente su fuente de datos y comportamiento.
  const [androidWeatherOpen, setAndroidWeatherOpen] = useState(false);
  const [selectedMapPackId, setSelectedMapPackId] = useState(() => {
    if (typeof window === "undefined") return "azuay";
    const saved = window.localStorage.getItem("thera-selected-map-pack");
    return mapPackById(saved)?.id ?? "azuay";
  });
  const [placeResults, setPlaceResults] = useState<PlaceSearchResult[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<PlaceSearchResult | null>(null);
  const [placeSearchBusy, setPlaceSearchBusy] = useState(false);
  const [offlineQuickPickId, setOfflineQuickPickId] = useState(() => {
    try {
      return window.localStorage.getItem("thera-selected-map-pack") ?? "azuay";
    } catch {
      return "azuay";
    }
  });
  const [placeSearchMessage, setPlaceSearchMessage] = useState("");
  const [recordingMode, setRecordingMode] = useState<RecordingMode | null>(null);
  const [deviationAlertMode, setDeviationAlertMode] = useState<DeviationAlertMode>(() => {
    if (typeof window === "undefined") return "both";
    const saved = window.localStorage.getItem("thera-deviation-alert-mode");
    return saved === "none" || saved === "vibration" || saved === "message" || saved === "both" ? saved : "both";
  });
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [pendingPersonalMapActivityId, setPendingPersonalMapActivityId] = useState<string | null>(null);
  const [packProgress, setPackProgress] = useState<Record<string, number>>({});
  const [packRemoteInfo, setPackRemoteInfo] = useState<Record<string, { available: boolean; size: number; checked: boolean; contourAvailable?: boolean }>>({});
  const [offlinePackState, setOfflinePackState] = useState<Record<string, { map: boolean; terrain: boolean; contours: boolean; usable: boolean; complete: boolean }>>({});
  const [plannedRouteSegment, setPlannedRouteSegment] = useState<RouteItem | null>(null);
  const [networkOnline, setNetworkOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [mountainAIWeather, setMountainAIWeather] =
  useState<RouteWeather | null>(null);

  const [recording, setRecording] = useState(false);
  const [recordingPaused, setRecordingPaused] = useState(false);
  const [routeNavigationActive, setRouteNavigationActive] = useState(false);
  const [approachMode, setApproachMode] = useState<ApproachMode>("foot-walking");
  const [roadApproachRoute, setRoadApproachRoute] = useState<RoadApproachRoute | null>(null);
  const [approachRoutingBusy, setApproachRoutingBusy] = useState(false);
  const [approachRoutingMessage, setApproachRoutingMessage] = useState("Línea directa disponible sin conexión.");
  const [seconds, setSeconds] = useState(0);
  const [distance, setDistance] = useState(0);
  const [track, setTrack] = useState<TrackPoint[]>([]);
  // Posicion GPS visual: se actualiza con cada lectura valida del sensor, incluso
  // antes de que el punto cumpla los filtros usados para guardar el GPX.
  // Esto mantiene visible el punto azul y permite guiar al inicio o de vuelta a la ruta.
  const [livePosition, setLivePosition] = useState<TrackPoint | null>(null);
  useEffect(() => {
  if (activeTab !== "ai" || livePosition) return;

  let cancelled = false;

  void getCurrentTheraPosition()
    .then((point) => {
      if (!cancelled) setLivePosition(point);
    })
    .catch(() => {
      // Si no hay permiso o posición disponible,
      // Mountain AI mantiene su estado "sin posición".
    });

  return () => {
    cancelled = true;
  };
}, [activeTab, livePosition]);
  const [activityId, setActivityId] = useState<string | null>(null);
  const [activityStartedAt, setActivityStartedAt] = useState<number | null>(null);
  const [activityMarkers, setActivityMarkers] = useState<ActivityMarker[]>([]);
  const [activityHistory, setActivityHistory] = useState<NavigationSession[]>([]);
  const [batteryPercent, setBatteryPercent] = useState<number | null>(null);
  const [shareFormat, setShareFormat] = useState<ShareCardFormat>("portrait");
  const [shareBackground, setShareBackground] = useState<ShareCardBackground>("thera");
  const [sharePhoto, setSharePhoto] = useState<string | null>(null);
  const [hideShareEndpoints, setHideShareEndpoints] = useState(true);
  const [gpsMessage, setGpsMessage] = useState("Listo para registrar");
  const [photoResults, setPhotoResults] = useState<PhotoResult[]>([]);
  const [panoramaResults, setPanoramaResults] = useState<PanoramaResult[]>([]);
  const [selectedPanoramaId, setSelectedPanoramaId] = useState<string | null>(null);
  const [photoMessage, setPhotoMessage] = useState(
    "Elige hasta seis fotografías",
  );
  const [routeForm, setRouteForm] = useState<RouteFormState>(emptyRouteForm);
  const [routeDraftId, setRouteDraftId] = useState<string>(() => crypto.randomUUID());
  const [importedRouteFile, setImportedRouteFile] =
    useState<LocalRouteFile | null>(null);
  const [localRouteDrafts, setLocalRouteDrafts] = useState<LocalRouteDraft[]>([]);
  const [ownRoutes, setOwnRoutes] = useState<CloudRoute[]>([]);
  const [publishedRoutes, setPublishedRoutes] = useState<CloudRoute[]>([]);
  const [pendingRoutes, setPendingRoutes] = useState<PendingRoute[]>([]);
  const [routeBusy, setRouteBusy] = useState(false);
  const [routeAdminActionId, setRouteAdminActionId] = useState<string | null>(null);
  const [importedTrack, setImportedTrack] = useState<
    Array<{ latitude: number; longitude: number; elevation: number | null }>
  >([]);
  const [importedWaypoints, setImportedWaypoints] = useState<RouteWaypoint[]>([]);
  const [plannerTool, setPlannerTool] = useState<PlannerTool>(null);
  const [plannerPoints, setPlannerPoints] = useState<PlannerPoint[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const saved = window.localStorage.getItem("thera-free-plan-v1");
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed
        .filter((point) => point && Number.isFinite(point.latitude) && Number.isFinite(point.longitude)
          && ["start", "waypoint", "finish"].includes(point.kind))
        .map((point) => ({
          id: typeof point.id === "string" && point.id ? point.id : crypto.randomUUID(),
          latitude: Number(point.latitude),
          longitude: Number(point.longitude),
          elevation: Number.isFinite(point.elevation) ? Number(point.elevation) : null,
          kind: point.kind as PlannerPoint["kind"],
        })) : [];
    } catch {
      return [];
    }
  });
  const [plannerTravelMode, setPlannerTravelMode] = useState<ApproachMode>(() => {
    if (typeof window === "undefined") return "foot-walking";
    return window.localStorage.getItem("thera-planner-travel-mode") === "driving-car" ? "driving-car" : "foot-walking";
  });
  const [plannerLegs, setPlannerLegs] = useState<PlannerLeg[]>([]);
  const [plannerRoutingBusy, setPlannerRoutingBusy] = useState(false);
  const [plannerRoutingMessage, setPlannerRoutingMessage] = useState("Marca inicio y destino directamente en el mapa.");
  const [plannerWeatherPointId, setPlannerWeatherPointId] = useState<string | null>(null);
  const [importMessage, setImportMessage] = useState(
    "Importa un GPX para comprobarlo antes de publicarlo.",
  );
  const [accessSent, setAccessSent] = useState(false);
  const [authMode, setAuthMode] = useState<"register" | "login">("register");
  const [passwordFlow, setPasswordFlow] = useState<PasswordFlow>(() =>
    typeof window !== "undefined" && new URL(window.location.href).searchParams.get("password-recovery") === "1"
      ? "update"
      : "login",
  );
  const [authEmail, setAuthEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null);
  const [currentProfile, setCurrentProfile] = useState<TheraProfile | null>(null);
  const [profileBusy, setProfileBusy] = useState(false);
  const [recoverableDraft, setRecoverableDraft] = useState<{
    points: TrackPoint[];
    seconds: number;
    session?: NavigationSession;
  } | null>(null);
  // Android V1.6: una sesion puede seguir viva en SQLite aunque React/IndexedDB
  // haya perdido su referencia. Este estado solo expone esa sesion para recuperarla;
  // no crea otro GPS ni modifica el servicio nativo.
  const [nativePendingRecovery, setNativePendingRecovery] = useState<NativePendingRecovery | null>(null);
  const [nativeRecoveryBusy, setNativeRecoveryBusy] = useState(false);
  const [nativeRecoveryAttachRequest, setNativeRecoveryAttachRequest] = useState<{
    activityId: string;
    ownerId: string;
    startedAt: number;
    intent: "recover" | "finish" | "discard";
  } | null>(null);
  const [recoveryDetailsOpen, setRecoveryDetailsOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(
    null,
  );
  const [toast, setToast] = useState("");
  const [pendingMembers, setPendingMembers] = useState<PendingMember[]>([]);
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminActionId, setAdminActionId] = useState<string | null>(null);
  const mapSectionRef = useRef<HTMLElement | null>(null);
  const pausedRef = useRef(false);
  const routeNavigationActiveRef = useRef(false);
  const roadApproachRouteRef = useRef<RoadApproachRoute | null>(null);
  const secondsRef = useRef(0);
  const nativeRecoveryCursorRef = useRef<NativeRecoveryCursor>({});
  const nativeRecoveryBufferRef = useRef<RecorderTrackPoint[]>([]);
  const nativeRecoveryInProgressRef = useRef(false);
  const nativeSyncPromiseRef = useRef<Promise<void> | null>(null);
  const trackRef = useRef<TrackPoint[]>([]);
  const mapSnapshotProvider = useRef<(() => string | null) | null>(null);
  const lastOffRouteAlert = useRef(0);
  const wasOffRoute = useRef(false);
  const lastApproachRecalculation = useRef(0);
  const syncingActivities = useRef(new Set<string>());
  const wakeLock = useRef<WakeLockSentinelLike | null>(null);
  const isApprovedMember = currentProfile?.status === "approved";
  const miMapaEnabled = import.meta.env.VITE_MI_MAPA_ENABLED === "true";
  const isAdmin = currentProfile?.role === "admin" && currentProfile.status === "approved";
  const isAndroidApp = currentPlatform() === "android";
  const uiPlatform = currentUiPlatform();
  const isAndroidUi = uiPlatform === "android-native";
  const isPwaUi = uiPlatform === "pwa";
  const isWebUi = uiPlatform === "web";
  const androidBackgroundTrackingV1Enabled = isAndroidApp && import.meta.env.VITE_ANDROID_BACKGROUND_TRACKING_V1 !== "false";

  useEffect(() => {
    if (!isAndroidUi || activeTab === "explorar") return;
    if (androidExplorePanelOpen) setAndroidExplorePanelOpen(false);
    if (androidExplorePanelSection !== null) setAndroidExplorePanelSection(null);
  }, [activeTab, isAndroidUi, androidExplorePanelOpen, androidExplorePanelSection]);

  const probeNativePendingRecovery = useCallback(async (): Promise<NativePendingRecovery | null> => {
    if (!androidBackgroundTrackingV1Enabled || !currentProfile || currentProfile.status !== "approved") return null;
    const snapshot = await getNativeTrackingSnapshot();
    if (!snapshot.active || !snapshot.activityId || (snapshot.state !== "recording" && snapshot.state !== "paused")) return null;

    const [recovered, localSession] = await Promise.all([
      recoverNativeLocationPoints({ activityId: snapshot.activityId }),
      loadActiveNavigation(currentProfile.id).catch(() => null),
    ]);
    return {
      snapshot,
      points: recovered.points,
      cursor: recovered.cursor,
      session: localSession?.id === snapshot.activityId ? localSession : undefined,
    };
  }, [androidBackgroundTrackingV1Enabled, currentProfile]);

  useEffect(() => {
    if (!androidBackgroundTrackingV1Enabled || !currentProfile || currentProfile.status !== "approved" || recording || activityId) {
      if (recording || activityId || !currentProfile) setNativePendingRecovery(null);
      return;
    }
    let cancelled = false;
    const refresh = () => {
      void probeNativePendingRecovery()
        .then((pending) => { if (!cancelled) setNativePendingRecovery(pending); })
        .catch(() => { if (!cancelled) setNativePendingRecovery(null); });
    };
    refresh();
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [activityId, activeTab, androidBackgroundTrackingV1Enabled, currentProfile, probeNativePendingRecovery, recording]);

  const platformOfflinePacks = isAndroidApp ? ANDROID_OFFLINE_PACKS : WEB_ONLINE_PACKS;
  const platformOfflineGroups = isAndroidApp ? ANDROID_OFFLINE_GROUPS : WEB_OFFLINE_GROUPS;
  const installedOfflinePacks = platformOfflinePacks.filter((pack) => Boolean(offlinePackState[pack.id]?.usable));
  const selectedMapPack = mapPackById(selectedMapPackId) ?? mapPackById("azuay")!;
  // Web/PWA mantiene el mapa nacional. Android usa siempre la región seleccionada,
  // tanto con Internet como sin conexión. Esta bandera también controla si el
  // visor abre el mapa como invitado online o usa/guarda el paquete regional.
  const useNationalOnlineMap = !isAndroidApp;
  // Android debe respetar SIEMPRE la región elegida, también con Internet.
  // Antes, cualquier conexión activa forzaba "ecuador", por lo que Azuay
  // nunca llegaba a TheraMapLibre y sus curvas regionales no podían cargarse.
  // Web/PWA conserva el mapa nacional como vista general.
  const activeMapPackId = isAndroidApp ? selectedMapPack.id : "ecuador";
  const activeMapPack = mapPackById(activeMapPackId) ?? mapPackById("ecuador")!;
  const registerMapSnapshot = useCallback((provider: (() => string | null) | null) => {
    mapSnapshotProvider.current = provider;
  }, []);

    const renderMiMapaMap = useCallback((props: MiMapaMapRenderProps) => (
    <TheraMapLibre
      key={`mi-mapa-${props.mapFile}`}
      file={props.mapFile}
      center={props.mapCenter}
      zoom={props.mapZoom}
      allowGuestViewer={false}
      persistDownloadedMaps={true}
      personalMapGeojson={props.overlay}
      personalMapFocusBounds={props.focusBounds}
      onPersonalMapTrackSelect={props.onTrackSelect}
    />
  ), []);
useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" })
        .then(async (registration) => {
          // Fuerza la comprobación de una nueva shell. Si existe una versión esperando,
          // la activa inmediatamente para que el modo sin conexión use la misma UI actual.
          await registration.update().catch(() => undefined);
          registration.waiting?.postMessage({ type: "SKIP_WAITING" });
          registration.addEventListener("updatefound", () => {
            const worker = registration.installing;
            worker?.addEventListener("statechange", () => {
              if (worker.state === "installed" && navigator.serviceWorker.controller) {
                worker.postMessage({ type: "SKIP_WAITING" });
              }
            });
          });
        })
        .catch(() => undefined);
    }
    const online = () => setNetworkOnline(true);
    const offline = () => setNetworkOnline(false);
    const beforeInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    window.addEventListener("beforeinstallprompt", beforeInstall);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
      window.removeEventListener("beforeinstallprompt", beforeInstall);
    };
  }, []);

  useEffect(() => {
    window.localStorage.setItem("thera-selected-map-pack", selectedMapPackId);
    setOfflineQuickPickId(selectedMapPackId);
  }, [selectedMapPackId]);

  useEffect(() => {
    if (!isAndroidApp) return;
    // V4: los nombres del PMTiles se preparan automáticamente fuera del hilo
    // principal. El mapa seleccionado tiene prioridad; el resto de mapas ya
    // descargados se indexa después sin bloquear búsqueda, mapas ni navegación.
    const timer = window.setTimeout(() => {
      void scheduleDownloadedMapLabelIndexes(selectedMapPackId);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [isAndroidApp, selectedMapPackId]);
  useEffect(() => {
    window.localStorage.setItem("thera-free-plan-v1", JSON.stringify(plannerPoints));
  }, [plannerPoints]);
  useEffect(() => {
    window.localStorage.setItem("thera-planner-travel-mode", plannerTravelMode);
  }, [plannerTravelMode]);

  useEffect(() => {
    window.localStorage.setItem("thera-deviation-alert-mode", deviationAlertMode);
  }, [deviationAlertMode]);

  useEffect(() => {
    type BatteryLike = EventTarget & { level: number };
    const batteryNavigator = navigator as Navigator & { getBattery?: () => Promise<BatteryLike> };
    let battery: BatteryLike | null = null;
    const update = () => setBatteryPercent(battery ? Math.round(battery.level * 100) : null);
    batteryNavigator.getBattery?.().then((value) => {
      battery = value;
      update();
      battery.addEventListener("levelchange", update);
    }).catch(() => undefined);
    return () => battery?.removeEventListener("levelchange", update);
  }, []);

  useEffect(() => {
    let requestId = 0;
    const stopObserving = observeSession((session, event) => {
      const currentRequest = ++requestId;
      if (event === "PASSWORD_RECOVERY") {
        setPasswordFlow("update");
        setAuthMode("login");
        setActiveTab("perfil");
        setToast("Enlace verificado. Crea ahora tu nueva contraseña.");
      }
      // La sesión de Supabase puede no estar disponible al arrancar completamente
      // sin señal. Conservamos únicamente el id de la última cuenta autenticada
      // para poder recuperar SU perfil local previamente validado. No se guardan
      // contraseña ni credenciales nuevas aquí.
      const sessionUserId = session?.user.id ?? null;
      if (sessionUserId) rememberUserId(sessionUserId);
      const rememberedUserId = sessionUserId ?? loadRememberedUserId();
      const cachedProfile = loadCachedProfile(rememberedUserId ?? undefined);

      if (!session) {
        if ((isAndroidApp || !networkOnline) && cachedProfile) {
          setSignedInEmail(cachedProfile.email);
          setCurrentProfile(cachedProfile);
          setProfileBusy(false);
          setToast(isAndroidApp && networkOnline ? "Acceso de campo recuperado. THERA conserva las funciones locales mientras se restablece la sesión segura." : "Sesión local recuperada. THERA continúa disponible sin conexión.");
          return;
        }

        // Con conexión, la ausencia real de sesión sigue significando invitado.
        // Así una sesión cerrada o revocada no queda habilitada por el caché local.
        setSignedInEmail(null);
        setCurrentProfile(null);
        setPendingMembers([]);
        setPendingRoutes([]);
        setOwnRoutes([]);
        setLocalRouteDrafts([]);
        setRecoverableDraft(null);
        setNativePendingRecovery(null);
        setNativeRecoveryAttachRequest(null);
        setProfileBusy(false);
        return;
      }
      setSignedInEmail(session.user.email ?? cachedProfile?.email ?? null);
      setCurrentProfile(cachedProfile);
      if (!networkOnline) {
        setProfileBusy(false);
        if (cachedProfile) setToast("Sesión y autorización recuperadas en modo sin conexión.");
        return;
      }
      setProfileBusy(true);
      getCurrentProfile(session.user.id)
        .then((profile) => {
          if (requestId === currentRequest) {
            setCurrentProfile(profile);
            if (profile) {
              saveCachedProfile(profile);
              rememberUserId(session.user.id);
            }
          }
        })
        .catch((error: unknown) => {
          if (requestId === currentRequest) {
            if (cachedProfile) {
              setCurrentProfile(cachedProfile);
              setToast("No fue posible actualizar el perfil; se mantiene la autorización guardada.");
            } else {
              setToast(getErrorMessage(error, "No fue posible comprobar tu perfil."));
            }
          }
        })
        .finally(() => {
          if (requestId === currentRequest) setProfileBusy(false);
        });
    });
    return () => {
      requestId += 1;
      stopObserving();
    };
  }, [networkOnline]);

  useEffect(() => {
    let ignore = false;
    listPublishedRoutes()
      .then((items) => {
        if (!ignore) {
          setPublishedRoutes(items);
          savePublishedRouteCache(items).catch(() => undefined);
        }
      })
      .catch(() => loadPublishedRouteCache().then((items) => { if (!ignore) setPublishedRoutes(items); }).catch(() => undefined));
    return () => {
      ignore = true;
    };
  }, [networkOnline]);

  useEffect(() => {
    if (!currentProfile || currentProfile.status !== "approved") return;
    let ignore = false;
    Promise.all([
      listLocalRouteDrafts(currentProfile.id),
      loadActivityDraft(currentProfile.id),
      loadActiveNavigation(currentProfile.id),
      listActivityHistory(currentProfile.id),
    ])
      .then(([local, activityDraft, activeNavigation, history]) => {
        if (ignore) return;
        setLocalRouteDrafts(local);
        setActivityHistory(history);
        setRecoverableDraft(
          activeNavigation?.points?.length
            ? { points: activeNavigation.points, seconds: activeNavigation.elapsedSeconds, session: activeNavigation }
            : activityDraft?.points?.length
              ? { points: activityDraft.points, seconds: activityDraft.seconds }
            : null,
        );
      })
      .catch((error: unknown) => {
        if (!ignore) {
          setToast(getErrorMessage(error, "No fue posible cargar tus rutas."));
        }
      });
    if (networkOnline) {
      listOwnRoutes()
        .then((remote) => { if (!ignore) setOwnRoutes(remote); })
        .catch(() => undefined);
    }
    return () => {
      ignore = true;
    };
  }, [currentProfile?.id, currentProfile?.status, networkOnline]);

  useEffect(() => {
    if (activeTab !== "admin" || !isAdmin) return;
    let ignore = false;
    setAdminBusy(true);
    Promise.all([listPendingMembers(), listPendingRoutes()])
      .then(([members, routeItems]) => {
        if (!ignore) {
          setPendingMembers(members);
          setPendingRoutes(routeItems);
        }
      })
      .catch((error: unknown) => {
        if (!ignore) {
          setToast(getErrorMessage(error, "No fue posible cargar las revisiones."));
        }
      })
      .finally(() => {
        if (!ignore) setAdminBusy(false);
      });
    return () => {
      ignore = true;
    };
  }, [activeTab, isAdmin]);
  const refreshOfflineStorageState = useCallback(async () => {
    const nextStates = await Promise.all(platformOfflinePacks.map(async (pack) => {
      const terrainFile = terrainFileFor(pack.file);
      const contourFile = contourFileFor(pack.file);
      const [mapStored, terrainStored, contourStored] = await Promise.all([
        hasStoredMapPack(pack.file),
        terrainFile ? hasStoredMapPack(terrainFile) : Promise.resolve(true),
        contourFile ? hasStoredMapPack(contourFile) : Promise.resolve(true),
      ]);
      const remote = packRemoteInfo[pack.id];
      const contoursRequired = Boolean(contourFile && remote?.contourAvailable);
      const usable = mapStored;
      const complete = mapStored && terrainStored && (!contoursRequired || contourStored);
      return [pack.id, { map: mapStored, terrain: terrainStored, contours: contourStored, usable, complete }] as const;
    }));
    setOfflinePackState(Object.fromEntries(nextStates));
    setPackProgress((current) => {
      const next = { ...current };
      for (const [id, state] of nextStates) {
        if (state.complete) next[id] = 100;
        else if ((next[id] ?? 0) === 100) next[id] = 0;
      }
      return next;
    });

  }, [platformOfflinePacks, packRemoteInfo]);

  useEffect(() => {
    refreshOfflineStorageState().catch(() => undefined);
  }, [refreshOfflineStorageState]);

  useEffect(() => {
    Promise.all(platformOfflinePacks.map(async (pack) => {
      const terrainFile = terrainFileFor(pack.file);
      const [mapStored, terrainStored] = await Promise.all([
        hasStoredMapPack(pack.file),
        terrainFile ? hasStoredMapPack(terrainFile) : Promise.resolve(true),
      ]);
      return [pack.id, mapStored && terrainStored] as const;
    }))
      .then((states) => setPackProgress((current) => ({ ...current, ...Object.fromEntries(states.filter(([, stored]) => stored).map(([id]) => [id, 100])) })))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (activeTab !== "offline" || !isApprovedMember || !networkOnline) return;
    let cancelled = false;
    const checkRemotePack = async (
  pack: (typeof platformOfflinePacks)[number],
) => {
  const contourFile = contourFileFor(pack.file);

  const [base, terrain, contour] = await Promise.all([
    getRemoteMapInfo(pack.file),
    pack.terrainFile
      ? getRemoteMapInfo(pack.terrainFile)
      : Promise.resolve({ available: true, size: 0 }),
    contourFile
      ? getRemoteMapInfo(contourFile)
      : Promise.resolve({ available: false, size: 0 }),
  ]);

  return [
    pack.id,
    {
      available: base.available,
      contourAvailable: Boolean(contourFile && contour.available),
      size:
        base.size +
        terrain.size +
        (contour.available ? contour.size : 0),
      checked: true,
    },
  ] as const;
};

const remoteChecks = isAndroidApp
  ? (async () => {
      const items = [];

      for (const pack of platformOfflinePacks) {
        if (cancelled) break;
        items.push(await checkRemotePack(pack));
      }

      return items;
    })()
  : Promise.all(platformOfflinePacks.map(checkRemotePack));

remoteChecks.then(async (items) => {
      if (cancelled) return;
      const nextRemote = Object.fromEntries(items);
      setPackRemoteInfo(nextRemote);

      // Si el usuario ya tenía mapa+relieve al 100% pero ahora existe un
      // paquete de curvas que aún no está guardado, habilitamos completar
      // el paquete sin obligarlo a borrar y descargar todo otra vez.
      const missingContours = await Promise.all(platformOfflinePacks.map(async (pack) => {
        const contourFile = contourFileFor(pack.file);
        const remote = nextRemote[pack.id];
        if (!contourFile || !remote?.contourAvailable) return [pack.id, false] as const;
        return [pack.id, !(await hasStoredMapPack(contourFile))] as const;
      }));
      if (!cancelled) {
        setPackProgress((current) => {
          const next = { ...current };
          for (const [id, missing] of missingContours) {
            if (missing && next[id] === 100) next[id] = 99;
          }
          return next;
        });
      }
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [activeTab, isApprovedMember, networkOnline]);

  useEffect(() => {
    secondsRef.current = seconds;
  }, [seconds]);
  useEffect(() => {
    trackRef.current = track;
  }, [track]);
  useEffect(() => {
    routeNavigationActiveRef.current = routeNavigationActive;
  }, [routeNavigationActive]);
  useEffect(() => {
    roadApproachRouteRef.current = roadApproachRoute;
  }, [roadApproachRoute]);
  useEffect(() => {
    if (!recording || recordingPaused) return;
    const timer = window.setInterval(
      () => setSeconds((value) => value + 1),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [recording, recordingPaused]);
  useEffect(
    () => () => {
      stopLocationRecorder().catch(() => undefined);
      wakeLock.current?.release().catch(() => undefined);
    },
    [],
  );
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 3600);
    return () => window.clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    if (!finishDialogOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFinishDialogOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [finishDialogOpen]);

  const availableRoutes = useMemo(() => {
    const routeIndex = new Map<string, RouteItem>();
    routes.forEach((route) => routeIndex.set(route.id, route));
    publishedRoutes
      .map(cloudRouteToItem)
      .forEach((route) => routeIndex.set(route.id, route));
    return Array.from(routeIndex.values());
  }, [publishedRoutes]);
  const recordingGeojson = recordingMode === "selected-route" ? selectedRoute.geojson : undefined;
  const recordingRouteId = recordingMode === "selected-route" ? selectedRoute.id : null;
  const recordingRouteName = recordingMode === "selected-route" ? selectedRoute.name : "Ruta nueva GPS";
  const recordingKind: ActivityKind = recordingMode === "selected-route" ? selectedRoute.kind : "senderismo";
  const recordingMapRoute = useMemo<RouteItem>(() => recordingMode !== "selected-route"
    ? {
        id: "thera-free-recording",
        name: "Ruta nueva GPS",
        area: `${selectedMapPack.shortName} · ubicación actual`,
        kind: "senderismo",
        distance: "En grabación",
        time: "Tiempo activo",
        ascent: "Medición GPS",
        difficulty: "Por definir",
        points: "22,76 80,68 140,55 210,49 285,38 346,30",
        tags: ["GPS libre"],
        status: "En revisión",
      }
    : selectedRoute, [recordingMode, selectedMapPack.shortName, selectedRoute]);
  const placeMapRoute = useMemo<RouteItem>(() => {
    if (!selectedPlace) return recordingMapRoute;
    const location = `${selectedPlace.region} ${selectedPlace.locality} ${selectedPlace.label}`;
    const placePack = mapPackForPoint(selectedPlace.longitude, selectedPlace.latitude, location);
    return {
      ...recordingMapRoute,
      id: `place-${selectedPlace.id}`,
      name: selectedPlace.name,
      area: `${placePack?.shortName ?? selectedPlace.region ?? "Ecuador"} · ${selectedPlace.locality || selectedPlace.region || "resultado"}`,
    };
  }, [recordingMapRoute, selectedPlace]);
  const liveStats = useMemo(
    () => navigationStats(recordingGeojson, track, seconds, recordingMode === "selected-route" && routeNavigationActive),
    [recordingGeojson, recordingMode, track, seconds, routeNavigationActive],
  );
  const routeStart = useMemo(() => routeCoordinates(recordingGeojson)[0] ?? null, [recordingGeojson]);
  // Durante una ruta guiada Android debe mantener el paquete regional que
  // contiene el inicio de esa ruta. Esto evita que al cambiar entre Explorar
  // y Grabar el visor vuelva al mapa nacional/mundial o a otra region.
  const guidedRouteMapPackId = useMemo(() => {
    if (!isAndroidApp || !routeStart) return activeMapPackId;
    return mapPackForPoint(routeStart.longitude, routeStart.latitude, selectedRoute.area)?.id
      ?? selectedMapPack.id;
  }, [activeMapPackId, isAndroidApp, routeStart, selectedMapPack.id, selectedRoute.area]);
  const currentTrackPoint = track.at(-1);
  const currentNavigationPosition = livePosition ?? currentTrackPoint ?? null;

  useEffect(() => {
    if (!isAndroidApp || activeTab !== "grabar" || !currentNavigationPosition) return;
    // Radix desmonta/monta el contenido al cambiar de seccion. MapLibre puede recibir
    // la posicion antes de terminar de cargar el estilo; por eso reenviamos la senal
    // unas veces durante el primer segundo. En web no se ejecuta.
    const timers = [180, 700, 1400].map((delay) => window.setTimeout(() => {
      setMobileMapRecenterSignal((value) => value + 1);
    }, delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [activeTab, currentNavigationPosition, isAndroidApp]);
  const recordingWeatherPoint = recordingMode === "selected-route"
    ? routeStart
    : currentNavigationPosition
      ? { longitude: currentNavigationPosition.longitude, latitude: currentNavigationPosition.latitude, elevation: currentNavigationPosition.altitude }
      : null;

  // La guia azul no depende del filtro del GPX. Antes de llegar al inicio apunta
  // al punto inicial; una vez iniciada la ruta, si el usuario se separa del
  // trazado, apunta al punto mas cercano de la ruta naranja.
    const mountainAISnapshot = useMemo<TheraRuntimeSnapshot>(() => {
    const timestamp = new Date().toISOString();
    const gpsTimestamp = currentNavigationPosition && Number.isFinite(currentNavigationPosition.timestamp)
      ? new Date(currentNavigationPosition.timestamp).toISOString()
      : timestamp;
    const routeAvailable = recording && recordingMode === "selected-route";
    const routeDeviationMeters = routeAvailable && routeNavigationActive ? liveStats.offRouteDistanceM : null;
    const weatherPoint = mountainAIWeather?.points.length
      ? currentNavigationPosition
        ? mountainAIWeather.points.reduce((nearest, point) => {
            const nearestDistance = haversineKm(currentNavigationPosition, { latitude: nearest.latitude, longitude: nearest.longitude });
            const pointDistance = haversineKm(currentNavigationPosition, { latitude: point.latitude, longitude: point.longitude });
            return pointDistance < nearestDistance ? point : nearest;
          })
        : mountainAIWeather.points[0]
      : null;
    const alerts = routeAvailable && routeNavigationActive && liveStats.offRoute
      ? [{
        level: "WARNING" as const,
        code: "OFF_ROUTE",
        message: liveStats.offRouteDistanceM === null
          ? "THERA detecta que la posicion esta fuera del trazado activo."
          : `THERA detecta una desviacion aproximada de ${Math.round(liveStats.offRouteDistanceM)} m respecto del trazado activo.`,
        timestamp,
      }]
      : [];

    return {
      timestamp,
      networkState: networkOnline ? "ONLINE" : "OFFLINE",
      ...(currentNavigationPosition ? {
        gps: {
          latitude: currentNavigationPosition.latitude,
          longitude: currentNavigationPosition.longitude,
          altitudeMeters: currentNavigationPosition.altitude,
          accuracyMeters: currentNavigationPosition.accuracy,
          timestamp: gpsTimestamp,
        },
      } : {}),
      ...(routeAvailable ? {
        route: {
          distanceTravelledMeters: liveStats.recordedDistanceKm * 1000,
          distanceRemainingMeters: liveStats.remainingDistanceKm * 1000,
          progressPercent: liveStats.progress,
          routeDeviationMeters,
          estimatedTimeRemainingMinutes: routeNavigationActive && liveStats.estimatedRemainingSeconds !== null
            ? liveStats.estimatedRemainingSeconds / 60
            : null,
          timestamp,
        },
      } : {}),
      ...(weatherPoint && mountainAIWeather ? {
        weather: {
          temperatureCelsius: weatherPoint.temperatureC,
          windSpeedKph: weatherPoint.windKmh,
          gustSpeedKph: weatherPoint.gustKmh,
          precipitationMm: weatherPoint.precipitationMm,
          precipitationProbabilityPercent: weatherPoint.precipitationProbability,
          visibilityMeters: weatherPoint.visibilityM,
          uvIndex: weatherPoint.uvIndex,
          next6HoursPrecipitationMm: weatherPoint.next6HoursPrecipitationMm,
          condition: weatherConditionLabel(weatherPoint),
          cached: mountainAIWeather.offline,
          timestamp: new Date(mountainAIWeather.updatedAt).toISOString(),
        },
      } : {}),
      alerts,
    };
  }, [currentNavigationPosition, liveStats, mountainAIWeather, networkOnline, recording, recordingMode, routeNavigationActive]);
    const mountainAIWeatherTarget = useMemo(() => selectMountainAIWeatherTarget({
    recordingMode,
    selectedRouteId: selectedRoute.id,
    recordingGeojson,
    routeChosen,
    selectedRouteGeojson: selectedRoute.geojson,
    currentPosition: currentNavigationPosition
      ? {
          latitude: currentNavigationPosition.latitude,
          longitude: currentNavigationPosition.longitude,
          elevation: currentNavigationPosition.altitude,
        }
      : null,
    selectedPlace: selectedPlace
      ? {
          id: selectedPlace.id,
          latitude: selectedPlace.latitude,
          longitude: selectedPlace.longitude,
          elevation: null,
        }
      : null,
  }), [
    recordingMode,
    recordingGeojson,
    routeChosen,
    selectedRoute.id,
    selectedRoute.geojson,
    selectedPlace?.id,
    selectedPlace?.latitude,
    selectedPlace?.longitude,
    currentNavigationPosition
      ? `${currentNavigationPosition.latitude.toFixed(2)}:${currentNavigationPosition.longitude.toFixed(2)}`
      : "sin-gps",
  ]);

  useEffect(() => {
    let cancelled = false;

    if (!mountainAIWeatherTarget) {
      setMountainAIWeather(null);
      return () => { cancelled = true; };
    }

    setMountainAIWeather(null);

    const loadMountainAIWeather = async () => {
      try {
        const result = mountainAIWeatherTarget.kind === "route"
          ? await fetchRouteWeather(mountainAIWeatherTarget.routeId, mountainAIWeatherTarget.geojson)
          : await fetchPointWeather(mountainAIWeatherTarget.routeId, mountainAIWeatherTarget.point);
        if (!cancelled) setMountainAIWeather(result);
      } catch {
        if (!cancelled) setMountainAIWeather(null);
      }
    };

    void loadMountainAIWeather();
    const refreshTimer = window.setInterval(() => {
      void loadMountainAIWeather();
    }, 30 * 60 * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(refreshTimer);
    };
  }, [mountainAIWeatherTarget?.key, networkOnline]);
  const nearestActiveRoutePoint = useMemo(() => {
    if (recordingMode !== "selected-route" || !routeNavigationActive || !currentNavigationPosition || !recordingGeojson) return null;
    return nearestRoutePosition(currentNavigationPosition, routeCoordinates(recordingGeojson));
  }, [recordingMode, routeNavigationActive, currentNavigationPosition, recordingGeojson]);
  const visualOffRouteLimitM = Math.max(50, (currentNavigationPosition?.accuracy ?? 0) * 1.5);
  const visualOffRoute = Boolean(nearestActiveRoutePoint && nearestActiveRoutePoint.distanceM > visualOffRouteLimitM);
  const approachingStartNow = Boolean(recordingMode === "selected-route" && !routeNavigationActive && currentNavigationPosition && routeStart);
  const approachTarget = approachingStartNow && routeStart
    ? [routeStart.longitude, routeStart.latitude] as [number, number]
    : visualOffRoute && nearestActiveRoutePoint
      ? [nearestActiveRoutePoint.coordinate.longitude, nearestActiveRoutePoint.coordinate.latitude] as [number, number]
      : undefined;

  const roadProgress = useMemo(
    () => roadApproachProgress(roadApproachRoute, currentNavigationPosition ?? undefined),
    [roadApproachRoute, currentNavigationPosition],
  );
  const directDistanceToStartKm = currentNavigationPosition && routeStart
    ? haversineKm(currentNavigationPosition, routeStart)
    : liveStats.distanceToStartKm;
  const approachDistanceKm = approachingStartNow
    ? roadProgress?.remainingKm ?? directDistanceToStartKm
    : null;
  const approachTimeSeconds = approachingStartNow
    ? roadProgress?.remainingSeconds ?? null
    : null;

  const calculateRoadApproach = useCallback(async (mode: ApproachMode, automatic = false) => {
    if (recordingMode !== "selected-route") return;
    const origin = livePosition ?? trackRef.current.at(-1);
    const destination = routeCoordinates(selectedRoute.geojson)[0];
    if (!origin || !destination) {
      if (!automatic) setToast("Espera a que el GPS obtenga una ubicación antes de calcular el camino.");
      return;
    }
    setApproachMode(mode);
    setApproachRoutingBusy(true);
    if (!automatic) setApproachRoutingMessage("Calculando el camino hasta el inicio…");
    try {
      const result = await fetchRoadApproachRoute({
        routeId: selectedRoute.id,
        mode,
        origin: { latitude: origin.latitude, longitude: origin.longitude },
        destination: { latitude: destination.latitude, longitude: destination.longitude },
      });
      setRoadApproachRoute(result);
      setApproachRoutingMessage(result.offline
        ? "Mostrando la última aproximación guardada sin conexión."
        : `${mode === "driving-car" ? "Ruta en vehículo" : "Ruta a pie"} calculada por caminos registrados.`);
    } catch (error) {
      roadApproachRouteRef.current = null;
      setRoadApproachRoute(null);
      setApproachRoutingMessage(error instanceof Error
        ? error.message
        : "No fue posible calcular el camino. Se mantiene la línea directa.");
    } finally {
      setApproachRoutingBusy(false);
    }
  }, [recordingMode, selectedRoute.geojson, selectedRoute.id, livePosition]);

  useEffect(() => {
    const alertsEnabled = deviationAlertMode !== "none";
    const selectedRouteActive = recordingMode === "selected-route" && recording && routeNavigationActive;

    if (!selectedRouteActive) {
      wasOffRoute.current = false;
      return;
    }

    if (!liveStats.offRoute) {
      if (wasOffRoute.current && alertsEnabled) {
        if (deviationAlertMode === "vibration" || deviationAlertMode === "both") navigator.vibrate?.([120, 70, 120]);
        if (deviationAlertMode === "message" || deviationAlertMode === "both") setGpsMessage("Ruta recuperada.");
      }
      wasOffRoute.current = false;
      return;
    }

    const now = Date.now();
    const firstDeviation = !wasOffRoute.current;
    wasOffRoute.current = true;
    if (!alertsEnabled || (!firstDeviation && now - lastOffRouteAlert.current < 45_000)) return;
    lastOffRouteAlert.current = now;

    if (deviationAlertMode === "vibration" || deviationAlertMode === "both") navigator.vibrate?.([240, 140, 240]);
    if (deviationAlertMode === "message" || deviationAlertMode === "both") {
      setGpsMessage(`Te saliste de la ruta${liveStats.offRouteDistanceM ? ` · aprox. ${liveStats.offRouteDistanceM} m` : ""}. Vuelve al trazado.`);
    }
  }, [deviationAlertMode, recordingMode, recording, routeNavigationActive, liveStats.offRoute, liveStats.offRouteDistanceM]);

  // Al iniciar una ruta seleccionada o planificada lejos del punto inicial,
  // calcular automaticamente una aproximacion por caminos/senderos desde la
  // ubicacion GPS actual. fetchRoadApproachRoute reutiliza la ruta guardada
  // cuando existe; si no puede calcularla, se conserva la guia directa como
  // respaldo sin alterar el GPS ni el trazado naranja de la actividad.
  useEffect(() => {
    // La aproximacion se prepara desde que la ruta queda seleccionada; no hay
    // que esperar a que empiece a guardarse el GPX. Asi la guia azul aparece
    // inmediatamente desde la ubicacion actual hasta el Inicio.
    if (recordingMode !== "selected-route" || !approachingStartNow || !currentNavigationPosition || !routeStart) return;
    if (roadApproachRoute || approachRoutingBusy) return;
    const now = Date.now();
    if (now - lastApproachRecalculation.current < 60_000) return;
    lastApproachRecalculation.current = now;
    calculateRoadApproach(approachMode, true).catch(() => undefined);
  }, [approachMode, approachRoutingBusy, calculateRoadApproach, approachingStartNow, currentNavigationPosition, recordingMode, roadApproachRoute, routeStart]);

  useEffect(() => {
    if (recordingMode !== "selected-route" || !recording || !approachingStartNow || !networkOnline || !roadApproachRoute || !roadProgress) return;
    if (roadProgress.deviationM <= 120 || approachRoutingBusy) return;
    const now = Date.now();
    if (now - lastApproachRecalculation.current < 60_000) return;
    lastApproachRecalculation.current = now;
    calculateRoadApproach(approachMode, true).catch(() => undefined);
  }, [approachMode, approachRoutingBusy, calculateRoadApproach, approachingStartNow, networkOnline, recording, recordingMode, roadApproachRoute, roadProgress]);

  useEffect(() => {
    if (recording) return;
    // Cada ruta nueva debe poder calcular su aproximación inmediatamente.
    // Sin este reinicio, el límite de 60 s de una ruta anterior podía bloquear
    // la primera guía azul de la nueva ruta hasta cambiar de pantalla.
    lastApproachRecalculation.current = 0;
    roadApproachRouteRef.current = null;
    setRoadApproachRoute(null);
    setApproachRoutingMessage("Línea directa disponible sin conexión.");
  }, [selectedRoute.id, recording]);

  useEffect(() => {
    if (!networkOnline || !currentProfile || currentProfile.status !== "approved") return;
    let cancelled = false;

    const syncBatch = async (items: NavigationSession[]) => {
      let completed = 0;
      for (const activity of items) {
        if (cancelled) return completed;
        if (syncingActivities.current.has(activity.id)) continue;

        syncingActivities.current.add(activity.id);
        try {
          await syncNavigationActivity({ ...activity, syncStatus: "pending" });
          await registerActivityFieldSurveyContribution(activity.id).catch(() => undefined);
          await updateActivitySyncStatus(activity.id, "synced");
          setActivityHistory((current) => current.map((item) =>
            item.id === activity.id ? { ...item, syncStatus: "synced" } : item
          ));
          completed += 1;
        } catch {
          await updateActivitySyncStatus(activity.id, "error").catch(() => undefined);
        } finally {
          syncingActivities.current.delete(activity.id);
        }
      }
      return completed;
    };

    const drainPendingActivitySync = async () => {
      while (!cancelled) {
        const batch = await listPendingActivitySyncBatch(currentProfile.id, 25, false);
        if (!batch.length) break;
        const completed = await syncBatch(batch);
        if (!completed) break;
      }

      if (!cancelled) {
        const retryBatch = await listPendingActivitySyncBatch(currentProfile.id, 10, true);
        const errorsOnly = retryBatch.filter((activity) => activity.syncStatus === "error");
        if (errorsOnly.length) await syncBatch(errorsOnly);
      }
    };

    drainPendingActivitySync().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [currentProfile?.id, currentProfile?.status, networkOnline]);

  useEffect(() => {
    if (!currentProfile || !activityId || !activityStartedAt || (!recording && !recordingPaused)) return;
    if (seconds % 10 !== 0 && track.length > 1) return;
    saveActiveNavigation({
      id: activityId,
      storageKey: "",
      ownerId: currentProfile.id,
      routeId: recordingRouteId,
      routeName: recordingRouteName,
      recordingMode: recordingMode ?? "free",
      kind: recordingKind,
      state: recordingPaused ? "paused" : "recording",
      startedAt: activityStartedAt,
      finishedAt: null,
      elapsedSeconds: seconds,
      points: track,
      markers: activityMarkers,
      stats: liveStats,
      sourcePlatform: currentPlatform(),
      syncStatus: "local",
      updatedAt: Date.now(),
    }).catch(() => undefined);
  }, [activityId, activityMarkers, activityStartedAt, currentProfile, liveStats, recording, recordingKind, recordingMode, recordingPaused, recordingRouteId, recordingRouteName, seconds, track]);

  // Field Session Logger V1: lectura desacoplada de la ruta actual.
  // No inicia GPS, no altera el filtrado GPX y no modifica Background Tracking.
  useEffect(() => {
    if (!activityId || !activityStartedAt || (!recording && !recordingPaused)) return;
    const latestPoint = track.at(-1);
    if (!latestPoint) return;
    appendFieldRouteSample({
      sessionKey: activityId,
      activityId,
      routeId: recordingRouteId,
      routeName: recordingRouteName,
      recordingMode: recordingMode ?? "free",
      startedAt: activityStartedAt,
      state: recordingPaused ? "paused" : "recording",
    }, latestPoint, Math.max(0, distance * 1000));
  }, [activityId, activityStartedAt, distance, recording, recordingMode, recordingPaused, recordingRouteId, recordingRouteName, track.length]);
  const filteredRoutes = useMemo(
    () => {
      const query = routeSearch.trim().toLocaleLowerCase("es");
      return availableRoutes.filter((route) =>
        activityFilter === "senderismo"
          ? route.kind === "senderismo"
          : route.kind !== "senderismo",
      ).filter((route) => !query || `${route.name} ${route.area} ${route.tags.join(" ")}`.toLocaleLowerCase("es").includes(query));
    },
    [activityFilter, availableRoutes, routeSearch],
  );
  useEffect(() => {
    const selectFromAddress = () => {
      const routeId = new URL(window.location.href).searchParams.get("route");
      if (!routeId) {
        setRouteChosen(false);
        return;
      }
      const routeFromAddress = availableRoutes.find((route) => route.id === routeId);
      if (routeFromAddress) {
        setSelectedRoute(routeFromAddress);
        const first = routeCoordinates(routeFromAddress.geojson)[0];
        const pack = mapPackForText(routeFromAddress.area)
          ?? (first ? mapPackForPoint(first.longitude, first.latitude, routeFromAddress.area) : null);
        if (pack) setSelectedMapPackId(pack.id);
        setRouteChosen(true);
      }
    };
    selectFromAddress();
    window.addEventListener("popstate", selectFromAddress);
    return () => window.removeEventListener("popstate", selectFromAddress);
  }, [availableRoutes]);

  useEffect(() => {
    if (!availableRoutes.length) return;
    const stillAvailable = availableRoutes.some((route) => route.id === selectedRoute.id);
    // Una planificación creada por el usuario es una ruta transitoria y no pertenece
    // al catálogo de rutas registradas. Mientras se esté siguiendo esa planificación,
    // nunca sustituirla por la primera ruta del catálogo.
    const followingTransientPlan = recordingMode === "selected-route" && !stillAvailable;
    if (!stillAvailable && !followingTransientPlan) setSelectedRoute(availableRoutes[0]);
  }, [availableRoutes, recordingMode, selectedRoute.id]);
  const activeDownloads = useMemo(
    () =>
      Object.entries(packProgress).filter(
        ([, value]) => value > 0 && value < 100,
      ),
    [packProgress],
  );
  const realMapsEnabled = import.meta.env.VITE_REAL_MAPS === "true";
  const searchPlaces = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = routeSearch.trim();
    if (query.length < 2) {
      setPlaceResults([]);
      setPlaceSearchMessage("Escribe al menos dos letras para buscar.");
      return;
    }

    // Android nativo usa una capa propia: intenta el geocodificador publicado
    // cuando hay red y cae al índice incluido en el APK cuando no hay conexión
    // o el endpoint remoto no responde. Web/PWA conservan su flujo existente.
    if (isNativeAndroidPlaceSearch()) {
      setPlaceSearchBusy(true);
      setPlaceSearchMessage("");
      try {
        const outcome = await searchAndroidPlaces(query, networkOnline, selectedMapPackId);
        setPlaceResults(outcome.results);
        setPlaceSearchMessage(outcome.message);
      } finally {
        setPlaceSearchBusy(false);
      }
      return;
    }

    if (!networkOnline) {
      setPlaceResults([]);
      setPlaceSearchMessage("Sin conexión: las rutas guardadas siguen disponibles, pero buscar lugares nuevos requiere internet.");
      return;
    }
    setPlaceSearchBusy(true);
    setPlaceSearchMessage("");
    try {
      const response = await fetch(`/api/geocode?q=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" },
      });
      const payload = await response.json() as { results?: PlaceSearchResult[]; error?: string };
      if (!response.ok) throw new Error(payload.error || "No fue posible buscar ese lugar.");
      const results = Array.isArray(payload.results) ? payload.results : [];
      setPlaceResults(results);
      setPlaceSearchMessage(results.length
        ? `${results.length} ${results.length === 1 ? "lugar encontrado" : "lugares similares encontrados"}.`
        : "No encontramos ese nombre. Prueba otra escritura o una localidad cercana.");
    } catch (error) {
      setPlaceResults([]);
      setPlaceSearchMessage(getErrorMessage(error, "No fue posible buscar ese lugar."));
    } finally {
      setPlaceSearchBusy(false);
    }
  };
  const chooseMapPack = (id: string) => {
    const pack = mapPackById(id);
    if (!pack) return;
    setMapRegionPickerOpen(false);
    setSelectedMapPackId(pack.id);
    setSelectedPlace(null);
    setRouteChosen(false);
    setPlannedRouteSegment(null);
    const address = new URL(window.location.href);
    address.searchParams.delete("route");
    window.history.replaceState({}, "", address);
  };
  const openPlace = (place: PlaceSearchResult) => {
    setSelectedPlace(place);
    const preferredPlacePack = place.mapPackId ? mapPackById(place.mapPackId) : null;
    const placePack = preferredPlacePack
      ?? mapPackForPoint(place.longitude, place.latitude, `${place.region} ${place.locality} ${place.label}`);
    if (placePack) setSelectedMapPackId(placePack.id);
    setPlannedRouteSegment(null);
    setRouteChosen(false);
    setRoutesExpanded(false);
    const address = new URL(window.location.href);
    address.searchParams.delete("route");
    window.history.replaceState({}, "", address);
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      const mapSection = mapSectionRef.current;
      if (!mapSection) return;
      mapSection.focus({ preventScroll: true });
      mapSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }));
  };
  const focusExploreOnGps = async () => {
    if (!isAndroidApp) return;
    try {
      const point = livePosition ?? await getCurrentTheraPosition();
      setLivePosition(point);
      const pack = mapPackForPoint(point.longitude, point.latitude);
      openPlace({
        id: `gps-${Math.round(point.latitude * 100000)}-${Math.round(point.longitude * 100000)}`,
        name: "Mi ubicación",
        label: `Posición GPS actual${pack ? ` · ${pack.name}` : ""}`,
        locality: pack?.shortName ?? "",
        region: pack?.group ?? "Ecuador",
        country: "Ecuador",
        kind: "gps",
        longitude: point.longitude,
        latitude: point.latitude,
        mapPackId: pack?.id,
      });
    } catch (error) {
      setToast(getErrorMessage(error, "No fue posible obtener tu ubicación GPS en este momento."));
    }
  };

  const openRoute = (routeItem: RouteItem) => {
    setSelectedPlace(null);
    setPlannedRouteSegment(null);
    setSelectedRoute(routeItem);
    const routeStartPoint = routeCoordinates(routeItem.geojson)[0];
    const routePack = mapPackForText(routeItem.area)
      ?? (routeStartPoint ? mapPackForPoint(routeStartPoint.longitude, routeStartPoint.latitude, routeItem.area) : null);
    if (routePack) setSelectedMapPackId(routePack.id);
    setRouteChosen(true);
    setRoutesExpanded(false);
    const address = new URL(window.location.href);
    const previousRouteId = address.searchParams.get("route");
    address.searchParams.set("route", routeItem.id);
    if (previousRouteId !== routeItem.id) {
      window.history.pushState({ routeId: routeItem.id }, "", address);
    }
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      const mapSection = mapSectionRef.current;
      if (!mapSection) return;
      mapSection.focus({ preventScroll: true });
      mapSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }));
  };
  const prepareRecordingMode = (mode: RecordingMode, routeOverride?: RouteItem) => {
    if (!isApprovedMember) {
      setAuthMode(signedInEmail ? "login" : "register");
      setActiveTab("perfil");
      setToast("Grabar y seguir rutas requiere una cuenta aprobada.");
      return;
    }
    if ((recording || activityId) && trackRef.current.length) {
      setActiveTab("grabar");
      setToast("Primero continúa, finaliza o descarta la actividad recuperada.");
      return;
    }
    setTrack([]);
    trackRef.current = [];
    setSeconds(0);
    secondsRef.current = 0;
    setDistance(0);
    setActivityMarkers([]);
    setRecordingMode(mode);
    const routeToRecord = routeOverride ?? selectedRoute;
    if (mode === "selected-route" && routeOverride) {
      setSelectedRoute(routeOverride);
      const first = routeCoordinates(routeOverride.geojson)[0];
      const pack = mapPackForText(routeOverride.area)
        ?? (first ? mapPackForPoint(first.longitude, first.latitude, routeOverride.area) : null);
      if (pack) setSelectedMapPackId(pack.id);

      // Si la ruta proviene de puntos creados por el usuario, es independiente del
      // catálogo de rutas registradas. Elimina cualquier ?route= anterior (por ejemplo
      // Zhinglla-Cachaulo) para impedir que el estado de URL vuelva a seleccionarla.
      const isTransientPlan = !availableRoutes.some((route) => route.id === routeOverride.id);
      if (isTransientPlan) {
        setRouteChosen(false);
        const address = new URL(window.location.href);
        address.searchParams.delete("route");
        window.history.replaceState({}, "", address);
      }
    }
    routeNavigationActiveRef.current = false;
    setRouteNavigationActive(false);
    roadApproachRouteRef.current = null;
    setRoadApproachRoute(null);
    setApproachRoutingMessage("Línea directa disponible sin conexión.");
    if (mode === "free") {
      setRouteChosen(false);
      const address = new URL(window.location.href);
      address.searchParams.delete("route");
      window.history.replaceState({}, "", address);
      setGpsMessage("Ruta nueva preparada · el GPX comenzará desde tu primera posición GPS.");
    } else {
      setGpsMessage(`Ruta seleccionada: ${routeToRecord.name}. Clima y mapa ajustados al punto inicial.`);
    }
    setActiveTab("grabar");
  };
  const chooseRegisteredRoute = () => {
    setRoutesExpanded(true);
    setActiveTab("explorar");
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };
  const plannerRoute = useMemo<RouteItem>(
    () =>
      importedTrack.length > 1
        ? {
            ...selectedRoute,
            name: routeForm.name || "Borrador de ruta",
            area: routeForm.area || "Ubicación por completar",
            kind: routeForm.kind,
            geojson: routeGeoJson(importedTrack, importedWaypoints),
          }
        : routeChosen
          ? selectedRoute
          : {
              ...recordingMapRoute,
              id: "empty-route-planner",
              name: "Borrador sin GPX",
              area: `${selectedMapPack.shortName} · selecciona o importa una ruta`,
            },
    [importedTrack, importedWaypoints, recordingMapRoute, routeChosen, routeForm.name, routeForm.area, routeForm.kind, selectedMapPack.shortName, selectedRoute],
  );
  const plannerRouteDistanceKm = useMemo(() => calculateDistance(importedTrack), [importedTrack]);
  const directPlannerDistanceKm = useMemo(() => {
    let total = 0;
    for (let index = 1; index < plannerPoints.length; index += 1) {
      total += haversineKm(plannerPoints[index - 1], plannerPoints[index]);
    }
    return total;
  }, [plannerPoints]);
  const plannerWeatherPoint = useMemo(() => {
    if (!plannerPoints.length) return null;
    return plannerPoints.find((point) => point.id === plannerWeatherPointId)
      ?? plannerPoints.find((point) => point.kind === "start")
      ?? plannerPoints[0];
  }, [plannerPoints, plannerWeatherPointId]);

  useEffect(() => {
    if (!plannerPoints.length) {
      if (plannerWeatherPointId !== null) setPlannerWeatherPointId(null);
      return;
    }
    if (!plannerWeatherPointId || !plannerPoints.some((point) => point.id === plannerWeatherPointId)) {
      setPlannerWeatherPointId(plannerPoints.find((point) => point.kind === "start")?.id ?? plannerPoints[0].id);
    }
  }, [plannerPoints, plannerWeatherPointId]);

  const plannerRouteCoordinates = useMemo(() => {
    if (!plannerLegs.length) return plannerPoints.map((point) => [point.longitude, point.latitude]);
    const merged: number[][] = [];
    plannerLegs.forEach((leg) => {
      leg.coordinates.forEach((coordinate, index) => {
        if (merged.length && index === 0) return;
        merged.push(coordinate);
      });
    });
    return merged;
  }, [plannerLegs, plannerPoints]);
  const plannerDistanceKm = useMemo(() => plannerLegs.length
    ? plannerLegs.reduce((total, leg) => total + leg.distanceKm, 0)
    : directPlannerDistanceKm, [directPlannerDistanceKm, plannerLegs]);
  const plannerHasDirectFallback = plannerLegs.some((leg) => leg.source === "direct");

  useEffect(() => {
    if (plannerPoints.length < 2) {
      setPlannerLegs([]);
      setPlannerRoutingMessage(plannerPoints.length ? "Añade otro punto para calcular el tramo." : "Marca inicio y destino directamente en el mapa.");
      setPlannerRoutingBusy(false);
      return;
    }
    let cancelled = false;
    const routePlan = async () => {
      setPlannerRoutingBusy(true);
      setPlannerLegs([]);
      setPlannerRoutingMessage(networkOnline ? "Calculando por senderos y vías registradas…" : "Buscando tramos guardados sin conexión…");
      const nextLegs: PlannerLeg[] = [];
      for (let index = 1; index < plannerPoints.length; index += 1) {
        if (cancelled) return;
        const from = plannerPoints[index - 1];
        const to = plannerPoints[index];
        try {
          const result = await fetchRoadApproachRoute({
            routeId: `planner-${plannerTravelMode}-${from.id}-${to.id}`,
            mode: plannerTravelMode,
            origin: { latitude: from.latitude, longitude: from.longitude },
            destination: { latitude: to.latitude, longitude: to.longitude },
          });
          const coordinates = Array.isArray(result.coordinates) && result.coordinates.length > 1
            ? result.coordinates
            : [[from.longitude, from.latitude], [to.longitude, to.latitude]];
          let distanceKm = 0;
          for (let coordinateIndex = 1; coordinateIndex < coordinates.length; coordinateIndex += 1) {
            distanceKm += haversineKm(
              { longitude: coordinates[coordinateIndex - 1][0], latitude: coordinates[coordinateIndex - 1][1] },
              { longitude: coordinates[coordinateIndex][0], latitude: coordinates[coordinateIndex][1] },
            );
          }
          nextLegs.push({
            id: `${from.id}-${to.id}`,
            fromId: from.id,
            toId: to.id,
            coordinates,
            distanceKm,
            durationSeconds: Number.isFinite(result.durationSeconds) ? result.durationSeconds : null,
            source: result.offline ? "cache" : "routing",
          });
        } catch {
          nextLegs.push({
            id: `${from.id}-${to.id}`,
            fromId: from.id,
            toId: to.id,
            coordinates: [[from.longitude, from.latitude], [to.longitude, to.latitude]],
            distanceKm: haversineKm(from, to),
            durationSeconds: null,
            source: "direct",
          });
        }
      }
      if (cancelled) return;
      setPlannerLegs(nextLegs);
      const directCount = nextLegs.filter((leg) => leg.source === "direct").length;
      const cachedCount = nextLegs.filter((leg) => leg.source === "cache").length;
      setPlannerRoutingMessage(directCount
        ? `${directCount} tramo${directCount === 1 ? "" : "s"} sin camino calculado · respaldo directo.`
        : cachedCount
          ? "Ruta recuperada desde tramos guardados · disponible sin conexión."
          : `Ruta ${plannerTravelMode === "driving-car" ? "en vehículo" : "a pie"} calculada por caminos registrados.`);
      setPlannerRoutingBusy(false);
    };
    routePlan().catch(() => {
      if (!cancelled) {
        setPlannerRoutingBusy(false);
        setPlannerRoutingMessage("No fue posible calcular todos los tramos. Se conserva la planificación directa.");
      }
    });
    return () => { cancelled = true; };
  }, [networkOnline, plannerPoints, plannerTravelMode]);

  const plannerPointLabel = useCallback((point: PlannerPoint, index: number) => {
    if (point.kind === "start") return "Inicio";
    if (point.kind === "finish") return "Destino";
    const priorWaypoints = plannerPoints.slice(0, index + 1).filter((candidate) => candidate.kind === "waypoint").length;
    return `Punto ${priorWaypoints}`;
  }, [plannerPoints]);
  const plannerNavigationRoute = useMemo<RouteItem | null>(() => {
    const hasStart = plannerPoints.some((point) => point.kind === "start");
    const hasFinish = plannerPoints.some((point) => point.kind === "finish");
    if (!hasStart || !hasFinish || plannerRouteCoordinates.length < 2) return null;
    const routePoints = plannerRouteCoordinates
      .map((coordinate) => ({
        longitude: Number(coordinate[0]),
        latitude: Number(coordinate[1]),
        elevation: Number.isFinite(coordinate[2]) ? Number(coordinate[2]) : null,
      }))
      .filter((point) => Number.isFinite(point.longitude) && Number.isFinite(point.latitude));
    if (routePoints.length < 2) return null;
    const waypoints: RouteWaypoint[] = plannerPoints.map((point, index) => ({
      id: point.id,
      name: plannerPointLabel(point, index),
      kind: point.kind,
      longitude: point.longitude,
      latitude: point.latitude,
      elevation: point.elevation,
    }));
    const durationSeconds = plannerLegs.reduce((total, leg) => total + (leg.durationSeconds ?? 0), 0);
    const hours = Math.floor(durationSeconds / 3600);
    const minutes = Math.round((durationSeconds % 3600) / 60);
    return {
      ...selectedRoute,
      id: `planner-${plannerPoints.map((point) => point.id.slice(0, 8)).join("-")}`,
      name: routeForm.name.trim() || "Mi planificación",
      area: routeForm.area.trim() || selectedMapPack.shortName,
      kind: routeForm.kind,
      distance: `${formatDistance(plannerDistanceKm)} km`,
      time: durationSeconds > 0 ? `${hours ? `${hours} h ` : ""}${minutes} min` : "Por registrar",
      ascent: "Por registrar",
      difficulty: plannerHasDirectFallback ? "Planificación con tramo por registrar" : "Planificación personal",
      tags: [routeForm.kind, plannerTravelMode === "driving-car" ? "Vehículo" : "A pie", "Planificación personal"],
      status: "En revisión",
      geojson: routeGeoJson(routePoints, waypoints),
    };
  }, [plannerDistanceKm, plannerHasDirectFallback, plannerLegs, plannerPointLabel, plannerPoints, plannerRouteCoordinates, plannerTravelMode, routeForm.area, routeForm.kind, routeForm.name, selectedMapPack.shortName, selectedRoute]);
  const addPlannerPoint = useCallback((coordinate: { latitude: number; longitude: number }) => {
    if (!plannerTool) return;
    setPlannerPoints((current) => {
      const point: PlannerPoint = { id: crypto.randomUUID(), ...coordinate, elevation: null, kind: plannerTool };
      if (plannerTool === "start") return [point, ...current.filter((item) => item.kind !== "start")];
      if (!current.some((item) => item.kind === "start")) {
        setToast("Primero marca el punto de inicio.");
        return current;
      }
      if (plannerTool === "finish") return [...current.filter((item) => item.kind !== "finish"), point];
      const finishIndex = current.findIndex((item) => item.kind === "finish");
      if (finishIndex < 0) return [...current, point];
      return [...current.slice(0, finishIndex), point, ...current.slice(finishIndex)];
    });
    setPlannerTool(null);
  }, [plannerTool]);
  const removePlannerPoint = useCallback((id: string) => {
    setPlannerPoints((current) => current.filter((point) => point.id !== id));
  }, []);
  const clearPlannerPoints = useCallback(() => {
    setPlannerPoints([]);
    setPlannerTool(null);
    setToast("Planificación eliminada del dispositivo.");
  }, []);
  const selectedPanorama = panoramaResults.find((panorama) => panorama.id === selectedPanoramaId)
    ?? panoramaResults[0]
    ?? null;

  const requestWakeLock = async () => {
    const wakeNavigator = navigator as Navigator & {
      wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinelLike> };
    };
    if (!wakeNavigator.wakeLock) return;
    try {
      wakeLock.current = await wakeNavigator.wakeLock.request("screen");
    } catch {
      setGpsMessage(
        "GPS activo; no fue posible mantener la pantalla encendida.",
      );
    }
  };

  const downloadPack = async (id: string, file: string) => {
    if (!isApprovedMember) {
      setAuthMode(signedInEmail ? "login" : "register");
      setActiveTab("perfil");
      setToast("Los mapas sin conexión están disponibles para miembros aprobados.");
      return;
    }
    if (packRemoteInfo[id]?.checked && !packRemoteInfo[id].available) {
      setToast("Este paquete todavía no está cargado en R2. Genera y sube su mapa y relieve con los nombres indicados.");
      return;
    }
    if ((packProgress[id] ?? 0) === 100) return;
    const realMaps = import.meta.env.VITE_REAL_MAPS === "true";
    if (realMaps) {
      setPackProgress((current) => ({ ...current, [id]: 1 }));
      try {
        const terrainFile = terrainFileFor(file);
        const contourFile = contourFileFor(file);
        const contourExpected = Boolean(contourFile && packRemoteInfo[id]?.contourAvailable);
        let baseSaved = await hasStoredMapPack(file); let terrainSaved = !terrainFile || await hasStoredMapPack(terrainFile); let contourSaved = !contourExpected || !contourFile || await hasStoredMapPack(contourFile);

        // Distribución de progreso: mapa 45%, relieve 40%, curvas 15%.
        // Si esta región aún no tiene curvas en R2 se conserva el flujo 55/45.
        const mapEnd = contourExpected ? 45 : (terrainFile ? 55 : 100);
        const terrainEnd = contourExpected ? 85 : 100;

        if (!baseSaved) {
          const result = await downloadMapPack(file, (value) => {
            const adjusted = Math.round((value / 100) * mapEnd);
            setPackProgress((current) => ({ ...current, [id]: adjusted }));
          });
          baseSaved = result.saved;
        } else {
          setPackProgress((current) => ({ ...current, [id]: Math.max(current[id] ?? 0, mapEnd) }));
        }

        if (terrainFile && !terrainSaved && baseSaved) {
          try {
          const result = await downloadMapPack(terrainFile, (value) => {
            const adjusted = mapEnd + Math.round((value / 100) * (terrainEnd - mapEnd));
            setPackProgress((current) => ({ ...current, [id]: adjusted }));
          });
          terrainSaved = result.saved;
          } catch {
            terrainSaved = false;
          }
        } else if (terrainFile && terrainSaved) {
          setPackProgress((current) => ({ ...current, [id]: Math.max(current[id] ?? 0, terrainEnd) }));
        }

        if (contourExpected && contourFile && !contourSaved && baseSaved) {
          try {
          const result = await downloadMapPack(contourFile, (value) => {
            const adjusted = 85 + Math.round(value * 0.15);
            setPackProgress((current) => ({ ...current, [id]: adjusted }));
          });
          contourSaved = result.saved;
          } catch {
            contourSaved = false;
          }
        }

        const saved = baseSaved && terrainSaved && contourSaved;
        setPackProgress((current) => ({ ...current, [id]: saved ? 100 : 0 }));
        await refreshOfflineStorageState();
        if (baseSaved && isAndroidApp) {
          // No descarga nada adicional: usa el mismo PMTiles recién guardado y
          // prepara su índice de nombres en un worker persistente.
          void scheduleMapLabelIndexForPack(id, true);
        }
        setToast(
          !baseSaved
            ? "No fue posible conservar el mapa base regional."
            : terrainSaved && contourSaved
              ? contourExpected
                ? "Mapa, relieve 3D y curvas topográficas guardados para usarlos sin conexión."
                : "Mapa y relieve 3D verificados y guardados para usarlos sin conexión."
              : !terrainSaved && !contourSaved
                ? "Mapa base guardado. Relieve 3D y curvas quedaron pendientes; puedes usar el mapa y completar después."
                : !terrainSaved
                  ? "Mapa base guardado. El relieve 3D quedó pendiente; puedes usar el mapa y completar después."
                  : "Mapa base guardado. Las curvas topográficas quedaron pendientes; puedes usar el mapa y completar después."
        );
      } catch (error) {
        setPackProgress((current) => ({ ...current, [id]: 0 }));
        setToast(error instanceof Error ? error.message : "No fue posible guardar el paquete regional.");
      }
      return;
    }
    setPackProgress((current) => ({ ...current, [id]: 8 }));
    const timer = window.setInterval(
      () =>
        setPackProgress((current) => {
          const next = Math.min((current[id] ?? 0) + 12, 100);
          if (next === 100) window.clearInterval(timer);
          return { ...current, [id]: next };
        }),
      260,
    );
  };

  const openOfflinePack = (id: string) => {
    const pack = mapPackById(id);
    if (!pack || !offlinePackState[id]?.usable) return;
    chooseMapPack(pack.id);
    setActiveTab("explorar");
    setToast(`${pack.name} está activo y disponible sin conexión.`);
  };

  const removeOfflinePack = async (id: string, file: string) => {
    const pack = mapPackById(id);
    if (!pack) return;
    const confirmed = window.confirm(`¿Eliminar ${pack.name}, su relieve y sus curvas topográficas del dispositivo? Podrás descargarlos nuevamente cuando tengas conexión.`);
    if (!confirmed) return;
    try {
      const contourFile = contourFileFor(file);
      await Promise.all([
        removeStoredMapPack(file),
        pack.terrainFile ? removeStoredMapPack(pack.terrainFile) : Promise.resolve(),
        contourFile ? removeStoredMapPack(contourFile) : Promise.resolve(),
      ]);
      if (isAndroidApp) await removeMapLabelIndex(file).catch(() => undefined);
      setPackProgress((current) => ({ ...current, [id]: 0 }));
      await refreshOfflineStorageState();
      if (selectedMapPackId === id) {
        const replacement = ANDROID_OFFLINE_PACKS.find((candidate) => candidate.id !== id && (packProgress[candidate.id] ?? 0) === 100);
        chooseMapPack(replacement?.id ?? "ecuador");
      }
      setToast(`${pack.name} fue eliminado del dispositivo.`);
    } catch {
      setToast("No fue posible eliminar este mapa del dispositivo.");
    }
  };

  const applyNativeElapsedMilliseconds = useCallback((elapsedMilliseconds: number, exact = false) => {
    if (!Number.isFinite(elapsedMilliseconds)) return;
    const nativeSeconds = Math.max(0, Math.floor(elapsedMilliseconds / 1000));
    const nextSeconds = exact ? nativeSeconds : Math.max(secondsRef.current, nativeSeconds);
    secondsRef.current = nextSeconds;
    setSeconds(nextSeconds);
  }, []);

  const mergeNativeRecoveryCursor = useCallback((nextCursor: NativeRecoveryCursor) => {
    const current = nativeRecoveryCursorRef.current;
    const afterPointId = Math.max(current.afterPointId ?? 0, nextCursor.afterPointId ?? 0);
    const afterTimestamp = Math.max(current.afterTimestamp ?? 0, nextCursor.afterTimestamp ?? 0);
    nativeRecoveryCursorRef.current = {
      afterPointId: afterPointId > 0 ? afterPointId : undefined,
      afterTimestamp: afterTimestamp > 0 ? afterTimestamp : undefined,
    };
  }, []);

  const persistRecoveredRecording = useCallback(async (
    ownerId: string,
    targetActivityId: string,
    targetStartedAt: number,
  ) => {
    const points = trackRef.current;
    const elapsedSeconds = secondsRef.current;
    await saveActivityDraft(points, elapsedSeconds, ownerId).catch(() => undefined);
    await saveActiveNavigation({
      id: targetActivityId,
      storageKey: "",
      ownerId,
      routeId: recordingRouteId,
      routeName: recordingRouteName,
      recordingMode: recordingMode ?? "free",
      kind: recordingKind,
      state: pausedRef.current ? "paused" : "recording",
      startedAt: targetStartedAt,
      finishedAt: null,
      elapsedSeconds,
      points,
      markers: activityMarkers,
      stats: navigationStats(
        recordingGeojson,
        points,
        elapsedSeconds,
        recordingMode === "free" || routeNavigationActiveRef.current,
      ),
      sourcePlatform: currentPlatform(),
      syncStatus: "local",
      updatedAt: Date.now(),
    }).catch(() => undefined);
  }, [activityMarkers, recordingGeojson, recordingKind, recordingMode, recordingRouteId, recordingRouteName]);

  const handleRecordedPoint = useCallback((
    rawPoint: RecorderTrackPoint,
    context: { ownerId: string; activityId: string; startedAt: number },
    options: { fromRecovery?: boolean; persist?: boolean } = {},
  ) => {
    const fromRecovery = options.fromRecovery === true;
    const persist = options.persist !== false;
    const nativePointId = rawPoint.nativePointId;

    if (!fromRecovery && nativePointId && nativeRecoveryInProgressRef.current) {
      nativeRecoveryBufferRef.current.push(rawPoint);
      return;
    }

    // Web/PWA sigue usando pausedRef. En Android, el servicio nativo ya controla
    // la pausa y puede entregar un ultimo punto que fue capturado justo antes de pausarse.
    if (!fromRecovery && pausedRef.current && !nativePointId) return;

    if (nativePointId) {
      const currentPointId = nativeRecoveryCursorRef.current.afterPointId ?? 0;
      if (nativePointId <= currentPointId) return;
      mergeNativeRecoveryCursor({
        afterPointId: nativePointId,
        afterTimestamp: rawPoint.timestamp,
      });
    }

    const nativeActiveSeconds = typeof rawPoint.activeMilliseconds === "number"
      ? Math.max(0, Math.floor(rawPoint.activeMilliseconds / 1000))
      : rawPoint.activeSeconds;
    if (typeof rawPoint.activeMilliseconds === "number") {
      applyNativeElapsedMilliseconds(rawPoint.activeMilliseconds, false);
    }

    const point: TrackPoint = {
      latitude: rawPoint.latitude,
      longitude: rawPoint.longitude,
      altitude: rawPoint.altitude ?? null,
      accuracy: rawPoint.accuracy,
      timestamp: rawPoint.timestamp,
      activeSeconds: nativeActiveSeconds ?? secondsRef.current,
    };

    // Siempre actualizar la posicion visual. El filtro siguiente solo decide
    // si el punto se guarda en el GPX, no si el usuario se ve en el mapa.
    setLivePosition(point);
    const previous = trackRef.current.at(-1);
    if (!acceptedTrackPoint(previous, point)) {
      setGpsMessage(`GPS activo · esperando desplazamiento (precisión aproximada ${Math.round(point.accuracy)} m)`);
      return;
    }

    const nextTrack = [...trackRef.current, point];
    trackRef.current = nextTrack;
    setTrack(nextTrack);
    setDistance(calculateDistance(nextTrack));
    if (recordingMode === "free" && !previous) {
      const startPack = mapPackForPoint(point.longitude, point.latitude);
      if (startPack) setSelectedMapPackId(startPack.id);
      setGpsMessage(`Primer punto guardado · mapa y clima actualizados al sector ${startPack?.shortName ?? "Ecuador"}.`);
    }
    const start = recordingMode === "selected-route" ? routeCoordinates(selectedRoute.geojson)[0] : undefined;
    const distanceToStartKm = start ? haversineKm(point, start) : null;
    let routeStarted = recordingMode === "free" || routeNavigationActiveRef.current;
    if (recordingMode === "selected-route" && !routeStarted && distanceToStartKm !== null && distanceToStartKm * 1000 <= ROUTE_START_REACHED_M) {
      routeStarted = true;
      routeNavigationActiveRef.current = true;
      setRouteNavigationActive(true);
      roadApproachRouteRef.current = null;
      setRoadApproachRoute(null);
      navigator.vibrate?.([120, 80, 120]);
      setGpsMessage("Inicio alcanzado · navegación de la ruta activada.");
    } else if (recordingMode === "selected-route" && !routeStarted && distanceToStartKm !== null) {
      setGpsMessage(roadApproachRouteRef.current
        ? `Aproximación por caminos activa · ${formatDistance(distanceToStartKm)} km directos al inicio`
        : `Aproximación al inicio · ${formatDistance(distanceToStartKm)} km en línea recta`);
    } else if (recordingMode === "free") {
      const startPack = !previous ? mapPackForPoint(point.longitude, point.latitude) : null;
      setGpsMessage(!previous
        ? `Primer punto guardado · mapa y clima en ${startPack?.shortName ?? "Ecuador"} · GPX local`
        : `Grabación libre activa · precisión aproximada ${Math.round(point.accuracy)} m · GPX local`);
    } else {
      setGpsMessage(`GPS activo · precisión aproximada ${Math.round(point.accuracy)} m · guardado local`);
    }

    if (persist) {
      saveActivityDraft(nextTrack, secondsRef.current, context.ownerId).catch(() => undefined);
      saveActiveNavigation({
        id: context.activityId,
        storageKey: "",
        ownerId: context.ownerId,
        routeId: recordingRouteId,
        routeName: recordingRouteName,
        recordingMode: recordingMode ?? "free",
        kind: recordingKind,
        state: "recording",
        startedAt: context.startedAt,
        finishedAt: null,
        elapsedSeconds: secondsRef.current,
        points: nextTrack,
        markers: activityMarkers,
        stats: navigationStats(recordingGeojson, nextTrack, secondsRef.current, routeStarted),
        sourcePlatform: currentPlatform(),
        syncStatus: "local",
        updatedAt: Date.now(),
      }).catch(() => undefined);
    }
  }, [
    activityMarkers,
    applyNativeElapsedMilliseconds,
    mergeNativeRecoveryCursor,
    recordingGeojson,
    recordingKind,
    recordingMode,
    recordingRouteId,
    recordingRouteName,
    selectedRoute.geojson,
  ]);

  const syncNativeTrackingSession = useCallback(async (
    targetActivityId: string,
    ownerId: string,
    targetStartedAt: number,
  ) => {
    if (!androidBackgroundTrackingV1Enabled) return;
    if (nativeSyncPromiseRef.current) return nativeSyncPromiseRef.current;

    const task = (async () => {
      nativeRecoveryInProgressRef.current = true;
      try {
        const snapshot: NativeTrackingSnapshot = await getNativeTrackingSnapshot(targetActivityId);
        if (snapshot.activityId && snapshot.activityId !== targetActivityId) return;
        if (typeof snapshot.elapsedMilliseconds === "number") {
          applyNativeElapsedMilliseconds(snapshot.elapsedMilliseconds, true);
        }

        const recovered = await recoverNativeLocationPoints({
          activityId: targetActivityId,
          cursor: nativeRecoveryCursorRef.current,
        });
        for (const point of recovered.points) {
          handleRecordedPoint(point, {
            ownerId,
            activityId: targetActivityId,
            startedAt: targetStartedAt,
          }, { fromRecovery: true, persist: false });
        }
        mergeNativeRecoveryCursor(recovered.cursor);

        while (nativeRecoveryBufferRef.current.length > 0) {
          const buffered = nativeRecoveryBufferRef.current.splice(0)
            .sort((a, b) => (a.nativePointId ?? 0) - (b.nativePointId ?? 0) || a.timestamp - b.timestamp);
          for (const point of buffered) {
            handleRecordedPoint(point, {
              ownerId,
              activityId: targetActivityId,
              startedAt: targetStartedAt,
            }, { fromRecovery: true, persist: false });
          }
        }

        if (snapshot.state === "paused" || snapshot.state === "recording") {
          const nativePaused = snapshot.state === "paused";
          pausedRef.current = nativePaused;
          setRecordingPaused(nativePaused);
        }
        await persistRecoveredRecording(ownerId, targetActivityId, targetStartedAt);
      } finally {
        nativeRecoveryInProgressRef.current = false;
      }
    })();

    nativeSyncPromiseRef.current = task;
    try {
      await task;
    } finally {
      if (nativeSyncPromiseRef.current === task) nativeSyncPromiseRef.current = null;
    }
  }, [
    androidBackgroundTrackingV1Enabled,
    applyNativeElapsedMilliseconds,
    handleRecordedPoint,
    mergeNativeRecoveryCursor,
    persistRecoveredRecording,
  ]);

  const recoverPendingNativeActivity = useCallback(async (intent: "recover" | "finish" | "discard" = "recover") => {
    if (!nativePendingRecovery || !currentProfile || currentProfile.status !== "approved") return;
    setNativeRecoveryBusy(true);
    try {
      const targetActivityId = nativePendingRecovery.snapshot.activityId;
      if (!targetActivityId) throw new Error("La actividad pendiente no tiene identificador.");

      const latestSnapshot = await getNativeTrackingSnapshot(targetActivityId);
      if (!latestSnapshot.active || (latestSnapshot.state !== "recording" && latestSnapshot.state !== "paused")) {
        setNativePendingRecovery(null);
        setGpsMessage("La actividad Android pendiente ya no esta activa.");
        setToast("La sesion pendiente ya fue cerrada desde Android.");
        setNativeRecoveryBusy(false);
        return;
      }

      const recovered = await recoverNativeLocationPoints({ activityId: targetActivityId });
      const localSession = nativePendingRecovery.session
        ?? await loadActiveNavigation(currentProfile.id).catch(() => null)
        ?? undefined;
      const matchingSession = localSession?.id === targetActivityId ? localSession : undefined;
      const recoveredRoute = matchingSession?.routeId
        ? availableRoutes.find((route) => route.id === matchingSession.routeId)
        : undefined;
      const recoveredMode: RecordingMode = matchingSession?.recordingMode === "selected-route" && recoveredRoute
        ? "selected-route"
        : "free";
      const recoveredPoints = filteredNativeRecoveryTrack(
        recovered.points.length ? recovered.points : nativePendingRecovery.points,
        matchingSession?.points ?? [],
      );
      const elapsedMilliseconds = latestSnapshot.elapsedMilliseconds
        ?? nativePendingRecovery.snapshot.elapsedMilliseconds
        ?? (matchingSession?.elapsedSeconds ?? 0) * 1000;
      const recoveredSeconds = Math.max(0, Math.floor(elapsedMilliseconds / 1000));
      const recoveredStartedAt = latestSnapshot.startedAt
        ?? nativePendingRecovery.snapshot.startedAt
        ?? matchingSession?.startedAt
        ?? Date.now() - recoveredSeconds * 1000;
      const nativePaused = latestSnapshot.state === "paused";

      setRecordingMode(recoveredMode);
      if (recoveredMode === "selected-route" && recoveredRoute) {
        setSelectedRoute(recoveredRoute);
        setRouteChosen(true);
        const reachedStart = hasReachedRouteStart(recoveredRoute.geojson, recoveredPoints);
        routeNavigationActiveRef.current = reachedStart;
        setRouteNavigationActive(reachedStart);
      } else {
        setRouteChosen(false);
        routeNavigationActiveRef.current = false;
        setRouteNavigationActive(false);
      }
      setTrack(recoveredPoints);
      trackRef.current = recoveredPoints;
      setDistance(calculateDistance(recoveredPoints));
      setSeconds(recoveredSeconds);
      secondsRef.current = recoveredSeconds;
      setActivityId(targetActivityId);
      setActivityStartedAt(recoveredStartedAt);
      setActivityMarkers(matchingSession?.markers ?? []);
      nativeRecoveryCursorRef.current = recovered.cursor.afterPointId || recovered.cursor.afterTimestamp
        ? recovered.cursor
        : nativePendingRecovery.cursor;
      nativeRecoveryBufferRef.current = [];
      pausedRef.current = nativePaused;

      setRecordingPaused(nativePaused);

      setRecoverableDraft(null);
      setRecoveryDetailsOpen(false);
      setNativePendingRecovery(null);
      setActiveTab("grabar");
      setGpsMessage(nativePaused
        ? "Actividad Android recuperada en pausa."
        : "Actividad Android recuperada. Grabacion activa.");
      setNativeRecoveryAttachRequest({
        activityId: targetActivityId,
        ownerId: currentProfile.id,
        startedAt: recoveredStartedAt,
        intent,
      });
    } catch (error) {
      setGpsMessage(getErrorMessage(error, "No fue posible recuperar la actividad Android pendiente."));
      setToast("La actividad sigue protegida en Android; intenta Recuperar nuevamente.");
      setNativeRecoveryBusy(false);
    }
  }, [availableRoutes, currentProfile, nativePendingRecovery]);

  useEffect(() => {
    if (!nativeRecoveryAttachRequest || !recordingMode) return;
    let cancelled = false;
    const request = nativeRecoveryAttachRequest;
    void (async () => {
      try {
        const latest = await getNativeTrackingSnapshot(request.activityId);
        if (!latest.active || (latest.state !== "recording" && latest.state !== "paused")) {
          throw new Error("La actividad Android ya no esta activa.");
        }
        await reattachLocationRecorder({
          onPoint: (rawPoint) => handleRecordedPoint(rawPoint, {
            ownerId: request.ownerId,
            activityId: request.activityId,
            startedAt: request.startedAt,
          }),
          onTrackingState: (state) => {
            if (state.activityId !== request.activityId) return;
            if (typeof state.elapsedMilliseconds === "number") applyNativeElapsedMilliseconds(state.elapsedMilliseconds, true);
            if (state.state === "paused" || state.state === "recording") {
              const isPaused = state.state === "paused";
              pausedRef.current = isPaused;
              setRecordingPaused(isPaused);
            }
          },
          onError: setGpsMessage,
        });
        await syncNativeTrackingSession(request.activityId, request.ownerId, request.startedAt);

        if (cancelled) return;

        setRecording(true);

        if (request.intent === "finish" || request.intent === "discard") {
          setFinishDialogOpen(true);
          setToast(request.intent === "discard"
            ? "Actividad recuperada. Confirma Salir sin guardar para descartarla."
            : "Actividad recuperada. Confirma Finalizar y guardar.");
        } else {
          setToast("Actividad Android recuperada. Ya puedes continuar, pausar o finalizar.");
        }
      } catch (error) {
        if (!cancelled) {
          setRecording(false);
          setGpsMessage(getErrorMessage(error, "No fue posible reconectar la actividad Android."));
          setToast("La sesion sigue en Android. Vuelve a intentar Recuperar.");
          void probeNativePendingRecovery().then(setNativePendingRecovery).catch(() => undefined);
        }
      } finally {
        if (!cancelled) {
          setNativeRecoveryAttachRequest(null);
          setNativeRecoveryBusy(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [
    applyNativeElapsedMilliseconds,
    handleRecordedPoint,
    nativeRecoveryAttachRequest,
    probeNativePendingRecovery,
    recordingMode,
    syncNativeTrackingSession,
  ]);

  useEffect(() => {
    if (!androidBackgroundTrackingV1Enabled || !recording || !activityId || !activityStartedAt || !currentProfile) return;
    const syncIfVisible = () => {
      if (document.visibilityState !== "visible") return;
      syncNativeTrackingSession(activityId, currentProfile.id, activityStartedAt).catch((error) => {
        setGpsMessage(getErrorMessage(error, "No fue posible recuperar todavía los puntos Android pendientes."));
      });
    };
    document.addEventListener("visibilitychange", syncIfVisible);
    window.addEventListener("focus", syncIfVisible);
    syncIfVisible();
    return () => {
      document.removeEventListener("visibilitychange", syncIfVisible);
      window.removeEventListener("focus", syncIfVisible);
    };
  }, [
    activityId,
    activityStartedAt,
    androidBackgroundTrackingV1Enabled,
    currentProfile,
    recording,
    syncNativeTrackingSession,
  ]);

  const startRecording = async () => {
    if (!currentProfile || currentProfile.status !== "approved") {
      setToast("Inicia sesión con una cuenta aprobada antes de grabar una ruta.");
      setActiveTab("perfil");
      return;
    }
    if (!recordingMode) {
      setToast("Elige ‘Grabar ruta nueva’ o selecciona una ruta registrada.");
      return;
    }
    const continuingRecoveredActivity = Boolean(activityId && activityStartedAt && trackRef.current.length);

    const nextActivityId = activityId ?? crypto.randomUUID();

    const nextStartedAt = activityStartedAt ?? Date.now();

    if (androidBackgroundTrackingV1Enabled && continuingRecoveredActivity) {

      const pendingNative = await probeNativePendingRecovery().catch(() => null);

      if (pendingNative?.snapshot.activityId && pendingNative.snapshot.activityId !== nextActivityId) {

        setNativePendingRecovery(pendingNative);

        setGpsMessage("THERA encontro la sesion Android original. Usa Recuperar o Finalizar para conservarla.");

        setToast("THERA no inicio otro GPS ni mezclo identificadores.");

        return;

      }

    }
    if (androidBackgroundTrackingV1Enabled) {
      nativeRecoveryCursorRef.current = continuingRecoveredActivity ? cursorFromTrack(trackRef.current) : {};
      nativeRecoveryBufferRef.current = [];
    }
    if (!continuingRecoveredActivity) {
      setTrack([]);
      trackRef.current = [];
      setSeconds(0);
      secondsRef.current = 0;
      setDistance(0);
      setActivityMarkers([]);
      routeNavigationActiveRef.current = false;
      setRouteNavigationActive(false);
      roadApproachRouteRef.current = null;
      setRoadApproachRoute(null);
      setApproachRoutingMessage("Línea directa disponible sin conexión.");
    }
    setActivityId(nextActivityId);
    setActivityStartedAt(nextStartedAt);
    await requestWakeLock();

    if (!androidBackgroundTrackingV1Enabled) {

      setRecording(true);

      setRecordingPaused(false);

      pausedRef.current = false;

    }
    setGpsMessage("Buscando una señal GPS precisa…");
    const ownerId = currentProfile.id;
    try {
      await startLocationRecorder({
        activityId: nextActivityId,
        onPoint: (rawPoint) => handleRecordedPoint(rawPoint, {
          ownerId,
          activityId: nextActivityId,
          startedAt: nextStartedAt,
        }),
        onTrackingState: (state) => {
          if (!androidBackgroundTrackingV1Enabled || state.activityId !== nextActivityId) return;
          if (typeof state.elapsedMilliseconds === "number") {
            applyNativeElapsedMilliseconds(state.elapsedMilliseconds, true);
          }
          if (state.state === "paused" || state.state === "recording") {
            const nativePaused = state.state === "paused";
            pausedRef.current = nativePaused;
            setRecordingPaused(nativePaused);
          }
        },
        onError: setGpsMessage,
      });

      if (androidBackgroundTrackingV1Enabled) {

        setRecording(true);

        setRecordingPaused(false);

        pausedRef.current = false;

        await syncNativeTrackingSession(nextActivityId, ownerId, nextStartedAt).catch((error) => {
          setGpsMessage(getErrorMessage(error, "GPS activo · la recuperación nativa se reintentará al volver a THERA."));
        });
        setNativePendingRecovery(null);
      }
    } catch (error) {
      setRecording(false);
      await wakeLock.current?.release().catch(() => undefined);
      wakeLock.current = null;
      if (!continuingRecoveredActivity) {
        setActivityId(null);
        setActivityStartedAt(null);
      }
      setGpsMessage(getErrorMessage(error, "No fue posible iniciar el GPS."));
      if (androidBackgroundTrackingV1Enabled) {
        void probeNativePendingRecovery()
          .then((pending) => {
            if (pending?.snapshot.activityId && pending.snapshot.activityId !== nextActivityId) {
              setNativePendingRecovery(pending);
              setGpsMessage("Hay una actividad Android anterior pendiente. Recupérala, finalízala o descártala antes de iniciar otra.");
            }
          })
          .catch(() => undefined);
      }
    }
  };

  const finishRecordingAndSave = async () => {
    setFinishDialogOpen(false);
    const finishingActivityId = activityId;
    const finishingStartedAt = activityStartedAt ?? Date.now() - secondsRef.current * 1000;

    if (androidBackgroundTrackingV1Enabled && finishingActivityId && currentProfile) {
      try {
        if (nativeSyncPromiseRef.current) {
          await nativeSyncPromiseRef.current.catch(() => undefined);
        }
        let finalSnapshot: NativeTrackingSnapshot;
        try {
          finalSnapshot = await finalizeNativeTracking();
        } catch (finalizeError) {
          const snapshot = await getNativeTrackingSnapshot(finishingActivityId).catch(() => ({ active: true } as NativeTrackingSnapshot));
          if (snapshot.state !== "finished") throw finalizeError;
          finalSnapshot = snapshot;
        }
        if (typeof finalSnapshot.elapsedMilliseconds === "number") {
          applyNativeElapsedMilliseconds(finalSnapshot.elapsedMilliseconds, true);
        }
        await syncNativeTrackingSession(finishingActivityId, currentProfile.id, finishingStartedAt);
        // El servicio ya esta finalizado. Esta llamada limpia listeners del bridge;
        // un segundo STOP es inocuo y mantiene compatible la API existente.
        await stopLocationRecorder().catch(() => undefined);
      } catch (error) {
        setGpsMessage(getErrorMessage(error, "No fue posible cerrar y recuperar el tracking Android."));
        setToast("THERA no guardó todavía la actividad. Intenta finalizar nuevamente.");
        return;
      }
    } else {
      await stopLocationRecorder();
    }

    setRecording(false);
    setRecordingPaused(false);
    pausedRef.current = false;
    await wakeLock.current?.release().catch(() => undefined);
    wakeLock.current = null;
    const points = trackRef.current;
    if (!currentProfile || !points.length) {
      if (currentProfile) {
        await clearActiveNavigation(currentProfile.id).catch(() => undefined);
        await clearActivityDraft(currentProfile.id).catch(() => undefined);
      }
      nativeRecoveryCursorRef.current = {};
      nativeRecoveryBufferRef.current = [];
      setActivityId(null);
      setActivityStartedAt(null);
      setGpsMessage("Registro detenido sin puntos GPS.");
      return;
    }
    const finishedStats = navigationStats(recordingGeojson, points, secondsRef.current, recordingMode === "selected-route" && routeNavigationActiveRef.current);
    const finished: NavigationSession = {
      id: activityId ?? crypto.randomUUID(),
      storageKey: "",
      ownerId: currentProfile.id,
      routeId: recordingRouteId,
      routeName: recordingRouteName,
      recordingMode: recordingMode ?? "free",
      kind: recordingKind,
      state: "finished",
      startedAt: finishingStartedAt,
      finishedAt: Date.now(),
      elapsedSeconds: secondsRef.current,
      points,
      markers: activityMarkers,
      stats: finishedStats,
      sourcePlatform: currentPlatform(),
      syncStatus: networkOnline ? "pending" : "local",
      updatedAt: Date.now(),
    };
    await saveActivityHistory(finished).catch(() => undefined);
    finalizeFieldSession(finished.id, "finished");
    await clearActiveNavigation(currentProfile.id).catch(() => undefined);
    await clearActivityDraft(currentProfile.id).catch(() => undefined);
    setActivityHistory((current) => [finished, ...current.filter((item) => item.id !== finished.id)].slice(0, 500));
    if (networkOnline) {
      syncNavigationActivity(finished)
        .then(async () => {
          await registerActivityFieldSurveyContribution(finished.id).catch(() => undefined);
          updateActivitySyncStatus(finished.id, "synced").catch(() => undefined);
          setActivityHistory((current) => current.map((item) => item.id === finished.id ? { ...item, syncStatus: "synced" } : item));
        })
        .catch(() => updateActivitySyncStatus(finished.id, "error").catch(() => undefined));
    }
    if (miMapaEnabled && isApprovedMember) {
      if (isAndroidApp) {
        setPendingPersonalMapActivityId(finished.id);
      } else {
        window.setTimeout(() => {
          const saveToPersonalMap = window.confirm("Guardar esta salida en Mi mapa?");
          if (!saveToPersonalMap) return;
          setLocalBookmark(currentProfile.id, finished.id, true)
            .then(() => setToast("Salida guardada en Mi mapa."))
            .catch(() => setToast("La actividad se guardó, pero no fue posible añadirla a Mi mapa."));
        }, 0);
      }
    }
    nativeRecoveryCursorRef.current = {};
    nativeRecoveryBufferRef.current = [];
    setActivityId(null);
    setActivityStartedAt(null);
    setNativePendingRecovery(null);
    setNativeRecoveryAttachRequest(null);
    setTrack([]);
    trackRef.current = [];
    setSeconds(0);
    secondsRef.current = 0;
    setDistance(0);
    setActivityMarkers([]);
    setRecordingMode(null);
    routeNavigationActiveRef.current = false;
    setRouteNavigationActive(false);
    roadApproachRouteRef.current = null;
    setRoadApproachRoute(null);
    setGpsMessage("Actividad finalizada y protegida en este dispositivo.");
    setToast(networkOnline ? "Actividad guardada; la sincronización continuará automáticamente." : "Actividad guardada sin conexión; se sincronizará más adelante.");
  };

  const discardCurrentRecording = async () => {
    setFinishDialogOpen(false);
    const discardedActivityId = activityId;
    await stopLocationRecorder().catch(() => undefined);
    if (androidBackgroundTrackingV1Enabled && discardedActivityId) {
      await discardNativeTrackingActivity(discardedActivityId).catch(() => undefined);
    }
    if (discardedActivityId) finalizeFieldSession(discardedActivityId, "discarded");
    await wakeLock.current?.release().catch(() => undefined);
    wakeLock.current = null;
    if (currentProfile) {
      await clearActiveNavigation(currentProfile.id).catch(() => undefined);
      await clearActivityDraft(currentProfile.id).catch(() => undefined);
    }
    setRecording(false);
    setRecordingPaused(false);
    pausedRef.current = false;
    setTrack([]);
    trackRef.current = [];
    setSeconds(0);
    secondsRef.current = 0;
    nativeRecoveryCursorRef.current = {};
    nativeRecoveryBufferRef.current = [];
    setDistance(0);
    setActivityId(null);
    setActivityStartedAt(null);
    setNativePendingRecovery(null);
    setNativeRecoveryAttachRequest(null);
    setActivityMarkers([]);
    setRecordingMode(null);
    routeNavigationActiveRef.current = false;
    setRouteNavigationActive(false);
    roadApproachRouteRef.current = null;
    setRoadApproachRoute(null);
    setGpsMessage("Grabación descartada. No se guardó ni sincronizó la ruta.");
    setToast("Saliste sin guardar; el registro temporal fue eliminado.");
  };

  const togglePause = async () => {
    const next = !recordingPaused;
    try {
      if (next) await pauseLocationRecorder();
      else await resumeLocationRecorder();
    } catch (error) {
      setGpsMessage(getErrorMessage(error, next ? "No fue posible pausar la grabación." : "No fue posible reanudar la grabación."));
      return;
    }
    setRecordingPaused(next);
    pausedRef.current = next;
    setGpsMessage(next ? "Grabación en pausa." : "Grabación reanudada.");
  };
  const recoverDraft = async () => {

    if (!recoverableDraft) return;

    if (androidBackgroundTrackingV1Enabled) {

      const pendingNative = await probeNativePendingRecovery().catch(() => null);

      if (pendingNative?.snapshot.activityId) {

        setNativePendingRecovery(pendingNative);

        setRecoveryDetailsOpen(false);

        setActiveTab("grabar");

        setGpsMessage("THERA encontro la sesion Android original. Usa Recuperar o Finalizar para conservarla.");

        setToast("La sesion Android original tiene prioridad para evitar mezclar identificadores.");

        return;

      }

    }
    setTrack(recoverableDraft.points);
    trackRef.current = recoverableDraft.points;
    setSeconds(recoverableDraft.seconds);
    secondsRef.current = recoverableDraft.seconds;
    if (androidBackgroundTrackingV1Enabled) {
      nativeRecoveryCursorRef.current = cursorFromTrack(recoverableDraft.points);
      nativeRecoveryBufferRef.current = [];
    }
    setDistance(calculateDistance(recoverableDraft.points));
    if (recoverableDraft.session) {
      const recoveredMode: RecordingMode = recoverableDraft.session.recordingMode
        ?? (recoverableDraft.session.routeId ? "selected-route" : "free");
      const recoveredRoute = availableRoutes.find((route) => route.id === recoverableDraft.session?.routeId) ?? selectedRoute;
      setActivityId(recoverableDraft.session.id);
      setActivityStartedAt(recoverableDraft.session.startedAt);
      setActivityMarkers(recoverableDraft.session.markers);
      setRecordingMode(recoveredMode);
      if (recoveredMode === "selected-route") {
        setSelectedRoute(recoveredRoute);
        setRouteChosen(true);
      } else {
        setRouteChosen(false);
      }
      const reachedStart = recoveredMode === "selected-route" && hasReachedRouteStart(recoveredRoute.geojson, recoverableDraft.points);
      routeNavigationActiveRef.current = reachedStart;
      setRouteNavigationActive(reachedStart);
    } else {
      setRecordingMode("free");
      setRouteChosen(false);
    }
    setRecoverableDraft(null);
    setRecoveryDetailsOpen(false);
    setActiveTab("grabar");
    setGpsMessage(
      "Actividad recuperada. Pulsa Iniciar para continuar con el mismo registro.",
    );
  };
  const discardDraft = async () => {
    // IMPORTANTE: descartar el aviso de recuperación NUNCA debe reiniciar ni
    // detener una actividad que ya esté viva en memoria. Antes se limpiaban
    // track, cronómetro, modo GPS y navegación; eso hacía perder la actividad
    // actual al tocar "Descartar". Ahora solo eliminamos el borrador persistido
    // cuando no existe una sesión viva, o simplemente ocultamos el aviso si ya
    // hay una actividad en curso.
    const liveActivityInProgress = Boolean(
      recording || activityId || recordingMode || trackRef.current.length,
    );
    if (currentProfile && !liveActivityInProgress) {
      await clearActivityDraft(currentProfile.id).catch(() => undefined);
      await clearActiveNavigation(currentProfile.id).catch(() => undefined);
    }
    setRecoverableDraft(null);
    setRecoveryDetailsOpen(false);
    setToast(
      liveActivityInProgress
        ? "Aviso cerrado. Tu actividad actual continúa normalmente."
        : "Actividad anterior descartada. THERA continúa listo para una nueva actividad.",
    );
  };

  const convertPhotos = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []).slice(0, 6);
    if (!files.length) return;
    setPhotoMessage("Optimizando fotografías en tu dispositivo…");
    const converted: PhotoResult[] = [];
    for (const file of files) {
      try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, 1920 / Math.max(bitmap.width, bitmap.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        canvas
          .getContext("2d")
          ?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, "image/webp", 0.8),
        );
        bitmap.close();
        if (!blob) continue;
        converted.push({
          id: crypto.randomUUID(),
          url: URL.createObjectURL(blob),
          name: file.name.replace(/\.[^.]+$/, ".webp"),
          before: `${(file.size / 1024 / 1024).toFixed(1)} MB`,
          after: `${Math.max(1, Math.round(blob.size / 1024))} KB`,
          blob,
          width: canvas.width,
          height: canvas.height,
          altText: "Fotografía de ruta compartida en THERA",
        });
      } catch {
        setPhotoMessage(
          "Una fotografía no pudo convertirse. Prueba con JPG, PNG o WebP.",
        );
      }
    }
    setPhotoResults((current) => {
      current.forEach((photo) => URL.revokeObjectURL(photo.url));
      return converted;
    });
    if (converted.length)
      setPhotoMessage(
        networkOnline
          ? "Listas para enviar a la ruta seleccionada."
          : "Guardadas en la cola del dispositivo hasta recuperar internet.",
      );
  };

  const convertPanoramas = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!currentProfile || currentProfile.status !== "approved") {
      setToast("Solo un autor con cuenta aprobada puede crear una ruta 360.");
      return;
    }
    const remainingSlots = Math.max(0, 8 - panoramaResults.length);
    const files = Array.from(event.target.files ?? []).slice(0, remainingSlots);
    event.target.value = "";
    if (!files.length) {
      setToast(remainingSlots ? "Selecciona una imagen JPG, PNG o WebP." : "La beta admite hasta ocho paradas 360 por ruta.");
      return;
    }
    const routeDistanceKm = calculateDistance(importedTrack);
    const converted: PanoramaResult[] = [];
    for (const file of files) {
      try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, 4096 / bitmap.width, 2048 / bitmap.height);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(bitmap.width * scale));
        canvas.height = Math.max(1, Math.round(bitmap.height * scale));
        canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", 0.86));
        if (!blob) continue;
        const panoramaIndex = panoramaResults.length + converted.length;
        const ratio = canvas.width / canvas.height;
        converted.push({
          id: crypto.randomUUID(),
          name: file.name.replace(/\.[^.]+$/, ".webp"),
          blob,
          url: URL.createObjectURL(blob),
          width: canvas.width,
          height: canvas.height,
          stopName: `Parada ${panoramaIndex + 1}`,
          distanceKm: routeDistanceKm > 0
            ? Number((routeDistanceKm * (panoramaIndex + 1) / (panoramaResults.length + files.length + 1)).toFixed(2))
            : 0,
          coverage: Math.abs(ratio - 2) <= 0.18 ? "spherical" : "cylindrical",
        });
      } catch {
        setToast("Una panorámica no pudo procesarse. Prueba con JPG, PNG o WebP.");
      }
    }
    if (!converted.length) return;
    setPanoramaResults((current) => [...current, ...converted]);
    setSelectedPanoramaId((current) => current ?? converted[0].id);
    setToast(`${converted.length} panorámica${converted.length === 1 ? "" : "s"} guardada${converted.length === 1 ? "" : "s"} en el borrador privado.`);
  };

  const updatePanorama = (id: string, update: Partial<Pick<PanoramaResult, "stopName" | "distanceKm">>) => {
    setPanoramaResults((current) => current.map((panorama) => panorama.id === id ? { ...panorama, ...update } : panorama));
  };

  const removePanorama = (id: string) => {
    setPanoramaResults((current) => {
      const removed = current.find((panorama) => panorama.id === id);
      if (removed) URL.revokeObjectURL(removed.url);
      const next = current.filter((panorama) => panorama.id !== id);
      setSelectedPanoramaId((selected) => selected === id ? next[0]?.id ?? null : selected);
      return next;
    });
  };

  const importGpx = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setImportMessage("El GPX supera el límite de 50 MB.");
      return;
    }
    const xml = new DOMParser().parseFromString(
      await file.text(),
      "application/xml",
    );
    if (xml.querySelector("parsererror")) {
      setImportMessage("El archivo no es un GPX válido.");
      return;
    }
    const points = Array.from(xml.querySelectorAll("trkpt, rtept"))
      .map((node) => ({
        latitude: Number(node.getAttribute("lat")),
        longitude: Number(node.getAttribute("lon")),
        elevation: node.querySelector("ele")
          ? Number(node.querySelector("ele")?.textContent)
          : null,
      }))
      .filter(
        (point) =>
          Number.isFinite(point.latitude) && Number.isFinite(point.longitude),
      );
    if (points.length < 2) {
      setImportMessage("El GPX necesita al menos dos puntos de ruta.");
      return;
    }
    const waypoints = Array.from(xml.querySelectorAll("wpt"))
      .map((node, index): RouteWaypoint | null => {
        const latitude = Number(node.getAttribute("lat"));
        const longitude = Number(node.getAttribute("lon"));
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
        const label = node.querySelector("type")?.textContent?.trim().toLowerCase() ?? "";
        const kind: RouteWaypoint["kind"] = label.includes("inicio") || label.includes("start")
          ? "start"
          : label.includes("final") || label.includes("finish")
            ? "finish"
            : label.includes("agua") || label.includes("water")
              ? "water"
              : label.includes("mirador") || label.includes("view")
                ? "viewpoint"
                : label.includes("camp")
                  ? "camp"
                  : label.includes("refug") || label.includes("shelter")
                    ? "shelter"
                    : label.includes("pelig") || label.includes("danger")
                      ? "danger"
                      : "waypoint";
        return {
          id: `gpx-waypoint-${index}-${crypto.randomUUID()}`,
          name: node.querySelector("name")?.textContent?.trim() || `Punto ${index + 1}`,
          description: node.querySelector("desc, cmt")?.textContent?.trim() || undefined,
          kind,
          latitude,
          longitude,
          elevation: node.querySelector("ele") ? Number(node.querySelector("ele")?.textContent) : null,
        };
      })
      .filter((point): point is RouteWaypoint => point !== null);
    setImportedTrack(points);
    setImportedWaypoints(waypoints);
    setImportedRouteFile({
      name: file.name,
      blob: file,
      mimeType: "application/gpx+xml",
    });
    const suggestedName = xml.querySelector("trk > name, rte > name")?.textContent?.trim();
    setRouteForm((current) => ({
      ...current,
      name:
        current.name ||
        suggestedName ||
        file.name.replace(/\.gpx$/i, "").replace(/[-_]+/g, " "),
    }));
    setImportMessage(
      `${points.length} puntos válidos · ${waypoints.length} lugares señalados · ${formatDistance(calculateDistance(points))} km · +${calculateAscent(points)} m · archivo protegido como borrador.`,
    );
  };

  const exportImported = (format: "gpx" | "geojson") => {
    if (!importedTrack.length) {
      setToast("Primero importa un archivo GPX.");
      return;
    }
    if (format === "geojson") {
      const content = JSON.stringify(routeGeoJson(importedTrack, importedWaypoints), null, 2);
      downloadBlob(
        new Blob([content], { type: "application/geo+json" }),
        "thera-ruta.geojson",
      );
      return;
    }
    const points = importedTrack
      .map(
        (point) =>
          `<trkpt lat="${point.latitude}" lon="${point.longitude}">${point.elevation === null ? "" : `<ele>${point.elevation}</ele>`}</trkpt>`,
      )
      .join("");
    const waypoints = importedWaypoints.map((point) =>
      `<wpt lat="${point.latitude}" lon="${point.longitude}">${point.elevation === null ? "" : `<ele>${point.elevation}</ele>`}<name>${xmlText(point.name)}</name><type>${point.kind}</type>${point.description ? `<desc>${xmlText(point.description)}</desc>` : ""}</wpt>`,
    ).join("");
    downloadBlob(
      new Blob(
        [
          `<?xml version="1.0" encoding="UTF-8"?><gpx version="1.1" creator="THERA" xmlns="http://www.topografix.com/GPX/1/1">${waypoints}<trk><name>Ruta THERA</name><trkseg>${points}</trkseg></trk></gpx>`,
        ],
        { type: "application/gpx+xml" },
      ),
      "thera-ruta.gpx",
    );
  };

  const refreshRouteCollections = async () => {
    const [remote, published] = await Promise.all([
      listOwnRoutes(),
      listPublishedRoutes(),
    ]);
    setOwnRoutes(remote);
    setPublishedRoutes(published);
    if (currentProfile) {
      setLocalRouteDrafts(await listLocalRouteDrafts(currentProfile.id));
    }
  };

  const routeDraftInput = () => {
    if (!currentProfile || currentProfile.status !== "approved") {
      throw new Error("Inicia sesión con una cuenta aprobada para guardar rutas.");
    }
    if (routeForm.name.trim().length < 3) {
      throw new Error("Escribe un nombre de al menos 3 caracteres.");
    }
    if (routeForm.area.trim().length < 2) {
      throw new Error("Indica el parque, sector o zona de la ruta.");
    }
    if (importedTrack.length < 2) {
      throw new Error("Añade un GPX o prepara una grabación con puntos GPS.");
    }
    return {
      id: routeDraftId,
      ownerId: currentProfile.id,
      name: routeForm.name.trim(),
      area: routeForm.area.trim(),
      kind: routeForm.kind,
      description: routeForm.description.trim(),
      recommendations: routeForm.recommendations.trim(),
      distanceM: Math.round(calculateDistance(importedTrack) * 1000),
      ascentM: calculateAscent(importedTrack),
      estimatedSeconds: Math.max(
        0,
        Math.round((Number(routeForm.estimatedHours.replace(",", ".")) || 0) * 3600),
      ),
      pathGeojson: routeGeoJson(importedTrack, importedWaypoints),
      gpxFile: importedRouteFile,
      photos: photoResults.map((photo) => ({
        id: photo.id,
        name: photo.name,
        blob: photo.blob,
        before: photo.before,
        after: photo.after,
        width: photo.width,
        height: photo.height,
        altText: photo.altText,
      })),
      panoramas: panoramaResults.map((panorama) => ({
        id: panorama.id,
        name: panorama.name,
        blob: panorama.blob,
        width: panorama.width,
        height: panorama.height,
        stopName: panorama.stopName,
        distanceKm: panorama.distanceKm,
        coverage: panorama.coverage,
      })),
      sourcePlatform: currentPlatform(),
    } as const;
  };

  const resetRouteComposer = () => {
    photoResults.forEach((photo) => URL.revokeObjectURL(photo.url));
    panoramaResults.forEach((panorama) => URL.revokeObjectURL(panorama.url));
    setPhotoResults([]);
    setPanoramaResults([]);
    setSelectedPanoramaId(null);
    setRouteForm(emptyRouteForm);
    setRouteDraftId(crypto.randomUUID());
    setImportedRouteFile(null);
    setImportedTrack([]);
    setImportedWaypoints([]);
    setImportMessage("Importa un GPX o prepara una grabación para crear una ruta.");
    setPhotoMessage("Elige hasta seis fotografías");
  };

  const saveRouteWorkflow = async (sendForReview: boolean) => {
    setRouteBusy(true);
    try {
      if (sendForReview && panoramaResults.length) {
        throw new Error("La beta 360 permanece privada. Guarda la prueba 360; todavía no puede enviarse a publicación.");
      }
      const draft = routeDraftInput();
      await saveLocalRouteDraft(draft);
      setLocalRouteDrafts(await listLocalRouteDrafts(draft.ownerId));

      if (!networkOnline) {
        if (sendForReview) {
          throw new Error(
            "La ruta quedó guardada en este dispositivo. Conéctate para enviarla a revisión.",
          );
        }
        setToast("Borrador guardado localmente; se podrá sincronizar al recuperar internet.");
        return;
      }

      const routeId = await syncRouteDraft(draft);
      if (sendForReview) {
        await submitRoute(routeId);
        await deleteLocalRouteDraft(draft.ownerId, draft.id);
        await clearActivityDraft(draft.ownerId).catch(() => undefined);
        setTrack([]);
        trackRef.current = [];
        setSeconds(0);
        setDistance(0);
        resetRouteComposer();
        setToast("Ruta, GPX y fotografías enviados a revisión con tu misma cuenta.");
      } else {
        setToast(panoramaResults.length
          ? "Ruta sincronizada; las panorámicas 360 permanecen privadas en este dispositivo y ligadas a tu autoría."
          : "Borrador sincronizado con tu cuenta THERA.");
      }
      await refreshRouteCollections();
    } catch (error) {
      setToast(getErrorMessage(error, "No fue posible guardar la ruta."));
    } finally {
      setRouteBusy(false);
    }
  };

  const restoreRouteDraft = (draft: LocalRouteDraft) => {
    photoResults.forEach((photo) => URL.revokeObjectURL(photo.url));
    panoramaResults.forEach((panorama) => URL.revokeObjectURL(panorama.url));
    setRouteDraftId(draft.id);
    setRouteForm({
      name: draft.name,
      area: draft.area,
      kind: draft.kind,
      description: draft.description,
      recommendations: draft.recommendations,
      estimatedHours: draft.estimatedSeconds
        ? (draft.estimatedSeconds / 3600).toFixed(1).replace(".", ",")
        : "",
    });
    const points = pointsFromGeoJson(draft.pathGeojson);
    setImportedTrack(points);
    setImportedWaypoints(routeWaypoints(draft.pathGeojson).filter((point) => !["start", "finish"].includes(point.kind)));
    setImportedRouteFile(draft.gpxFile);
    setPhotoResults(
      draft.photos.map((photo) => ({
        ...photo,
        url: URL.createObjectURL(photo.blob),
      })),
    );
    const restoredPanoramas = (draft.panoramas ?? []).map((panorama) => ({
      ...panorama,
      url: URL.createObjectURL(panorama.blob),
    }));
    setPanoramaResults(restoredPanoramas);
    setSelectedPanoramaId(restoredPanoramas[0]?.id ?? null);
    setImportMessage(
      `${points.length} puntos recuperados · borrador disponible para sincronizar.`,
    );
    setPhotoMessage(
      draft.photos.length
        ? `${draft.photos.length} fotografías recuperadas.`
        : "Puedes añadir hasta seis fotografías.",
    );
    setToast("Borrador local recuperado correctamente.");
  };

  const prepareRecordedRoute = () => {
    if (track.length < 2) {
      setToast("La grabación necesita al menos dos puntos GPS.");
      return;
    }
    const name = recordingMode === "selected-route"
      ? `${selectedRoute.name} · grabación`
      : `Ruta GPS ${new Date().toLocaleDateString("es-EC")}`;
    const points = track.map((point) => ({
      latitude: point.latitude,
      longitude: point.longitude,
      elevation: point.altitude,
    }));
    setRouteDraftId(crypto.randomUUID());
    setRouteForm({
      ...emptyRouteForm,
      name,
      kind: "senderismo",
      estimatedHours: (seconds / 3600).toFixed(1).replace(".", ","),
    });
    setImportedTrack(points);
    setImportedWaypoints(activityMarkers);
    setImportedRouteFile({
      name: "thera-recorrido.gpx",
      blob: trackToGpx(track, name, activityMarkers),
      mimeType: "application/gpx+xml",
    });
    setImportMessage(
      `${track.length} puntos de la grabación preparados · completa la zona y los detalles.`,
    );
    setActiveTab("planificar");
  };

  const decideRoute = async (
    route: PendingRoute,
    decision: "published" | "rejected",
  ) => {
    setRouteAdminActionId(route.id);
    try {
      await reviewRoute(route.id, decision);
      setPendingRoutes((current) => current.filter((item) => item.id !== route.id));
      setPublishedRoutes(await listPublishedRoutes());
      setToast(
        decision === "published"
          ? `${route.name} fue publicada y ya se comparte con web y Android.`
          : `${route.name} volvió al autor para correcciones.`,
      );
    } catch (error) {
      setToast(getErrorMessage(error, "No fue posible revisar la ruta."));
    } finally {
      setRouteAdminActionId(null);
    }
  };

  const shareRoute = async () => {
    const data = {
      title: `${selectedRoute.name} · THERA`,
      text: `${selectedRoute.distance} · ${selectedRoute.time} · ${selectedRoute.ascent}`,
      url: window.location.href,
    };
    try {
      if (navigator.share) await navigator.share(data);
      else {
        await navigator.clipboard.writeText(
          `${data.title}\n${data.text}\n${data.url}`,
        );
        setToast("Enlace y datos copiados para compartir.");
      }
    } catch {
      setToast("La acción de compartir se canceló.");
    }
  };

  const addActivityMarker = (kind: ActivityMarker["kind"], name: string) => {
    const point = trackRef.current.at(-1);
    if (!point) {
      setToast("Espera a que el GPS registre una posición antes de marcar un lugar.");
      return;
    }
    const marker: ActivityMarker = {
      id: crypto.randomUUID(),
      name,
      kind,
      latitude: point.latitude,
      longitude: point.longitude,
      elevation: point.altitude,
      timestamp: point.timestamp,
    };
    setActivityMarkers((current) => [...current, marker]);
    setToast(`${name} guardado en el recorrido.`);
  };

  const exportRecordedActivity = (format: "gpx" | "geojson") => {
    if (track.length < 2) {
      setToast("La actividad necesita al menos dos puntos GPS para exportarse.");
      return;
    }
    if (format === "gpx") {
      downloadBlob(trackToGpx(track, recordingRouteName, activityMarkers), "thera-actividad.gpx");
      return;
    }
    downloadBlob(
      new Blob([JSON.stringify(activityGeoJson(track, activityMarkers), null, 2)], { type: "application/geo+json" }),
      "thera-actividad.geojson",
    );
  };

  const selectSharePhoto = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (sharePhoto) URL.revokeObjectURL(sharePhoto);
    setSharePhoto(URL.createObjectURL(file));
    setShareBackground("photo");
  };

  const shareActivityCard = async () => {
    if (!track.length) {
      setToast("Finaliza una actividad con puntos GPS antes de compartir.");
      return;
    }
    try {
      const backgroundImage = shareBackground === "map"
        ? mapSnapshotProvider.current?.() ?? null
        : shareBackground === "photo"
          ? sharePhoto
          : null;
      const shareTrack = hideShareEndpoints ? trimTrackEndpoints(track) : track;
      const blob = await createActivityShareCard({
        routeName: recordingRouteName,
        track: shareTrack,
        stats: liveStats,
        format: shareFormat,
        background: shareBackground,
        backgroundImage,
      });
      const file = new File([blob], `thera-actividad-${shareFormat}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] }))
        await navigator.share({
          files: [file],
          title: "Mi actividad en THERA",
        });
      else downloadBlob(blob, file.name);
    } catch (error) {
      setToast(getErrorMessage(error, "No fue posible crear la imagen de la actividad."));
    }
  };

  const requestInstall = async () => {
    if (!installPrompt) {
      setToast(
        "Usa el menú del navegador y elige ‘Instalar aplicación’ o ‘Añadir a pantalla de inicio’.",
      );
      return;
    }
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };
  const submitAccess = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    if (!isSupabaseConfigured) {
      setToast("Completa las variables de Supabase para activar el registro real.");
      return;
    }
    const legalAccepted = form.get("legalAccepted") === "on";
    const contributionAuthorized = form.get("contributionAuthorized") === "on";
    setAuthBusy(true);
    const { error } = await requestAccessWithConsent({
      firstNames: String(form.get("firstNames") ?? "").trim(),
      lastNames: String(form.get("lastNames") ?? "").trim(),
      age: Number(form.get("age")),
      country: String(form.get("country") ?? "").trim(),
      city: String(form.get("city") ?? "").trim(),
      email: String(form.get("email") ?? "").trim().toLowerCase(),
      password: String(form.get("password") ?? ""),
      termsAccepted: legalAccepted,
      privacyAccepted: legalAccepted,
      contributionAuthorized,
    });
    setAuthBusy(false);
    if (error) {
      setToast(error.message);
      return;
    }
    setAccessSent(true);
  };
  const submitLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setAuthBusy(true);
    const { error } = await signIn(String(form.get("email") ?? "").trim(), String(form.get("password") ?? ""));
    setAuthBusy(false);
    setToast(error ? error.message : "Sesión iniciada. THERA comprobará ahora tu aprobación.");
  };
  const submitPasswordResetRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = authEmail.trim().toLowerCase();
    if (!email) return;
    setAuthBusy(true);
    const { error } = await requestPasswordReset(email);
    setAuthBusy(false);
    if (error) {
      setToast(error.message);
      return;
    }
    setToast("Si el correo está registrado, recibirás un enlace seguro para cambiar la contraseña.");
    setPasswordFlow("login");
  };
  const submitPasswordUpdate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    const confirmation = String(form.get("passwordConfirmation") ?? "");
    if (password !== confirmation) {
      setToast("Las contraseñas no coinciden.");
      return;
    }
    setAuthBusy(true);
    const { error } = await updatePassword(password);
    setAuthBusy(false);
    if (error) {
      setToast(error.message);
      return;
    }
    const address = new URL(window.location.href);
    address.searchParams.delete("password-recovery");
    window.history.replaceState({}, "", address);
    await signOut();
    clearCachedProfile();
    clearRememberedUserId();
    setCurrentProfile(null);
    setSignedInEmail(null);
    setPasswordFlow("login");
    setShowPassword(false);
    setToast("Contraseña actualizada. Ya puedes iniciar sesión con la nueva clave.");
  };
  const closeSession = async () => {
    await signOut();
    clearCachedProfile();
    clearRememberedUserId();
    setCurrentProfile(null);
    setSignedInEmail(null);
    setToast("Sesión cerrada de forma segura.");
  };
  const approveMember = async (member: PendingMember) => {
    setAdminActionId(member.id);
    try {
      await reviewMember(member.id, "approved");
      setPendingMembers((current) => current.filter((item) => item.id !== member.id));
      setToast(`${member.first_names} fue aprobado correctamente.`);
    } catch (error) {
      setToast(getErrorMessage(error, "No fue posible aprobar la solicitud."));
    } finally {
      setAdminActionId(null);
    }
  };

  const memberOnlyTab = (tab: TabName) => !["explorar", "ai", "ar", "360", "perfil", "terrain-sense"].includes(tab);
  const openMemberAccess = (message = "Esta función requiere una cuenta aprobada.") => {
    setAuthMode(signedInEmail ? "login" : "register");
    setActiveTab("perfil");
    setToast(message);
  };
  const navigateToTab = (tab: TabName) => {
    if (tab === "mi-mapa" && !miMapaEnabled) {
      setActiveTab("explorar");
      return;
    }
    if (!isApprovedMember && memberOnlyTab(tab)) {
      openMemberAccess();
      return;
    }
    setActiveTab(tab);
  };

  const navItems: Array<{
    value: TabName;
    label: string;
    icon: typeof Compass;
  }> = [
    { value: "explorar", label: "Explorar", icon: Compass },
    { value: "planificar", label: "Planificar", icon: Route },
    { value: "grabar", label: "Grabar", icon: Activity },
    { value: "escalada", label: "Escalada y rápel", icon: Mountain },
    { value: "comunidad", label: "Comunidad", icon: Users },
    { value: "offline", label: "Mapas offline", icon: CloudDownload },
    ...(miMapaEnabled && isApprovedMember
      ? [{ value: "mi-mapa" as TabName, label: "Mi mapa", icon: Route }]
      : []),
    { value: "ar", label: "THERA AR Explore", icon: Camera },
    { value: "360", label: "THERA 360 Routes", icon: MoveHorizontal },
  ];
  // Android: Explorar y Grabar son una sola entrada visible.
  // Internamente conservamos la pestaña "grabar" para no reescribir ni arriesgar
  // el motor GPS/navegación ya aprobado; el usuario siempre ve un único botón
  // "Explorar" que abre el mapa normal o la sesión GPS activa según corresponda.
  // La web mantiene Explorar y Grabar separados exactamente como antes.
  const androidExploreTarget: TabName = recording || activityId || track.length
    ? "grabar"
    : "explorar";
  const mobileItems: Array<{
    value: TabName;
    label: string;
    icon: typeof Compass;
  }> = isAndroidUi
    ? [
        { value: androidExploreTarget, label: "Explorar", icon: Compass },
        { value: "comunidad", label: "Comunidad", icon: Users },
        { value: "ar", label: "AR", icon: Camera },
        { value: "360", label: "360", icon: MoveHorizontal },
        { value: "perfil", label: "Perfil", icon: CircleUserRound },
      ]
    : [
        { value: "explorar", label: "Explorar", icon: Compass },
        { value: "comunidad", label: "Comunidad", icon: Users },
        { value: "ar", label: "AR", icon: Camera },
        { value: "360", label: "360", icon: MoveHorizontal },
        {
  value: isPwaUi && miMapaEnabled && isApprovedMember ? "mi-mapa" : "perfil",
  label: isPwaUi && miMapaEnabled && isApprovedMember ? "Mi mapa" : "Perfil",
  icon: isPwaUi && miMapaEnabled && isApprovedMember ? Route : CircleUserRound,
},
      ];

  // Android Contextual Single Dock V1: only one navigation row is visible at a time.
  // Explore tools replace the primary dock while open; AR owns its own contextual dock.
  // This flag changes presentation only and never owns map, GPS, route or recording state.
  const androidPrimaryDockHidden = isAndroidUi && (androidExplorePanelOpen || activeTab === "ar" || activeTab === "terrain-sense");

  const renderCompactPlanner = (
    context: "explore" | "record",
    options?: { open?: boolean; hideSummary?: boolean },
  ) => (
    <details className={`planner-quick-panel planner-quick-${context}${options?.hideSummary ? " planner-quick-inline" : ""}`} open={options?.open}>
      {!options?.hideSummary && <summary>
        <span><Route /><strong>Planificar por puntos</strong></span>
        <span className="planner-quick-meta">{plannerPoints.length ? `${formatDistance(plannerDistanceKm)} km` : "Opcional"}<ChevronDown /></span>
      </summary>}
      <div className="planner-quick-body">
        <div className="planner-routing-buttons" aria-label="Modo de planificación">
          <button type="button" className={plannerTravelMode === "foot-walking" ? "selected" : ""} onClick={() => setPlannerTravelMode("foot-walking")}><Footprints /> A pie</button>
          <button type="button" className={plannerTravelMode === "driving-car" ? "selected" : ""} onClick={() => setPlannerTravelMode("driving-car")}><Car /> Vehículo</button>
        </div>
        <div className="planner-point-toolbar compact">
          <button type="button" className={plannerTool === "start" ? "selected" : ""} onClick={() => setPlannerTool("start")}><MapPin /> Inicio</button>
          <button type="button" className={plannerTool === "waypoint" ? "selected" : ""} disabled={!plannerPoints.some((point) => point.kind === "start")} onClick={() => setPlannerTool("waypoint")}><Route /> Punto</button>
          <button type="button" className={plannerTool === "finish" ? "selected" : ""} disabled={!plannerPoints.some((point) => point.kind === "start")} onClick={() => setPlannerTool("finish")}><MapPinned /> Destino</button>
          <button type="button" disabled={!plannerPoints.length} onClick={clearPlannerPoints}><Trash2 /> Limpiar</button>
        </div>
        <div className="planner-quick-status" role="status">
          <span><strong>{plannerTool ? `Toca el mapa para marcar ${plannerTool === "start" ? "el inicio" : plannerTool === "finish" ? "el destino" : "un punto"}.` : "Planificación por puntos"}</strong><small>{plannerRoutingBusy ? "Calculando por caminos…" : plannerRoutingMessage}</small></span>
          <b>{formatDistance(plannerDistanceKm)} km</b>
        </div>
        {plannerPoints.length > 0 && <ol className="planner-quick-list">{plannerPoints.map((point, index) => <li key={`${context}-${point.id}`}><span><strong>{plannerPointLabel(point, index)}</strong><small>{point.latitude.toFixed(5)}, {point.longitude.toFixed(5)}</small></span><button type="button" onClick={() => removePlannerPoint(point.id)} aria-label={`Eliminar ${plannerPointLabel(point, index)}`}><X /></button></li>)}</ol>}
        <button className="planner-start-route" type="button" disabled={!plannerNavigationRoute || plannerRoutingBusy} onClick={() => plannerNavigationRoute && prepareRecordingMode("selected-route", plannerNavigationRoute)}><Play /> Iniciar ruta planificada</button>
        <small className="planner-quick-help">El mapa, GPS, relieve y curvas no cambian: esta herramienta solo añade puntos y un trazado de planificación sobre el mapa.</small>
      </div>
    </details>
  );

  return (
    <main
      className={`app-shell${isAndroidUi ? " android-app-shell" : ""}${isPwaUi ? " pwa-app-shell" : ""}${isWebUi ? " web-app-shell" : ""}${isAndroidUi && activeTab === "ar" ? " android-ar-immersive" : ""}`}
      data-thera-ui={uiPlatform}
    >
      <header className={`topbar platform-topbar ${isAndroidUi ? "android-topbar" : isPwaUi ? "pwa-topbar" : "web-topbar"}`}>
        <button
          className="brand"
          onClick={() => setActiveTab("explorar")}
          aria-label="Ir al inicio de THERA"
        >
          <img src="/brand/site/thera-symbol-transparent.png" alt="" />
          <span className="brand-word">
            TH<span>E</span>RA<small>RUTAS Y AVENTURA</small>
          </span>
        </button>
        <nav className="desktop-nav" aria-label="Navegación principal">
          {navItems.map((item) => (
            <button
              key={item.value}
              className={`${activeTab === item.value ? "active" : ""}${!isApprovedMember && memberOnlyTab(item.value) ? " locked" : ""}`}
              onClick={() => navigateToTab(item.value)}
              aria-label={!isApprovedMember && memberOnlyTab(item.value) ? `${item.label}, requiere acceso` : item.label}
            >
              {item.label}
              {!isApprovedMember && memberOnlyTab(item.value) && <LockKeyhole className="nav-lock" />}
            </button>
          ))}
        </nav>
        <div className="header-actions">
          <span
            className={`network-pill ${networkOnline ? "online" : "offline"}`}
          >
            {networkOnline ? <Signal /> : <WifiOff />}
            {networkOnline ? "En línea" : "Sin conexión"}
          </span>
          <button
            className="access-button"
            onClick={() => setActiveTab("perfil")}
          >
            <UserRoundPlus /> Solicitar acceso
          </button>
          <button
            type="button"
            className="thera-ai-header-button"
            onClick={() => setActiveTab("ai")}
            aria-label="Abrir THERA Mountain AI"
            title="THERA Mountain AI"
          >
            <Sparkles />
          </button>
          <button
            className="avatar"
            onClick={() => setActiveTab("perfil")}
            aria-label="Abrir perfil"
          >
            AR
          </button>
          <button
            className={`menu-button${mobileHeaderMenuOpen ? " active" : ""}`}
            onClick={() => {
              if (isAndroidUi) {
                setMobileHeaderMenuOpen((open) => !open);
              } else {
                setActiveTab("perfil");
              }
            }}
            aria-label={mobileHeaderMenuOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={isAndroidUi ? mobileHeaderMenuOpen : undefined}
          >
            <Menu />
          </button>
        </div>
      </header>

      {isAndroidUi && (
        <div
          className={`mobile-header-menu${mobileHeaderMenuOpen ? " open" : ""}`}
          role="menu"
          aria-label="Menú móvil de THERA"
          aria-hidden={!mobileHeaderMenuOpen}
        >
          {miMapaEnabled && isApprovedMember && (
            <button
              type="button"
              role="menuitem"
              tabIndex={mobileHeaderMenuOpen ? 0 : -1}
              className={activeTab === "mi-mapa" ? "active" : ""}
              onClick={() => {
                setMobileHeaderMenuOpen(false);
                navigateToTab("mi-mapa");
              }}
            >
              <Route />
              <span>
                <strong>Mi mapa</strong>
                <small>Mis recorridos guardados y progreso personal</small>
              </span>
              <ChevronRight />
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            tabIndex={mobileHeaderMenuOpen ? 0 : -1}
            className={activeTab === "offline" ? "active" : ""}
            onClick={() => {
              setMobileHeaderMenuOpen(false);
              navigateToTab("offline");
            }}
          >
            <CloudDownload />
            <span>
              <strong>Mapas offline</strong>
              <small>Descargar, elegir y administrar mapas sin conexión</small>
            </span>
            <ChevronRight />
          </button>
        </div>
      )}

      {!isAndroidUi && (
        <div className={`build-notice platform-build-notice ${isPwaUi ? "pwa-build-notice" : "web-build-notice"}`}>
          <Info />
          <span>
            {isApprovedMember ? <><strong>Base compartida web + Android.</strong> Usuarios, borradores,
            GPX, fotografías y revisión de rutas utilizan la misma cuenta.</> : <><strong>Modo visitante.</strong> Puedes explorar el mapa, buscar lugares y cambiar sus capas. Grabar, crear y descargar requiere una cuenta aprobada.</>}
          </span>
          {isAdmin && !isAndroidUi && (
            <button onClick={() => setActiveTab("admin")}>Administración</button>
          )}
        </div>
      )}
      {recoverableDraft && !nativePendingRecovery && (
        <div className={`recovery-banner${recoveryDetailsOpen ? " recovery-banner-open" : ""}`}>
          <ShieldCheck className="recovery-icon" />
          <button
            type="button"
            className="recovery-summary"
            onClick={() => setRecoveryDetailsOpen((open) => !open)}
            aria-expanded={recoveryDetailsOpen}
          >
            <strong>Encontramos una actividad sin finalizar.</strong>
            <span>
              {recoveryDetailsOpen ? "Ocultar datos de la actividad" : "Toca aquí para revisar sus datos antes de decidir."}
            </span>
          </button>
          <div className="recovery-actions">
            <button type="button" onClick={recoverDraft}>Recuperar</button>
            <button type="button" className="ghost" onClick={discardDraft}>No recuperar</button>
          </div>
          {recoveryDetailsOpen && (
            <div className="recovery-details" aria-label="Datos de la actividad recuperable">
              <div className="recovery-detail-item"><small>Actividad</small><strong>
                {recoverableDraft.session?.recordingMode === "selected-route"
                  ? availableRoutes.find((route) => route.id === recoverableDraft.session?.routeId)?.name ?? "Ruta guiada"
                  : "Grabación libre"}
              </strong></div>
              <div className="recovery-detail-item"><small>Tiempo registrado</small><strong>{formatDuration(recoverableDraft.seconds)}</strong></div>
              <div className="recovery-detail-item"><small>Distancia registrada</small><strong>{calculateDistance(recoverableDraft.points).toFixed(2)} km</strong></div>
              <div className="recovery-detail-item"><small>Puntos GPS</small><strong>{recoverableDraft.points.length}</strong></div>
            </div>
          )}
        </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(value) => navigateToTab(value as TabName)}
        className="app-tabs"
      >
        <TabsList className={`mobile-nav platform-primary-nav${isAndroidUi ? " android-mobile-nav android-primary-nav" : isPwaUi ? " pwa-primary-nav" : " web-primary-nav"}${(androidPrimaryDockHidden || finishDialogOpen || Boolean(pendingPersonalMapActivityId)) ? " android-primary-dock-hidden" : ""}`} aria-label="Secciones de THERA">
          {mobileItems.map((item) => {
            const Icon = item.icon;
            return (
              <TabsTrigger
                key={item.value}
                value={item.value}
                className={`${!isApprovedMember && memberOnlyTab(item.value) ? "locked" : ""}${["ar", "360"].includes(item.value) ? " thera-new-module-tab" : ""}`}
               {...(isAndroidUi
  ? {
      onClick: () => {
        if (item.label === "Explorar" && androidExploreTarget === "explorar") {
          if (activeTab === "explorar") {
            setAndroidExplorePanelOpen((open) => !open);
            if (androidExplorePanelOpen) setAndroidExplorePanelSection(null);
          } else {
            setAndroidExplorePanelOpen(true);
            navigateToTab("explorar");
          }
          return;
        }

        // Android Home UI V1.3: keep each bottom access explicitly wired
        // to the same existing tab/navigation contract. This avoids a visual
        // overlay swallowing the tap without changing any module logic.
        setAndroidExplorePanelOpen(false);
        setAndroidExplorePanelSection(null);
        navigateToTab(item.value);
      },
    }
  : {})}
              >
                <Icon />
                <span>{item.label}</span>
                {!isApprovedMember && memberOnlyTab(item.value) && <LockKeyhole className="mobile-lock" />}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value="explorar" id="main-content" className={`workspace platform-explore-workspace${isAndroidUi ? " android-explore-workspace" : isPwaUi ? " pwa-explore-workspace" : " web-explore-workspace"}`}>
          <aside className={`route-sidebar platform-explore-sidebar${isAndroidUi ? " android-explore-sidebar" : isPwaUi ? " pwa-explore-sidebar" : " web-explore-sidebar"}`}>
            {!isAndroidUi && (
              <div className="section-heading">
                <div>
                  <p className="eyebrow">EXPLORA CON CONFIANZA</p>
                  <h1>Tu próxima ruta empieza preparada.</h1>
                </div>
                <button aria-label="Abrir filtros">
                  <Settings />
                </button>
              </div>
            )}
            {!isAndroidUi && (
              <p className="supporting-copy">
                Descubre, guarda y revisa cada detalle antes de salir. Sin
                improvisaciones cuando la señal desaparece.
              </p>
            )}
            <div className={isAndroidUi ? `android-explore-search-row${androidSearchOpen ? " search-open" : " search-collapsed"}` : undefined}>
              {isAndroidUi && !androidSearchOpen ? (
                <button
                  type="button"
                  className="android-search-toggle"
                  onClick={() => setAndroidSearchOpen(true)}
                  aria-label="Abrir buscador"
                  aria-expanded={false}
                >
                  <Search />
                </button>
              ) : (
                <form className="search-field" onSubmit={searchPlaces} role="search">
                  <Search />
                  <input
                    value={routeSearch}
                    placeholder={isAndroidUi ? "Buscar lugar, cerro, laguna..." : "Ruta, parque o ciudad"}
                    onChange={(event) => {
                      setRouteSearch(event.target.value);
                      setPlaceResults([]);
                      setPlaceSearchMessage("");
                      if (event.target.value.trim()) setRoutesExpanded(true);
                    }}
                    aria-label="Buscar ruta o lugar en el mapa"
                    autoFocus={isAndroidUi && androidSearchOpen}
                  />
                  <button type="submit" className="place-search-submit" disabled={placeSearchBusy} aria-label="Buscar lugar">
                    {placeSearchBusy ? <RefreshCw className="spinning" /> : <span>Buscar</span>}
                  </button>
                  {isAndroidUi && (
                    <button
                      type="button"
                      className="android-search-close"
                      onClick={() => setAndroidSearchOpen(false)}
                      aria-label="Contraer buscador"
                    >
                      <X />
                    </button>
                  )}
                </form>
              )}
              {isAndroidUi && (
                <button
                  type="button"
                  className={`android-weather-toggle${androidWeatherOpen ? " active" : ""}`}
                  onClick={() => setAndroidWeatherOpen((open) => !open)}
                  aria-label={androidWeatherOpen ? "Cerrar clima en este punto" : "Abrir clima en este punto"}
                  aria-expanded={androidWeatherOpen}
                >
                  <Sun />
                </button>
              )}
              {isAndroidUi && (
                <button type="button" className="android-explore-gps-button" onClick={focusExploreOnGps} aria-label="Centrar mapa en mi ubicación GPS">
                  <LocateFixed />
                  <span>GPS</span>
                </button>
              )}
            </div>
            {isAndroidUi && androidWeatherOpen && (
              <div className="android-weather-popover" aria-label="Clima en este punto">
                <RouteWeatherPanel
                  compact
                  routeId={selectedPlace ? `lugar-${selectedPlace.id}` : `zona-${activeMapPack.id}`}
                  point={selectedPlace ? {
                    longitude: selectedPlace.longitude,
                    latitude: selectedPlace.latitude,
                    elevation: null,
                  } : {
                    longitude: activeMapPack.center[0],
                    latitude: activeMapPack.center[1],
                    elevation: null,
                  }}
                />
              </div>
            )}
            {(placeResults.length > 0 || placeSearchMessage) && (
              <section className="place-search-panel" aria-live="polite" aria-label="Resultados de lugares">
                <header>
                  <span><MapPin /></span>
                  <div><strong>Lugares en el mapa</strong><small>{placeSearchMessage}</small></div>
                  <button type="button" aria-label="Cerrar resultados" onClick={() => { setPlaceResults([]); setPlaceSearchMessage(""); }}><X /></button>
                </header>
                {placeResults.length > 0 && <div className="place-search-results">
                  {placeResults.map((place) => (
                    <button type="button" key={place.id} onClick={() => openPlace(place)}>
                      <MapPin />
                      <span><strong>{place.name}</strong><small>{place.label}</small></span>
                      <ChevronRight />
                    </button>
                  ))}
                </div>}
              </section>
            )}
            {!isAndroidUi && (useNationalOnlineMap ? (
              <div className="map-region-picker">
                <span><Layers3 /><span><strong>Mapa nacional</strong><small>{activeMapPack.detail}</small></span></span>
                <div className="map-region-toggle" role="status" aria-label="Mapa nacional activo">
                  <span>{activeMapPack.name}</span>
                  <Check />
                </div>
              </div>
            ) : (
              <div className="map-region-picker">
                <span><Layers3 /><span><strong>Mapa sin conexión</strong><small>{selectedMapPack.provinces.length ? selectedMapPack.provinces.join(" · ") : selectedMapPack.detail}</small></span></span>
                <button
                  className="map-region-toggle"
                  type="button"
                  aria-expanded={mapRegionPickerOpen}
                  aria-controls="map-region-options"
                  onClick={() => setMapRegionPickerOpen((open) => !open)}
                >
                  <span>{selectedMapPack.name}</span>
                  <ChevronDown />
                </button>
                {mapRegionPickerOpen && (
                  <div className="map-region-options" id="map-region-options" role="listbox" aria-label="Elegir mapa regional descargado">
                    {(["Sierra", "Costa", "Amazonía", "Insular", "Provincia"] as const).map((group) => (
                      <section key={group} aria-label={group}>
                        <strong>{group}</strong>
                        {ECUADOR_MAP_PACKS.filter((pack) => pack.group === group).map((pack) => (
                          <button
                            type="button"
                            role="option"
                            aria-selected={pack.id === selectedMapPackId}
                            className={pack.id === selectedMapPackId ? "selected" : ""}
                            onClick={() => chooseMapPack(pack.id)}
                            key={pack.id}
                          >
                            <span>{pack.name}</span>
                            {pack.id === selectedMapPackId ? <Check /> : null}
                          </button>
                        ))}
                      </section>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {isApprovedMember ? <>
            {!isAndroidUi && <div className="chip-row">
              <button
                className={activityFilter === "senderismo" ? "selected" : ""}
                onClick={() => setActivityFilter("senderismo")}
              >
                <Footprints /> Senderismo
              </button>
              <button
                className={activityFilter !== "senderismo" ? "selected" : ""}
                onClick={() => {
                  setActivityFilter("escalada");
                  setActiveTab("escalada");
                }}
              >
                <Mountain /> Escalada y rápel
              </button>
            </div>}
            {!isAndroidUi && <details className="new-route-card new-route-collapsible">
              <summary>
                <span><Navigation /></span>
                <span><strong>Grabar ruta nueva</strong><small>GPS libre o planificación por puntos</small></span>
                <ChevronDown />
              </summary>
              <div className="new-route-collapsible-body">
                <button className="new-route-free" type="button" onClick={() => prepareRecordingMode("free")}><Navigation /><span><strong>Grabación libre</strong><small>Empieza directamente desde tu GPS</small></span><ChevronRight /></button>
                {renderCompactPlanner("explore")}
              </div>
            </details>}
            {!isAndroidUi && <div className="registered-routes">
              <button
                className="registered-routes-toggle"
                type="button"
                aria-expanded={routesExpanded}
                aria-controls="registered-route-list"
                onClick={() => setRoutesExpanded((current) => !current)}
              >
                <span><Route /></span>
                <span><strong>Rutas registradas</strong><small>{filteredRoutes.length} disponibles</small></span>
                <ChevronDown />
              </button>
              {routesExpanded && (
                <div id="registered-route-list" className="route-list">
                  {filteredRoutes.length ? filteredRoutes.map((routeItem) => (
                    <button
                      type="button"
                      key={routeItem.id}
                      className={`route-card ${routeChosen && selectedRoute.id === routeItem.id ? "selected" : ""}`}
                      onClick={() => openRoute(routeItem)}
                      aria-controls="route-view"
                      aria-pressed={routeChosen && selectedRoute.id === routeItem.id}
                    >
                      <div className="route-card-top">
                        <span>{routeItem.area}</span>
                        <span className={`publication ${routeItem.status === "Publicada" ? "published" : "review"}`}>
                          {routeItem.status}
                        </span>
                      </div>
                      <h2>{routeItem.name}</h2>
                      <div className="mini-profile">
                        <svg viewBox="0 0 370 112">
                          <polyline points={routeItem.points} fill="none" stroke="#FF7F00" strokeWidth="5" strokeLinecap="round" />
                        </svg>
                      </div>
                      <div className="route-meta">
                        <span><Route />{routeItem.distance}</span>
                        <span><Timer />{routeItem.time}</span>
                        <span><Mountain />{routeItem.ascent}</span>
                      </div>
                      <div className="route-footer">
                        <span className="difficulty">{routeItem.difficulty}</span>
                        <span>{hasValidRouteTrace(routeItem) ? "Elegir ruta" : "Ver mapa · sin GPX"} <ChevronRight /></span>
                      </div>
                    </button>
                  )) : <p className="route-list-empty">No encontramos rutas con esos datos.</p>}
                </div>
              )}
            </div>}
            </> : <aside className="visitor-access-card">
              <span><Eye /></span>
              <div>
                <strong>Exploración pública</strong>
                <small>Desplaza el mapa, busca lugares y prueba las vistas Mapa, Relieve y 3D.</small>
              </div>
              <button type="button" onClick={() => openMemberAccess("Solicita acceso para grabar, crear o seguir rutas.")}>
                <LockKeyhole /> Solicitar acceso
              </button>
            </aside>}
          </aside>
          {routeChosen && isApprovedMember ? <section id="route-view" ref={mapSectionRef} className={`map-column platform-map-frame ${isAndroidUi ? "android-map-frame" : isPwaUi ? "pwa-map-frame" : "web-map-frame"}`} tabIndex={-1} aria-label="Ruta seleccionada" aria-live="polite">
            <div className={`map-header platform-map-header ${isAndroidUi ? "android-map-header" : isPwaUi ? "pwa-map-header" : "web-map-header"}`}>
              <div>
                <div>
                  <p>{selectedRoute.area}</p>
                  <h2>{selectedRoute.name}</h2>
                </div>
              </div>
              <div className="map-header-actions">
                <Button variant="outline" onClick={shareRoute}>
                  <Share2 /> Compartir
                </Button>
                <Button onClick={() => prepareRecordingMode("selected-route")}>
                  <Navigation /> Iniciar ruta
                </Button>
              </div>
            </div>
            <TopographicMap route={plannedRouteSegment ?? selectedRoute} mapPackId={activeMapPackId} allowGuestViewer persistDownloadedMaps={false} />
            <div className="route-summary">
              <RouteStats route={selectedRoute} />
              {(plannedRouteSegment ?? selectedRoute).geojson && hasValidRouteTrace(plannedRouteSegment ?? selectedRoute) && (
                <RouteWeatherPanel
                  routeId={`${selectedRoute.id}-ficha-${routeCoordinates((plannedRouteSegment ?? selectedRoute).geojson)[0]?.latitude.toFixed(4) ?? "sin-inicio"}`}
                  routeGeojson={(plannedRouteSegment ?? selectedRoute).geojson}
                />
              )}
              {selectedRoute.geojson && hasValidRouteTrace(selectedRoute) && (
                <RoutePointPlanner
                  route={selectedRoute}
                  onPreview={setPlannedRouteSegment}
                  onStart={(segment) => prepareRecordingMode("selected-route", segment)}
                />
              )}
              <RouteWaypointDistances route={selectedRoute} />
              <div className="route-action-row">
                <button onClick={() => setActiveTab("offline")}>
                  <CloudDownload />
                  <span>
                    <strong>Preparar sin conexión</strong>
                    <small>Mapa regional + archivo de ruta</small>
                  </span>
                  <ChevronRight />
                </button>
                <label>
                  <ImagePlus />
                  <span>
                    <strong>Añadir fotografías</strong>
                    <small>Conversión automática a WebP</small>
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={convertPhotos}
                  />
                </label>
                <button
                  onClick={() => {
                    setActiveTab("planificar");
                    setToast(
                      "Importa el GPX validado para descargarlo o convertirlo.",
                    );
                  }}
                >
                  <Download />
                  <span>
                    <strong>GPX y GeoJSON</strong>
                    <small>Disponible después de validar la ruta</small>
                  </span>
                  <ChevronRight />
                </button>
              </div>
            </div>
          </section> : (
            <section id="route-view" ref={mapSectionRef} className={`map-column explore-base-map platform-map-frame ${isAndroidUi ? "android-map-frame" : isPwaUi ? "pwa-map-frame" : "web-map-frame"}`} tabIndex={-1} aria-label="Mapa general sin ruta seleccionada" aria-live="polite">
              <div className={`map-header platform-map-header ${isAndroidUi ? "android-map-header" : isPwaUi ? "pwa-map-header" : "web-map-header"}`}>
                <div>
                <div>
                    <p>{selectedPlace ? "Resultado de búsqueda" : "Mapa general"}</p>
                    <h2>{selectedPlace ? selectedPlace.name : "Explora sin una ruta cargada"}</h2>
                </div>
                </div>
                <Button className="empty-map-action" onClick={() => isApprovedMember
                  ? prepareRecordingMode("free")
                  : openMemberAccess("Solicita acceso para grabar tu propia ruta.")}>
                  {isApprovedMember ? <Navigation /> : <LockKeyhole />} <span>{isApprovedMember ? "Grabar ruta nueva" : "Solicitar acceso"}</span>
                </Button>
              </div>
              <TopographicMap
                route={placeMapRoute}
                mapPackId={activeMapPackId}
                showMissingTraceNotice={false}
                searchTarget={selectedPlace ? {
                  longitude: selectedPlace.longitude,
                  latitude: selectedPlace.latitude,
                  label: selectedPlace.name,
                } : undefined}
                allowGuestViewer
                persistDownloadedMaps={false}
                plannerPoints={plannerPoints}
                plannerRouteCoordinates={plannerRouteCoordinates}
                plannerRouteHasFallback={plannerHasDirectFallback}
                plannerActive={plannerTool !== null}
                onPlannerMapClick={addPlannerPoint}
              />
              {isAndroidUi && androidExplorePanelOpen && (
                <section className="android-explore-drawer open nav-controlled android-context-dock" aria-label="Herramientas de Explorar">
                  <button
                    type="button"
                    className="android-context-dock-close"
                    onClick={() => {
                      setAndroidExplorePanelSection(null);
                      setAndroidExplorePanelOpen(false);
                    }}
                    aria-label="Cerrar herramientas de Explorar y volver a la navegación principal"
                  >
                    <X />
                  </button>
                  <div className="android-explore-drawer-body">
                      <div className="android-explore-action-grid">
                        <button
                          type="button"
                          className="primary"
                          onClick={() => isApprovedMember
                            ? prepareRecordingMode("free")
                            : openMemberAccess("Solicita acceso para grabar tu propia ruta.")}
                        >
                          <Navigation /><span>Grabar ruta</span>
                        </button>
                        <button
                          type="button"
                          className={androidExplorePanelSection === "routes" ? "selected" : ""}
                          onClick={() => setAndroidExplorePanelSection((section) => section === "routes" ? null : "routes")}
                        >
                          <Route /><span>Mis rutas</span>
                        </button>
                        <button
                          type="button"
                          className={androidExplorePanelSection === "planner" ? "selected" : ""}
                          onClick={() => setAndroidExplorePanelSection((section) => section === "planner" ? null : "planner")}
                        >
                          <MapPin /><span>Por puntos</span>
                        </button>
                        <button
                          type="button"
                          className={androidExplorePanelSection === "activity" ? "selected" : ""}
                          onClick={() => setAndroidExplorePanelSection((section) => section === "activity" ? null : "activity")}
                        >
                          <Footprints /><span>Senderismo</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            clearPlannerPoints();
                            chooseMapPack(selectedMapPackId);
                            setAndroidExplorePanelSection(null);
                            setAndroidExplorePanelOpen(false);
                          }}
                        >
                          <MapPinned /><span>Mapa sin ruta</span>
                        </button>
                      </div>

                      {androidExplorePanelSection === "routes" && (
                        <div className="android-explore-subpanel android-explore-route-list" aria-label="Mis rutas registradas">
                          {filteredRoutes.length ? filteredRoutes.map((routeItem) => (
                            <button type="button" key={`android-quick-${routeItem.id}`} onClick={() => openRoute(routeItem)}>
                              <Route />
                              <span><strong>{routeItem.name}</strong><small>{routeItem.area} · {routeItem.distance}</small></span>
                              <ChevronRight />
                            </button>
                          )) : <p>No hay rutas registradas para este filtro.</p>}
                        </div>
                      )}

                      {androidExplorePanelSection === "planner" && (
                        <div className="android-explore-subpanel android-explore-planner">
                          {renderCompactPlanner("explore", { open: true, hideSummary: true })}
                        </div>
                      )}

                      {androidExplorePanelSection === "activity" && (
                        <div className="android-explore-subpanel android-explore-activity-picker" aria-label="Elegir actividad">
                          <button
                            type="button"
                            className={activityFilter === "senderismo" ? "selected" : ""}
                            onClick={() => { setActivityFilter("senderismo"); setAndroidExplorePanelSection(null); }}
                          >
                            <Footprints /> Senderismo
                          </button>
                          <button
                            type="button"
                            className={activityFilter !== "senderismo" ? "selected" : ""}
                            onClick={() => { setActivityFilter("escalada"); setAndroidExplorePanelSection(null); setActiveTab("escalada"); }}
                          >
                            <Mountain /> Escalada y rápel
                          </button>
                        </div>
                      )}
                    </div>
                </section>
              )}
              {!isAndroidUi && (
                <div className="empty-map-footer-stack">
                  <div className="empty-map-footer">
                    <MapPinned />
                    <span>
                      <strong>{selectedPlace ? selectedPlace.name : isApprovedMember ? activeMapPack.name : `Mapa público · ${activeMapPack.shortName}`}</strong>
                      <small>{selectedPlace
                        ? `${selectedPlace.label}. Este resultado es un lugar del mapa; todavía no carga una ruta registrada.`
                        : isApprovedMember
                          ? "El mapa permanece disponible. Los recorridos aparecerán únicamente cuando elijas una ruta o comiences tu propio GPX."
                          : "Puedes desplazarlo, acercarlo y cambiar entre mapa, relieve y vista 3D. Las rutas y herramientas de creación permanecen protegidas."}</small>
                    </span>
                  </div>
                  <RouteWeatherPanel
                    compact
                    routeId={selectedPlace ? `lugar-${selectedPlace.id}` : `zona-${activeMapPack.id}`}
                    point={selectedPlace ? {
                      longitude: selectedPlace.longitude,
                      latitude: selectedPlace.latitude,
                      elevation: null,
                    } : {
                      longitude: activeMapPack.center[0],
                      latitude: activeMapPack.center[1],
                      elevation: null,
                    }}
                  />
                </div>
              )}
            </section>
          )}
        </TabsContent>

        <TabsContent value="planificar" className="feature-page planner-feature-page">
          {isWebUi && (
            <nav className="web-planner-tool-rail" aria-label="Herramientas de Planificar">
              <button
                type="button"
                onClick={() => document.querySelector(".planner-map-card")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              >
                <MapPinned />
                <span><strong>Mapa</strong><small>Planificar</small></span>
              </button>
              <label className="web-planner-tool-upload">
                <FileUp />
                <span><strong>GPX</strong><small>{importedRouteFile ? "Cargado" : "Importar"}</small></span>
                <input
                  type="file"
                  accept=".gpx,application/gpx+xml,application/xml,text/xml"
                  onChange={importGpx}
                />
              </label>
              <label className="web-planner-tool-upload">
                <ImagePlus />
                <span><strong>Fotos</strong><small>{photoResults.length}/6</small></span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={convertPhotos}
                />
              </label>
              <button
                type="button"
                onClick={() => {
                  const panel = document.querySelector<HTMLElement>(".planner-360-panel");
                  panel?.classList.add("web-planner-overlay-open");
                  panel?.focus();
                }}
              >
                <MoveHorizontal />
                <span><strong>360</strong><small>{panoramaResults.length ? `${panoramaResults.length}/8` : "Beta"}</small></span>
              </button>
              <button
                type="button"
                disabled={localRouteDrafts.length + ownRoutes.length === 0}
                onClick={() => document.querySelector(".planner-saved-routes-panel")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              >
                <Route />
                <span><strong>Mis rutas</strong><small>{localRouteDrafts.length + ownRoutes.length}</small></span>
              </button>
            </nav>
          )}
          <div className="route-builder planner-route-builder">
            <p className="eyebrow">RUTAS COMPARTIDAS · WEB + ANDROID</p>
            <h1>Crea una vez. Continúa desde cualquier dispositivo.</h1>
            <p>
              La ruta se guarda primero como borrador privado. El mismo usuario
              podrá verla en el sitio y en la aplicación Android antes de
              enviarla a revisión.
            </p>
            <div className="route-builder-form">
              <label>
                Nombre de la ruta
                <input
                  value={routeForm.name}
                  maxLength={120}
                  placeholder="Ej. Tres Cruces · Luspa"
                  onChange={(event) =>
                    setRouteForm((current) => ({ ...current, name: event.target.value }))
                  }
                />
              </label>
              <div className="route-form-grid">
                <label>
                  Parque, sector o zona
                  <input
                    value={routeForm.area}
                    maxLength={160}
                    placeholder="Parque Nacional Cajas"
                    onChange={(event) =>
                      setRouteForm((current) => ({ ...current, area: event.target.value }))
                    }
                  />
                </label>
                <label>
                  Actividad
                  <select
                    value={routeForm.kind}
                    onChange={(event) =>
                      setRouteForm((current) => ({
                        ...current,
                        kind: event.target.value as ActivityKind,
                      }))
                    }
                  >
                    <option value="senderismo">Senderismo</option>
                    <option value="escalada">Escalada</option>
                    <option value="rapel">Rápel</option>
                  </select>
                </label>
              </div>
              <label>
                Descripción
                <textarea
                  value={routeForm.description}
                  maxLength={5000}
                  rows={3}
                  placeholder="Recorrido, terreno y puntos importantes"
                  onChange={(event) =>
                    setRouteForm((current) => ({ ...current, description: event.target.value }))
                  }
                />
              </label>
              <label>
                Recomendaciones de seguridad
                <textarea
                  value={routeForm.recommendations}
                  maxLength={5000}
                  rows={2}
                  placeholder="Equipo, clima, horarios y advertencias"
                  onChange={(event) =>
                    setRouteForm((current) => ({
                      ...current,
                      recommendations: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Tiempo estimado en horas
                <input
                  value={routeForm.estimatedHours}
                  inputMode="decimal"
                  placeholder="Ej. 4,5"
                  onChange={(event) =>
                    setRouteForm((current) => ({
                      ...current,
                      estimatedHours: event.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="route-file-actions">
              <label>
                <FileUp />
                <span>
                  <strong>{importedRouteFile ? importedRouteFile.name : "Añadir GPX"}</strong>
                  <small>Archivo original · máximo 50 MB</small>
                </span>
                <input
                  type="file"
                  accept=".gpx,application/gpx+xml,application/xml,text/xml"
                  onChange={importGpx}
                />
              </label>
              <label>
                <ImagePlus />
                <span>
                  <strong>Añadir fotografías</strong>
                  <small>{photoResults.length}/6 · se convierten a WebP</small>
                </span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={convertPhotos}
                />
              </label>
            </div>
            <div className="import-status">
              <ClipboardCheck />
              <span>{importMessage}</span>
            </div>
            {photoResults.length > 0 && (
              <div className="route-photo-strip">
                {photoResults.map((photo) => (
                  <img key={photo.id} src={photo.url} alt={photo.altText} />
                ))}
              </div>
            )}
            <section
              className="panorama-beta-panel planner-360-panel"
              role={isWebUi ? "dialog" : undefined}
              aria-modal={isWebUi ? true : undefined}
              aria-label={isWebUi ? "Ruta 360" : undefined}
              tabIndex={isWebUi ? -1 : undefined}
              onKeyDown={(event) => {
                if (isWebUi && event.key === "Escape") {
                  event.currentTarget.classList.remove("web-planner-overlay-open");
                }
              }}
            >
              {isWebUi && (
                <button
                  type="button"
                  className="web-planner-360-close"
                  aria-label="Cerrar panel Ruta 360"
                  onClick={(event) => event.currentTarget.closest(".planner-360-panel")?.classList.remove("web-planner-overlay-open")}
                >
                  <X />
                  <span>Cerrar</span>
                </button>
              )}
              <header>
                <div>
                  <p className="eyebrow">CREAR UNA RUTA 360</p>
                  <h2>Ruta 360 <span>beta</span></h2>
                  <small>
                    {currentProfile?.status === "approved"
                      ? `Autor: ${currentProfile.first_names} ${currentProfile.last_names}`
                      : "Inicia sesión como autor aprobado para crearla"}
                  </small>
                </div>
                <i>360°</i>
              </header>
              <p>Usa el GPX como eje y coloca panoramas del teléfono en puntos clave. Esta beta se guarda como borrador privado.</p>
              {selectedPanorama ? (
                <Panorama360Viewer src={selectedPanorama.url} alt={`Panorama 360 de ${selectedPanorama.stopName}`} />
              ) : (
                <div className="panorama-empty-preview"><MoveHorizontal /><strong>Tu vista interactiva aparecerá aquí</strong><small>Recomendado: imagen equirectangular 2:1</small></div>
              )}
              <div className="panorama-steps" aria-label="Pasos para crear una ruta 360">
                <span className={importedTrack.length > 1 ? "done" : ""}><i>1</i>GPX</span>
                <span className={panoramaResults.length ? "active" : ""}><i>2</i>Panoramas</span>
                <span><i>3</i>Revisar</span>
              </div>
              <label className={`panorama-upload${currentProfile?.status === "approved" ? "" : " disabled"}`}>
                <ImagePlus />
                <span><strong>Añadir panorama 360</strong><small>{panoramaResults.length}/8 · JPG, PNG o WebP</small></span>
                <b>+</b>
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple disabled={currentProfile?.status !== "approved"} onChange={convertPanoramas} />
              </label>
              {panoramaResults.length > 0 && (
                <div className="panorama-stop-list">
                  {panoramaResults.map((panorama, index) => (
                    <article className={selectedPanorama?.id === panorama.id ? "selected" : ""} key={panorama.id}>
                      <button type="button" className="panorama-thumb" onClick={() => setSelectedPanoramaId(panorama.id)} aria-label={`Ver ${panorama.stopName}`}><img src={panorama.url} alt="" /></button>
                      <div>
                        <label>Nombre<input value={panorama.stopName} maxLength={80} onChange={(event) => updatePanorama(panorama.id, { stopName: event.target.value })} /></label>
                        <label>Kilómetro<input type="number" min="0" max={Math.max(0, plannerRouteDistanceKm).toFixed(2)} step="0.1" value={panorama.distanceKm} onChange={(event) => updatePanorama(panorama.id, { distanceKm: Math.max(0, Math.min(plannerRouteDistanceKm || Number.POSITIVE_INFINITY, Number(event.target.value) || 0)) })} /></label>
                        <small className={panorama.coverage === "spherical" ? "panorama-compatible" : "panorama-partial"}>{panorama.coverage === "spherical" ? "2:1 compatible · esfera completa" : "Panorama ancho · vista horizontal"}</small>
                      </div>
                      <div className="panorama-stop-actions">
                        <button type="button" onClick={() => setSelectedPanoramaId(panorama.id)}><Eye />Ver 360</button>
                        <button type="button" onClick={() => removePanorama(panorama.id)}><X />Eliminar</button>
                      </div>
                      <b>Parada {index + 1}</b>
                    </article>
                  ))}
                </div>
              )}
              <div className="panorama-private-note"><ShieldCheck /><span><strong>Borrador local · no publicado</strong><small>Solo la cuenta autora puede crear, editar o eliminar estas paradas.</small></span></div>
              <button className="panorama-save-button" type="button" disabled={routeBusy || currentProfile?.status !== "approved" || importedTrack.length < 2 || !panoramaResults.length} onClick={() => saveRouteWorkflow(false)}><Save />{routeBusy ? "Guardando…" : "Guardar prueba 360"}</button>
            </section>
            <div className="route-sync-actions">
              <Button
                disabled={routeBusy || currentProfile?.status !== "approved"}
                variant="outline"
                onClick={() => saveRouteWorkflow(false)}
              >
                <Save /> {routeBusy ? "Guardando…" : "Guardar borrador"}
              </Button>
              <Button
                disabled={
                  routeBusy ||
                  currentProfile?.status !== "approved" ||
                  !networkOnline ||
                  importedTrack.length < 2 ||
                  panoramaResults.length > 0
                }
                onClick={() => saveRouteWorkflow(true)}
              >
                <Upload /> Enviar a revisión
              </Button>
            </div>
            {panoramaResults.length > 0 && <p className="route-auth-note">Para proteger la beta, las rutas con panoramas 360 todavía no se envían a publicación. Guarda la prueba privada.</p>}
            {currentProfile?.status !== "approved" && (
              <p className="route-auth-note">
                Inicia sesión con un usuario aprobado para sincronizar la ruta.
              </p>
            )}
            {(localRouteDrafts.length > 0 || ownRoutes.length > 0) && (
              <details className="my-routes-panel planner-saved-routes-panel">
                <summary>
                  <span><Route /></span>
                  <span><strong>Mis rutas guardadas</strong><small>{localRouteDrafts.length + ownRoutes.length} entre borradores y sincronizadas</small></span>
                  <ChevronDown />
                </summary>
                <div className="my-routes-list">
                  {localRouteDrafts.map((draft) => (
                    <button key={`local-${draft.id}`} onClick={() => restoreRouteDraft(draft)}>
                      <WifiOff />
                      <span>
                        <strong>{draft.name}</strong>
                        <small>Borrador guardado en este dispositivo</small>
                      </span>
                      <ChevronRight />
                    </button>
                  ))}
                  {ownRoutes.map((route) => (
                    <div className="my-route-row" key={`remote-${route.id}`}>
                      <CloudDownload />
                      <span>
                        <strong>{route.name}</strong>
                        <small>
                          {route.status === "draft"
                            ? "Borrador sincronizado"
                            : route.status === "pending"
                              ? "En revisión administrativa"
                              : route.status === "published"
                                ? "Publicada en web y Android"
                                : "Requiere correcciones"}
                        </small>
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
          <div className="planner-card planner-map-card">
            <TopographicMap
              route={plannerRoute}
              mapPackId={activeMapPackId}
              allowGuestViewer={useNationalOnlineMap}
              persistDownloadedMaps={!useNationalOnlineMap}
              showMissingTraceNotice={Boolean(importedTrack.length > 1 || routeChosen)}
              plannerPoints={plannerPoints}
              plannerRouteCoordinates={plannerRouteCoordinates}
              plannerRouteHasFallback={plannerHasDirectFallback}
              plannerActive={plannerTool !== null}
              onPlannerMapClick={addPlannerPoint}
            />
            <div className="planner-controls">
              <span>
                <i>1</i>Borrador privado
              </span>
              <span>
                <i>2</i>Sincronización
              </span>
              <span>
                <i>3</i>Revisión
              </span>
              <Button
                variant="outline"
                disabled={!importedTrack.length}
                onClick={() => exportImported("geojson")}
              >
                <FileArchive /> Descargar copia
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="grabar" className={`record-page${!recordingMode && !recording && !activityId && !track.length ? " record-mode-choice" : ""}`}>
          <section className="record-map">
            <TopographicMap
              // Android/móvil solamente: cuando la aproximación por caminos llega
              // después de que el mapa empezó a cargar, remontamos esta instancia
              // una sola vez para que MapLibre cree la fuente azul con los datos
              // desde el primer ingreso. Web conserva exactamente su ciclo actual.
              key={isAndroidApp
                ? `android-record-map-${selectedRoute.id}-${roadApproachRoute?.coordinates?.length ? "approach" : "pending"}`
                : "web-record-map"}
              route={recordingMapRoute}
              mapPackId={recordingMode === "selected-route" ? guidedRouteMapPackId : activeMapPackId}
              recording={recording}
              allowGuestViewer={useNationalOnlineMap}
              persistDownloadedMaps={!useNationalOnlineMap}
              showMissingTraceNotice={false}
              recordedTrack={track}
              currentPosition={currentNavigationPosition ?? undefined}
              recenterSignal={isAndroidApp ? mobileMapRecenterSignal : 0}
              // Si ya existe una ruta por caminos mostramos solo esa linea
              // azul. La linea directa punteada queda como respaldo mientras
              // el calculo no existe o falla, evitando dos guias superpuestas.
              approachTarget={recordingMode === "selected-route" && !roadApproachRoute ? approachTarget : undefined}
              roadApproachCoordinates={recordingMode === "selected-route" ? roadApproachRoute?.coordinates : undefined}
              followUser={recording && !recordingPaused}
              onSnapshotProvider={registerMapSnapshot}
              plannerPoints={!recording ? plannerPoints : []}
              plannerRouteCoordinates={!recording ? plannerRouteCoordinates : []}
              plannerRouteHasFallback={plannerHasDirectFallback}
              plannerActive={!recording && plannerTool !== null}
              onPlannerMapClick={!recording ? addPlannerPoint : undefined}
            />
          </section>
          <aside className="record-console">
            <p className="eyebrow">TU AVENTURA, BAJO TU CONTROL</p>
            {isAndroidApp && nativePendingRecovery && !recording && (
              <section className="native-pending-recovery" role="status" aria-label="Actividad Android pendiente">
                <RefreshCw />
                <div className="native-pending-recovery-copy">
                  <strong>Actividad pendiente en este dispositivo</strong>
                  <small>
                    {formatDuration(Math.floor((nativePendingRecovery.snapshot.elapsedMilliseconds ?? 0) / 1000))}
                    {` · ${nativePendingRecovery.points.length} puntos GPS`}
                    {nativePendingRecovery.snapshot.state === "paused" ? " · en pausa" : " · activa"}
                  </small>
                </div>
                <div className="native-pending-recovery-actions">
                  <button type="button" disabled={nativeRecoveryBusy} onClick={() => recoverPendingNativeActivity("recover")}>
                    <RefreshCw />{nativeRecoveryBusy ? "Recuperando…" : "Recuperar"}
                  </button>
                  <button type="button" disabled={nativeRecoveryBusy} onClick={() => recoverPendingNativeActivity("finish")}>
                    <Save />Finalizar
                  </button>
                  <button type="button" className="danger" disabled={nativeRecoveryBusy} onClick={() => recoverPendingNativeActivity("discard")}>
                    <Trash2 />Descartar
                  </button>
                </div>
                <small className="native-pending-recovery-note">No se inicia un segundo GPS. THERA vuelve a enlazar la misma actividad guardada por Android.</small>
              </section>
            )}
            {!recordingMode && !recording && !activityId && !track.length ? (
              <section className="recording-mode-picker">
                <h1>¿Cómo quieres comenzar?</h1>
                <p>Elige una modalidad. THERA no cargará ninguna ruta anterior por defecto.</p>
                <details className="recording-mode-new recording-mode-new-details">
                  <summary><span><Navigation /></span><span><strong>Grabar ruta nueva</strong><small>GPS libre o planificación por puntos</small></span><ChevronDown /></summary>
                  <div className="recording-new-options-body">
                    <button className="recording-new-free" type="button" onClick={() => prepareRecordingMode("free")}><Navigation /><span><strong>Grabación libre</strong><small>Comienza desde tu posición GPS</small></span><ChevronRight /></button>
                    {renderCompactPlanner("record")}
                  </div>
                </details>
                <button className="recording-mode-saved" type="button" onClick={chooseRegisteredRoute}>
                  <span><Route /></span>
                  <span><strong>Seguir ruta registrada</strong><small>Elige una ruta guardada solamente si la necesitas</small></span>
                  <ChevronRight />
                </button>
                <div className="recording-mode-status"><LocateFixed /><span><strong>Mapa limpio</strong><small>Sin rutas cargadas · listo para GPS</small></span></div>
              </section>
            ) : <>
            <div className="activity-type">
              <Footprints />
              <span>{recordingMode === "free" ? "Grabación libre · GPX propio" : selectedRoute.name}</span>
              {!recording && !activityId && !track.length
                ? <button type="button" onClick={() => setRecordingMode(null)}>Cambiar</button>
                : <ChevronRight />}
            </div>
            {recordingMode === "selected-route" && (
              <details className="route-alert-settings">
                <summary>
                  <span><Bell /><strong>Avisos de desvío</strong></span>
                  <small>{deviationAlertMode === "none" ? "Sin avisos" : deviationAlertMode === "vibration" ? "Vibración" : deviationAlertMode === "message" ? "Mensaje" : "Ambos"}</small>
                  <ChevronDown />
                </summary>
                <div className="route-alert-options" role="group" aria-label="Avisos de desvío">
                  {([
                    ["none", "Ninguno"],
                    ["vibration", "Vibración"],
                    ["message", "Mensaje"],
                    ["both", "Ambos"],
                  ] as Array<[DeviationAlertMode, string]>).map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      aria-pressed={deviationAlertMode === mode}
                      className={deviationAlertMode === mode ? "selected" : ""}
                      onClick={() => setDeviationAlertMode(mode)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p>Se guarda en este dispositivo. No cambia el mapa, el GPS ni la ruta descargada.</p>
              </details>
            )}
            {recordingWeatherPoint && (
              <RouteWeatherPanel
                compact
                routeId={`inicio-${recordingMode ?? "ruta"}-${recordingWeatherPoint.latitude.toFixed(4)}-${recordingWeatherPoint.longitude.toFixed(4)}`}
                point={recordingWeatherPoint}
              />
            )}
            <div className="timer-display">{formatDuration(seconds)}</div>
            <p className={`gps-status ${liveStats.offRoute ? "off-route" : liveStats.approachingStart ? "approaching" : ""}`}>
              <LocateFixed />
              {gpsMessage}
            </p>
            {recordingMode === "selected-route" && liveStats.approachingStart && approachDistanceKm !== null && (
              <div className="route-approach-guidance" role="status">
                <i
                  aria-hidden="true"
                  style={{ transform: `rotate(${(liveStats.bearingToStartDegrees ?? 45) - 45}deg)` }}
                >
                  <Navigation />
                </i>
                <span>
                  <small>Aproximación al inicio</small>
                  <strong>Dirígete al punto de inicio</strong>
                  <em>
                    {formatDistance(approachDistanceKm)} km {roadApproachRoute ? "por caminos" : "en línea recta"}
                    {!roadApproachRoute && ` · rumbo ${compassDirection(liveStats.bearingToStartDegrees)}`}
                  </em>
                </span>
                <div className="approach-mode-actions" aria-label="Calcular camino hasta el inicio">
                  <button
                    type="button"
                    className={roadApproachRoute?.mode === "foot-walking" ? "selected" : ""}
                    disabled={approachRoutingBusy}
                    onClick={() => calculateRoadApproach("foot-walking")}
                  >
                    <Footprints /> A pie
                  </button>
                  <button
                    type="button"
                    className={roadApproachRoute?.mode === "driving-car" ? "selected" : ""}
                    disabled={approachRoutingBusy}
                    onClick={() => calculateRoadApproach("driving-car")}
                  >
                    <Car /> En vehículo
                  </button>
                  {roadApproachRoute && (
                    <button
                      type="button"
                      className="recalculate"
                      disabled={approachRoutingBusy || !networkOnline}
                      onClick={() => calculateRoadApproach(approachMode)}
                      aria-label="Recalcular camino"
                    >
                      <RefreshCw />
                    </button>
                  )}
                </div>
                <small className="approach-routing-message">
                  {approachRoutingBusy ? "Calculando…" : approachRoutingMessage}
                </small>
                {roadApproachRoute && (
                  <div className="road-approach-result">
                    <span>
                      <b>{formatDistance(approachDistanceKm)} km</b>
                      <small>{formatDuration(approachTimeSeconds ?? roadApproachRoute.durationSeconds)}</small>
                    </span>
                    <p>{roadProgress?.instruction?.instruction || "Continúa por el camino azul hasta el inicio."}</p>
                    <details>
                      <summary>Ver indicaciones</summary>
                      <ol>
                        {roadApproachRoute.instructions.slice(0, 8).map((step, index) => (
                          <li key={`${step.startIndex}-${step.endIndex}-${index}`}>
                            <span>{step.instruction}</span>
                            <b>{step.distanceM < 1000 ? `${Math.round(step.distanceM)} m` : `${formatDistance(step.distanceM / 1000)} km`}</b>
                          </li>
                        ))}
                      </ol>
                    </details>
                    <small>{roadApproachRoute.offline ? "Guardada sin conexión" : "OpenRouteService · datos OpenStreetMap"}</small>
                  </div>
                )}
              </div>
            )}
            {recordingMode === "selected-route" && liveStats.routeDistanceKm > 0 && (
              <div className="navigation-progress" aria-label={`${Math.round(liveStats.progress)} por ciento de la ruta`}>
                <span><i style={{ width: `${liveStats.progress}%` }} /></span>
                <small>{liveStats.approachingStart ? "La ruta comenzará al llegar al inicio" : `${Math.round(liveStats.progress)}% del recorrido planificado`}</small>
              </div>
            )}
            <div className="live-stats">
              <div>
                <strong>{formatDistance(liveStats.recordedDistanceKm)}</strong>
                <span>km recorridos</span>
              </div>
              <div>
                <strong>{recordingMode === "free" ? formatDuration(seconds) : liveStats.routeDistanceKm ? formatDistance(liveStats.remainingDistanceKm) : "—"}</strong>
                <span>{recordingMode === "free" ? "tiempo activo" : liveStats.approachingStart ? "km de ruta pendientes" : "km restantes"}</span>
              </div>
              <div>
                <strong>{recordingMode === "free" ? track.length : liveStats.approachingStart
                  ? approachTimeSeconds === null ? "—" : formatDuration(approachTimeSeconds)
                  : liveStats.estimatedRemainingSeconds === null ? "—" : formatDuration(liveStats.estimatedRemainingSeconds)}</strong>
                <span>{recordingMode === "free" ? "puntos GPS" : liveStats.approachingStart ? "tiempo al iniciar" : "tiempo restante"}</span>
              </div>
            </div>
            <div className="navigation-metrics">
              <span><Mountain /><b>{liveStats.currentAltitudeM ?? "—"} m</b><small>Altitud</small></span>
              <span><ArrowUpRight /><b>+{liveStats.ascentM} m</b><small>Ascenso</small></span>
              <span><Gauge /><b>{liveStats.averageSpeedKmh.toFixed(1)} km/h</b><small>Velocidad media</small></span>
              <span><Signal /><b>{liveStats.gpsQuality}</b><small>GPS {track.at(-1) ? `±${Math.round(track.at(-1)!.accuracy)} m` : ""}</small></span>
              <span><BatteryMedium /><b>{batteryPercent === null ? "—" : `${batteryPercent}%`}</b><small>Batería</small></span>
              <span><WifiOff /><b>{networkOnline ? "En línea" : "Sin conexión"}</b><small>Guardado local</small></span>
            </div>
            {recordingMode === "selected-route" && liveStats.nextWaypoint && (
              <div className={`next-waypoint ${liveStats.approachingStart ? "route-start-target" : ""}`}>
                <MapPinned />
                <span><small>{liveStats.approachingStart ? "Primero dirígete a" : "Siguiente punto"}</small><strong>{liveStats.nextWaypoint.name}</strong></span>
                <b>{formatDistance(liveStats.approachingStart && approachDistanceKm !== null ? approachDistanceKm : liveStats.nextWaypoint.distanceKm)} km</b>
              </div>
            )}
            {recording && (
              <div className="activity-markers">
                <small>Marcar lugar importante</small>
                <div>
                  <button onClick={() => addActivityMarker("water", "Agua")}><Droplets />Agua</button>
                  <button onClick={() => addActivityMarker("viewpoint", "Mirador")}><Eye />Mirador</button>
                  <button onClick={() => addActivityMarker("camp", "Campamento")}><TentTree />Campamento</button>
                  <button onClick={() => addActivityMarker("danger", "Precaución")}><AlertTriangle />Precaución</button>
                </div>
              </div>
            )}
            <div className="record-buttons">
              {!recording ? (
                <button className="record-main" onClick={startRecording}>
                  <Play />
                  {activityId
                    ? "Continuar actividad recuperada"
                    : recordingMode === "free" ? "Iniciar grabación libre" : "Iniciar ruta"}
                </button>
              ) : (
                <>
                  <button className="record-secondary" onClick={togglePause}>
                    {recordingPaused ? <Play /> : <Pause />}
                    {recordingPaused ? "Continuar" : "Pausar"}
                  </button>
                  <button className="record-stop" onClick={() => setFinishDialogOpen(true)}>
                    <Square />
                    Finalizar
                  </button>
                </>
              )}
            </div>
            {!recording && track.length > 0 && (
              <div className="activity-result-panel">
                <header><BadgeCheck /><span><strong>Resumen listo</strong><small>{formatDistance(liveStats.recordedDistanceKm)} km · {formatDuration(seconds)} · +{liveStats.ascentM} m</small></span></header>
                <ActivitySplitsReport points={track} />
                <div className="activity-export-row">
                  <button onClick={() => exportRecordedActivity("gpx")}><Download />GPX</button>
                  <button onClick={() => exportRecordedActivity("geojson")}><FileArchive />GeoJSON</button>
                  <button onClick={prepareRecordedRoute}><Route />Publicar ruta</button>
                </div>
                <div className="share-card-settings">
                  <label>Formato<select value={shareFormat} onChange={(event) => setShareFormat(event.target.value as ShareCardFormat)}><option value="portrait">Publicación 4:5</option><option value="story">Historia 9:16</option><option value="square">Cuadrado 1:1</option></select></label>
                  <label>Fondo<select value={shareBackground} onChange={(event) => setShareBackground(event.target.value as ShareCardBackground)}><option value="thera">Diseño THERA</option><option value="map">Mapa / relieve</option><option value="photo">Mi fotografía</option><option value="transparent">PNG transparente</option></select></label>
                  <label className="share-photo-input">Fotografía<input type="file" accept="image/jpeg,image/png,image/webp" onChange={selectSharePhoto} /></label>
                  <label className="privacy-toggle"><input type="checkbox" checked={hideShareEndpoints} onChange={(event) => setHideShareEndpoints(event.target.checked)} />Ocultar 200 m del inicio y final</label>
                </div>
                <button className="share-result-button" onClick={shareActivityCard}><Share2 />Crear y compartir PNG</button>
              </div>
            )}
            <div className="live-share-status" aria-disabled={!LIVE_SHARE_ENABLED}>
              <ShieldCheck />
              <span><strong>Ubicación en vivo para contacto de seguridad</strong><small>{LIVE_SHARE_ENABLED ? "Función beta habilitada" : "Preparada y desactivada por seguridad"}</small></span>
              <LockKeyhole />
            </div>
            {activityHistory.length > 0 && (
              <details className="activity-history">
                <summary>Historial en este dispositivo · {activityHistory.length}</summary>
                {activityHistory.slice(0, 5).map((activity) => (
                  <details className="activity-history-item" key={activity.id}>
                    <summary>
                      <Route />
                      <span><strong>{activity.routeName}</strong><small>{new Date(activity.startedAt).toLocaleDateString("es-EC")} · {formatDistance(activity.stats.recordedDistanceKm)} km</small></span>
                      <b>{activity.syncStatus === "synced" ? "Sincronizada" : "Local"}</b>
                    </summary>
                    <ActivitySplitsReport points={activity.points} compact />
                  </details>
                ))}
              </details>
            )}
            </>}
            <div className="limitation-note">
              {backgroundRecordingAvailable() ? <Smartphone /> : <WifiOff />}
              <p>
                <strong>{backgroundRecordingAvailable() ? "Android preparado:" : "Web/PWA:"}</strong> el recorrido se conserva en el dispositivo y utiliza el mismo flujo de rutas en web y Android.
                {!backgroundRecordingAvailable() && " En Android, el puente nativo permitirá continuar con la pantalla bloqueada."}
              </p>
            </div>
          </aside>
        </TabsContent>

        <TabsContent value="escalada" className="climbing-page">
          <section className="climbing-map">
            <TopographicMap route={selectedRoute} climbing />
          </section>
          <aside className="climbing-panel">
            <p className="eyebrow">ESCALADA EN ROCA Y RÁPEL</p>
            <h1>
              Encuentra el punto. Verifica las condiciones. Decide con
              información.
            </h1>
            <p>
              Los marcadores comunitarios deben incluir aproximación,
              dificultad, altura, anclajes, advertencias y fecha de revisión.
              Ningún punto será público sin tu aprobación.
            </p>
            <div className="climbing-list">
              {climbingPlaces.map((place) => (
                <article key={place.id}>
                  <span>
                    <Mountain />
                  </span>
                  <div>
                    <p>{place.area}</p>
                    <h2>{place.name}</h2>
                    <strong>{place.kind}</strong>
                    <small>{place.access}</small>
                  </div>
                  <ChevronRight />
                </article>
              ))}
            </div>
            <button
              className="orange-cta"
              onClick={() =>
                setToast(
                  "El punto se guardará como borrador privado y pasará a revisión administrativa.",
                )
              }
            >
              <MapPinned /> Proponer un punto
            </button>
          </aside>
        </TabsContent>

        <TabsContent value="comunidad" className="community-page">
          <section className="community-feed">
            <div className="feature-copy compact">
              <p className="eyebrow">COMUNIDAD THERA</p>
              <h1>
                Lo que aprendiste en la ruta puede preparar a quien viene
                después.
              </h1>
              <p className="community-disclaimer">
                Ejemplos de contenido para visualizar el flujo. Los perfiles
                reales mostrarán únicamente nombre e inicial.
              </p>
            </div>
            {communityActivity.map((item) => (
              <article className="community-card" key={item.name}>
                <div className="community-avatar">{item.initials}</div>
                <div>
                  <p>
                    <strong>{item.name}</strong> {item.action}
                  </p>
                  <h2>{item.route}</h2>
                  <span>{item.stat}</span>
                </div>
                <button aria-label="Guardar publicación">
                  <Heart />
                </button>
              </article>
            ))}
          </section>
          <aside className="photo-lab">
            <div className="photo-lab-icon">
              <Camera />
            </div>
            <p className="eyebrow">FOTOGRAFÍAS LIGERAS Y PRIVADAS</p>
            <h2>Sube una vez. THERA optimiza por ti.</h2>
            <p>
              Se redimensionan a 1920 px, se convierten a WebP y se vuelven a
              codificar para eliminar los metadatos habituales antes de
              enviarlas.
            </p>
            <label className="upload-zone">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={convertPhotos}
              />
              <Upload />
              <strong>Seleccionar fotografías</strong>
              <span>JPG, PNG o WebP · hasta 6</span>
            </label>
            <div className="photo-message">
              <ShieldCheck />
              {photoMessage}
            </div>
            {photoResults.length > 0 && (
              <div className="photo-grid">
                {photoResults.map((photo) => (
                  <div className="photo-result" key={photo.url}>
                    <img
                      src={photo.url}
                      alt={`Vista previa de ${photo.name}`}
                    />
                    <div>
                      <span>
                        Original <strong>{photo.before}</strong>
                      </span>
                      <span>
                        WebP <strong>{photo.after}</strong>
                      </span>
                    </div>
                    <p>
                      <Check /> Lista
                    </p>
                  </div>
                ))}
              </div>
            )}
          </aside>
        </TabsContent>

        <TabsContent value="offline" className="offline-page offline-page-compact">
          <section className="offline-intro compact-offline-intro">
            <div>
              <p className="eyebrow">MAPAS SIN CONEXIÓN</p>
              <h1>Descarga solo lo que necesitas.</h1>
              <p>{isAndroidApp
                ? "Elige una región. THERA guarda mapa, relieve 3D y curvas como un solo paquete lógico para usarlo sin señal."
                : "En la web el mapa nacional se consulta en línea por partes; las descargas regionales quedan reservadas para la app Android."}
              </p>
            </div>
          </section>

          <details className="offline-manager">
            <summary className="offline-manager-summary">
              <span className="offline-manager-icon"><CloudDownload /></span>
              <span className="offline-manager-copy">
                <strong>Mapas sin conexión</strong>
                <small>
                  {platformOfflinePacks.filter((pack) => Boolean(offlinePackState[pack.id]?.usable)).length} descargados · {activeDownloads.length ? `${activeDownloads.length} en preparación` : "listo para administrar"}
                </small>
              </span>
              <span className="offline-manager-status">
                {networkOnline ? "En línea" : "Sin conexión"}
                <ChevronDown />
              </span>
            </summary>

            <div className="offline-manager-panel">
              <div className="offline-manager-health">
                <span><LocateFixed /></span>
                <div>
                  <strong>GPS disponible sin internet</strong>
                  <small>Las regiones instaladas mantienen mapa, relieve y curvas en el dispositivo.</small>
                </div>
              </div>

              {isAndroidApp && (
                <div className="offline-active-pack">
                  <div className="offline-active-pack-copy">
                    <strong>Mapa offline activo</strong>
                    <small>Selecciona cualquier región instalada y ábrela directamente en Explorar.</small>
                  </div>
                  {installedOfflinePacks.length > 0 ? (
                    <div className="offline-active-pack-controls">
                      <select
                        aria-label="Seleccionar mapa offline instalado"
                        value={installedOfflinePacks.some((pack) => pack.id === offlineQuickPickId) ? offlineQuickPickId : installedOfflinePacks[0].id}
                        onChange={(event) => setOfflineQuickPickId(event.target.value)}
                      >
                        {installedOfflinePacks.map((pack) => (
                          <option key={pack.id} value={pack.id}>{pack.name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          const fallbackId = installedOfflinePacks[0]?.id;
                          const id = installedOfflinePacks.some((pack) => pack.id === offlineQuickPickId) ? offlineQuickPickId : fallbackId;
                          if (id) openOfflinePack(id);
                        }}
                      >
                        <MapPinned /> Usar mapa
                      </button>
                    </div>
                  ) : (
                    <small className="offline-active-pack-empty">Descarga una región para poder seleccionarla aquí.</small>
                  )}
                </div>
              )}

              {platformOfflineGroups.map((group) => {
                const groupPacks = platformOfflinePacks.filter((pack) => pack.group === group);
                const installedCount = groupPacks.filter((pack) => Boolean(offlinePackState[pack.id]?.usable)).length;
                return (
                  <details className="offline-region-accordion" key={group}>
                    <summary>
                      <span>
                        <strong>{group === "Provincia" ? "Mapas provinciales" : group}</strong>
                        <small>{installedCount}/{groupPacks.length} descargados</small>
                      </span>
                      <ChevronDown />
                    </summary>

                    <div className="offline-region-list">
                      {groupPacks.map((pack) => {
                        const value = packProgress[pack.id] ?? 0;
                        const remote = packRemoteInfo[pack.id];
                        const unavailable = remote?.checked && !remote.available;
                        return (
                          <article className={`offline-region-row ${unavailable ? "future" : ""}`} key={pack.id}>
                            <div className="offline-region-main">
                              <span className="offline-region-pin"><MapPinned /></span>
                              <div>
                                <strong>{pack.name}</strong>
                                <small>{pack.provinces.length > 0 ? pack.provinces.join(" · ") : pack.detail}</small>
                                <em>{value === 100
                                  ? "Disponible sin conexión · paquete verificado"
                                  : offlinePackState[pack.id]?.map || offlinePackState[pack.id]?.terrain || offlinePackState[pack.id]?.contours
                                    ? "Paquete incompleto · completar descarga"
                                    : remote?.available
                                      ? `${formatBytes(remote.size)} · mapa + relieve 3D${remote.contourAvailable ? " + curvas" : ""}`
                                      : remote?.checked ? "Pendiente en R2" : "Verificando disponibilidad…"}</em>
                              </div>
                            </div>

                            {value > 0 && value < 100 && (
                              <div className="offline-row-progress">
                                <Progress value={value} />
                                <small>{value}%</small>
                              </div>
                            )}

                            {!isAndroidApp ? (
                              <button className="downloaded" type="button" disabled>
                                <Check /> Disponible en línea
                              </button>
                            ) : offlinePackState[pack.id]?.usable ? (
                              <div className="android-pack-actions compact-pack-actions">
                                <button
                                  className={`downloaded ${selectedMapPackId === pack.id ? "active-pack" : ""}`}
                                  type="button"
                                  onClick={() => openOfflinePack(pack.id)}
                                >
                                  <MapPinned /> {selectedMapPackId === pack.id ? "En uso" : "Usar"}
                                </button>
                                {value !== 100 && (
                                  <button
                                    className="offline-download-button"
                                    type="button"
                                    disabled={Boolean(unavailable) || value > 0}
                                    onClick={() => downloadPack(pack.id, pack.file)}
                                  >
                                    {value > 0
                                      ? <><Download /> Preparando {value}%</>
                                      : <><Download /> Completar</>}
                                  </button>
                                )}
                                <button className="remove-pack" type="button" onClick={() => removeOfflinePack(pack.id, pack.file)}>
                                  <Trash2 /> Eliminar
                                </button>
                              </div>
                            ) : (
                              <button
                                className="offline-download-button"
                                disabled={Boolean(unavailable) || value > 0}
                                onClick={() => downloadPack(pack.id, pack.file)}
                              >
                                {value > 0 ? <><Download /> Preparando {value}%</>
                                  : unavailable ? <><Bell /> Pendiente de cargar</>
                                    : <><Download /> Descargar</>}
                              </button>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  </details>
                );
              })}

              <div className="offline-manager-note">
                <ShieldCheck />
                <p><strong>Cartografía responsable.</strong> Los paquetes regionales se mantienen en PMTiles, R2 y con atribución visible a OpenStreetMap.</p>
              </div>
            </div>
          </details>

          {activeDownloads.length > 0 && (
            <div className="download-toast">
              <Download />
              <div>
                <strong>Preparando la experiencia offline</strong>
                <span>{realMapsEnabled ? "Descarga segura desde R2 al dispositivo." : "Esta fase simula el progreso hasta conectar R2."}</span>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="ai" className="feature-page thera-module-tab-content">
          <TheraMountainAIPage snapshot={mountainAISnapshot} />
        </TabsContent>

        <TabsContent value="ar" className="feature-page thera-module-tab-content thera-ar-tab-content">
          <TheraARExplorePage
            routeContext={{
              activityId,
              routeId: recordingRouteId,
              routeName: recordingRouteName,
              recordingMode: recordingMode ?? null,
              startedAt: activityStartedAt,
              state: recordingPaused ? "paused" : recording ? "recording" : "idle",
              distanceMeters: Math.max(0, distance * 1000),
              track,
            } satisfies FieldRouteContext}
            onClose={isAndroidUi ? () => {
              setAndroidExplorePanelSection(null);
              setAndroidExplorePanelOpen(false);
              navigateToTab("explorar");
            } : undefined}
          />
          <NatureR03AppBridge />
        </TabsContent>

        <TabsContent value="360" className="feature-page thera-module-tab-content">
          <Thera360RoutesPage />
        </TabsContent>

        {miMapaEnabled && isApprovedMember && currentProfile && (() => {

          const miMapaPack = isAndroidApp ? selectedMapPack : activeMapPack;

          return (

            <TabsContent value="mi-mapa" className="feature-page thera-module-tab-content">

              <MiMapaPage

                ownerId={currentProfile.id}

                accessAllowed={true}

                online={networkOnline}

                isAndroidApp={isAndroidApp}

                mapFile={miMapaPack.file}

                mapCenter={miMapaPack.center}

                mapZoom={miMapaPack.zoom}

                regions={isAndroidApp

                  ? ECUADOR_MAP_PACKS.map((pack) => ({ id: pack.id, name: pack.name }))

                  : []}

                selectedRegion={isAndroidApp ? selectedMapPack.id : undefined}

                onRegionChange={isAndroidApp ? chooseMapPack : undefined}

                renderMap={renderMiMapaMap}

                onClose={() => navigateToTab("explorar")}

              />

            </TabsContent>

          );

        })()}

        <TabsContent value="perfil" className="profile-page">
          <section className="access-panel">
            <div className="feature-copy compact">
              <p className="eyebrow">ACCESO PRIVADO</p>
              <h1>Entra a una comunidad que cuida cada ruta.</h1>
              <p>
                El correo se verifica y cada solicitud la aprueba el
                administrador. Sin documentos de identidad y sin exponer datos
                personales.
              </p>
            </div>
            <div className="auth-switch" aria-label="Acceso a THERA">
              <button className={authMode === "register" ? "active" : ""} onClick={() => { setAuthMode("register"); setPasswordFlow("login"); }}>Solicitar acceso</button>
              <button className={authMode === "login" ? "active" : ""} onClick={() => { setAuthMode("login"); setPasswordFlow("login"); }}>Ya tengo cuenta</button>
            </div>
            {authMode === "login" ? (
              passwordFlow === "update" ? <form className="access-form password-recovery-form" onSubmit={submitPasswordUpdate}>
                <div className="form-intro">
                  <span><KeyRound /></span>
                  <div><strong>Crea una nueva contraseña</strong><small>El enlace del correo ya fue verificado.</small></div>
                </div>
                <label>Nueva contraseña
                  <span className="password-control">
                    <input type={showPassword ? "text" : "password"} name="password" autoComplete="new-password" minLength={10} required />
                    <button type="button" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>
                      {showPassword ? <EyeOff /> : <Eye />}
                    </button>
                  </span>
                </label>
                <label>Confirmar contraseña
                  <span className="password-control">
                    <input type={showPassword ? "text" : "password"} name="passwordConfirmation" autoComplete="new-password" minLength={10} required />
                    <button type="button" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>
                      {showPassword ? <EyeOff /> : <Eye />}
                    </button>
                  </span>
                </label>
                <button type="submit" className="orange-cta" disabled={authBusy}><ShieldCheck /> {authBusy ? "Actualizando…" : "Guardar nueva contraseña"}</button>
                <p className="form-note">Usa al menos 10 caracteres. Después volverás a iniciar sesión.</p>
              </form> : passwordFlow === "request" ? <form className="access-form password-recovery-form" onSubmit={submitPasswordResetRequest}>
                <div className="form-intro">
                  <span><Mail /></span>
                  <div><strong>Recuperar contraseña</strong><small>Te enviaremos un enlace seguro al correo registrado.</small></div>
                </div>
                <label>Correo electrónico<input type="email" value={authEmail} onChange={(event) => setAuthEmail(event.target.value)} autoComplete="email" required /></label>
                <button type="submit" className="orange-cta" disabled={authBusy}><Mail /> {authBusy ? "Enviando…" : "Enviar enlace de recuperación"}</button>
                <button type="button" className="auth-text-button" onClick={() => setPasswordFlow("login")}>Volver a iniciar sesión</button>
              </form> : signedInEmail ? <div className="success-panel">
                <BadgeCheck />
                <h2>Sesión activa</h2>
                <p>{signedInEmail}</p>
                <p>
                  {profileBusy
                    ? "Comprobando permisos…"
                    : isAdmin
                      ? "Administrador aprobado"
                      : currentProfile?.status === "approved"
                        ? "Miembro aprobado"
                        : currentProfile?.status === "rejected"
                          ? "Solicitud rechazada"
                          : "Solicitud pendiente de aprobación"}
                </p>
                {isAdmin && !isAndroidUi && (
                  <button onClick={() => setActiveTab("admin")}>Abrir administración</button>
                )}
                <button onClick={closeSession}>Cerrar sesión</button>
              </div> : <form className="access-form" onSubmit={submitLogin}>
                <label>Correo electrónico<input type="email" name="email" value={authEmail} onChange={(event) => setAuthEmail(event.target.value)} autoComplete="email" required /></label>
                <label>Contraseña
                  <span className="password-control">
                    <input type={showPassword ? "text" : "password"} name="password" autoComplete="current-password" minLength={10} required />
                    <button type="button" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>
                      {showPassword ? <EyeOff /> : <Eye />}
                    </button>
                  </span>
                </label>
                <button type="button" className="forgot-password" onClick={() => setPasswordFlow("request")}>¿Olvidaste tu contraseña?</button>
                <button type="submit" className="orange-cta" disabled={authBusy}><KeyRound /> {authBusy ? "Comprobando…" : "Iniciar sesión"}</button>
                <p className="form-note">Después de verificar el correo, tu perfil debe estar aprobado por el administrador.</p>
              </form>
            ) : !accessSent ? (
              <form className="access-form" onSubmit={submitAccess}>
                <div className="form-grid">
                  <label>
                    Nombres
                    <input
                      name="firstNames"
                      autoComplete="given-name"
                      required
                    />
                  </label>
                  <label>
                    Apellidos
                    <input
                      name="lastNames"
                      autoComplete="family-name"
                      required
                    />
                  </label>
                  <label>
                    Edad
                    <input
                      type="number"
                      name="age"
                      inputMode="numeric"
                      min="18"
                      max="100"
                      required
                    />
                  </label>
                  <label>
                    País
                    <input
                      name="country"
                      autoComplete="country-name"
                      defaultValue="Ecuador"
                      required
                    />
                  </label>
                  <label>
                    Ciudad
                    <input name="city" autoComplete="address-level2" required />
                  </label>
                  <label>
                    Correo electrónico
                    <input
                      type="email"
                      name="email"
                      autoComplete="email"
                      required
                    />
                  </label>
                  <label>
                    Contraseña
                    <span className="password-control">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        autoComplete="new-password"
                        minLength={10}
                        required
                      />
                      <button type="button" onClick={() => setShowPassword((current) => !current)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}>
                        {showPassword ? <EyeOff /> : <Eye />}
                      </button>
                    </span>
                  </label>
                </div>
                <label className="check-line">
                  <input type="checkbox" required />
                  <span>
                    Declaro que tengo 18 años o más y que la edad indicada es
                    correcta.
                  </span>
                </label>
                <label className="check-line">
                  <input type="checkbox" name="legalAccepted" required />
                  <span>
                    He leído y acepto la <a href="/privacidad">política de privacidad</a>{" "}
                    y las <a href="/terminos">condiciones de uso</a>.
                  </span>
                </label>
                <label className="check-line">
                  <input type="checkbox" name="contributionAuthorized" />
                  <span>
                    Autorizo usar mis aportes (rutas, observaciones y fotografías) para mejorar mapas, rutas, información del terreno, Nature y otras herramientas de THERA. Es opcional; si no lo autorizo, THERA funciona normalmente.
                  </span>
                </label>
                <button type="submit" className="orange-cta" disabled={authBusy}>
                  <UserCheck /> {authBusy ? "Enviando…" : "Aceptar y continuar"}
                </button>
                <p className="form-note">
                  La solicitud quedará pendiente hasta que el administrador la
                  apruebe. Recibirás primero un correo de verificación.
                </p>
              </form>
            ) : (
              <div className="success-panel">
                <BadgeCheck />
                <h2>Solicitud preparada</h2>
                <p>
                  Revisa tu correo y confirma la cuenta. Después quedará
                  pendiente de aprobación administrativa.
                </p>
                <button onClick={() => setAccessSent(false)}>
                  Editar información
                </button>
              </div>
            )}
          </section>
          <aside className="profile-tools">
            <article>
              <Smartphone />
              <div>
                <h2>Instalar THERA</h2>
                <p>
                  Acceso rápido, pantalla completa y recursos esenciales
                  disponibles sin conexión.
                </p>
              </div>
              <button onClick={requestInstall}>Instalar PWA</button>
            </article>
            <article>
              <KeyRound />
              <div>
                <h2>Acceso seguro</h2>
                <p>
                  Correo verificado y aprobación manual conectados. MFA
                  administrativo se activará antes del lanzamiento público.
                </p>
              </div>
              <span>En progreso</span>
            </article>
            <article>
              <ShieldCheck />
              <div>
                <h2>Privacidad y datos</h2>
                <p>
                  Rutas privadas por defecto, exportación y eliminación de
                  cuenta.
                </p>
              </div>
              <a href="/privacidad">Revisar</a>
            </article>
            <article className="membership-off">
              <Sparkles />
              <div>
                <h2>Membresías</h2>
                <p>
                  La arquitectura quedará lista, pero los cobros permanecerán
                  desactivados.
                </p>
              </div>
              <span>Inactiva</span>
            </article>
            {isTerrainSenseEntryAvailable() && (
              <article className="terrain-sense-profile-entry">
                <Radar />
                <div>
                  <h2>Terrain Sense LAB</h2>
                  <p>Percepción experimental del entorno y reconstrucción local 3D.</p>
                  <small className="ts-profile-badge">LAB</small>
                </div>
                <button type="button" onClick={() => setActiveTab("terrain-sense")}>Abrir</button>
              </article>
            )}
            <SurveyConsentSettings
              visible={Boolean(signedInEmail && currentProfile)}
              identityKey={currentProfile?.id ?? signedInEmail ?? ""}
            />
            {isAdmin && !isAndroidUi && (
              <button
                className="admin-link"
                onClick={() => setActiveTab("admin")}
              >
                <LockKeyhole /> Abrir administración <ArrowUpRight />
              </button>
            )}
            {isAndroidUi && (
              <div className="android-profile-settings-entry" aria-label="Configuración">
                <Settings />
                <span>
                  <strong>Configuración</strong>
                  <small>Acceso reservado para preferencias de THERA.</small>
                </span>
                <ChevronRight />
              </div>
            )}
          </aside>
        </TabsContent>

        <TabsContent value="terrain-sense" className="feature-page thera-module-tab-content">
          <Suspense fallback={<div className="module-loading">Abriendo Terrain Sense LAB…</div>}>
            <TerrainSensePage
              context={{
                location: livePosition ? {
                  latitude: livePosition.latitude,
                  longitude: livePosition.longitude,
                  altitudeM: livePosition.altitude,
                  accuracyM: livePosition.accuracy,
                  timestamp: livePosition.timestamp,
                } : null,
                routeId: recordingRouteId,
                activityId,
              }}
              onClose={() => setActiveTab("perfil")}
            />
          </Suspense>
        </TabsContent>

        {!isAndroidUi && (
        <TabsContent value="admin" className="admin-page">
          <header>
            <div>
              <p className="eyebrow">SOLO ADMINISTRADOR</p>
              <h1>Control claro, decisiones tuyas.</h1>
              <p>
                Las solicitudes se cargan desde Supabase y cada aprobación queda
                protegida por tu rol administrativo y las políticas RLS.
              </p>
            </div>
            <span>
              <ShieldCheck /> RLS activo
            </span>
          </header>
          <AdminArchitecturePanel />
          <FieldSurveyContributionAdminPanel />
          <section className="admin-metrics">
            <article>
              <Users />
              <div>
                <strong>{pendingMembers.length}</strong>
                <span>Solicitudes pendientes</span>
              </div>
            </article>
            <article>
              <Route />
              <div>
                <strong>{pendingRoutes.length}</strong>
                <span>Rutas por revisar</span>
              </div>
            </article>
            <article>
              <Eye />
              <div>
                <strong>Privadas</strong>
                <span>Rutas nuevas por defecto</span>
              </div>
            </article>
            <article>
              <Gauge />
              <div>
                <strong>Inactiva</strong>
                <span>Membresía</span>
              </div>
            </article>
          </section>
          <section className="admin-grid">
            <article className="admin-card">
              <div className="admin-card-title">
                <UserCheck />
                <div>
                  <h2>Aprobar miembros</h2>
                  <p>Correo verificado antes de tu decisión.</p>
                </div>
              </div>
              {adminBusy ? (
                <p className="empty-state">Cargando solicitudes reales…</p>
              ) : pendingMembers.length ? (
                pendingMembers.map((member) => (
                  <div className="review-row" key={member.id}>
                    <span className="community-avatar">
                      {[member.first_names, member.last_names]
                        .map((part) => part.trim()[0] ?? "")
                        .join("")}
                    </span>
                    <div>
                      <strong>{member.first_names} {member.last_names}</strong>
                      <small>{member.age} años · {member.city}, {member.country}</small>
                      <small>{member.email} · {member.email_confirmed ? "correo verificado" : "correo sin verificar"}</small>
                    </div>
                    <button
                      disabled={!member.email_confirmed || adminActionId === member.id}
                      onClick={() => approveMember(member)}
                    >
                      {adminActionId === member.id
                        ? "Aprobando…"
                        : member.email_confirmed
                          ? "Aprobar"
                          : "Sin verificar"}
                    </button>
                  </div>
                ))
              ) : (
                <p className="empty-state">No hay solicitudes pendientes.</p>
              )}
            </article>
            <article className="admin-card">
              <div className="admin-card-title">
                <ClipboardCheck />
                <div>
                  <h2>Publicar contenido</h2>
                  <p>El miembro crea; tú decides qué se vuelve público.</p>
                </div>
              </div>
              {adminBusy ? (
                <p className="empty-state">Cargando rutas reales…</p>
              ) : pendingRoutes.length ? (
                pendingRoutes.map((route) => (
                  <div className="route-review-row" key={route.id}>
                    <span className="square-icon"><Route /></span>
                    <div>
                      <strong>{route.name}</strong>
                      <small>{route.owner_name} · {route.area}</small>
                      <small>{route.description || "Sin descripción adicional"}</small>
                      <small>
                        {route.distance_m === null ? "Distancia por validar" : `${formatDistance(route.distance_m / 1000)} km`} · +{route.ascent_m ?? 0} m · {route.file_count} archivos · {route.photo_count} fotografías · {route.source_platform === "android" ? "Android" : "Web"}
                      </small>
                    </div>
                    <div className="route-review-actions">
                      <button
                        disabled={routeAdminActionId === route.id}
                        onClick={() => decideRoute(route, "published")}
                      >
                        Publicar
                      </button>
                      <button
                        disabled={routeAdminActionId === route.id}
                        onClick={() => decideRoute(route, "rejected")}
                      >
                        Corregir
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-state">No hay rutas pendientes de revisión.</p>
              )}
            </article>
          </section>
          <div className="admin-footer">
            <FileArchive />
            <div>
              <strong>Aprobaciones conectadas a Supabase</strong>
              <span>
                El correo debe estar confirmado y cada decisión administrativa
                queda registrada en la auditoría.
              </span>
            </div>
            <Button variant="outline" onClick={() => setActiveTab("perfil")}>
              Volver al perfil
            </Button>
          </div>
        </TabsContent>
        )}
      </Tabs>

      {finishDialogOpen && (
        <div className="finish-dialog-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.currentTarget === event.target) setFinishDialogOpen(false);
        }}>
          <section className="finish-dialog" role="dialog" aria-modal="true" aria-labelledby="finish-dialog-title">
            <button className="finish-dialog-close" type="button" onClick={() => setFinishDialogOpen(false)} aria-label="Volver a la grabación"><X /></button>
            <span className="finish-dialog-icon"><Square /></span>
            <h2 id="finish-dialog-title">¿Qué deseas hacer?</h2>
            <p>La actividad actual todavía no ha terminado. Elige cómo quieres salir.</p>
            <button className="finish-save" type="button" onClick={finishRecordingAndSave}><Save />Finalizar y guardar</button>
            <button className="finish-discard" type="button" onClick={discardCurrentRecording}><Trash2 />Salir sin guardar</button>
            <button className="finish-return" type="button" onClick={() => setFinishDialogOpen(false)}>Volver a la grabación</button>
            <small>“Salir sin guardar” elimina únicamente el registro temporal actual.</small>
          </section>
        </div>
      )}

      {pendingPersonalMapActivityId && (
        <div className="finish-dialog-backdrop" role="presentation">
          <section
            className="finish-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mi-mapa-dialog-title"
          >
            <button
              className="finish-dialog-close"
              type="button"
              onClick={() => setPendingPersonalMapActivityId(null)}
              aria-label="Ahora no"
            >
              <X />
            </button>

            <span className="finish-dialog-icon">
              <Route />
            </span>

            <h2 id="mi-mapa-dialog-title">¿Guardar en Mi mapa?</h2>

            <p>
              La actividad ya fue guardada. Puedes añadirla a Mi mapa para verla
              junto a tus recorridos y progreso personal.
            </p>

            <button
              className="finish-save"
              type="button"
              onClick={() => {
                const pendingId = pendingPersonalMapActivityId;
                setPendingPersonalMapActivityId(null);

                if (!currentProfile || !pendingId) return;

                void setLocalBookmark(currentProfile.id, pendingId, true)
                  .then(() => setToast("Salida guardada en Mi mapa."))
                  .catch(() =>
                    setToast(
                      "La actividad se guardó, pero no fue posible añadirla a Mi mapa.",
                    ),
                  );
              }}
            >
              <Save />
              Guardar en Mi mapa
            </button>

            <button
              className="finish-return"
              type="button"
              onClick={() => setPendingPersonalMapActivityId(null)}
            >
              Ahora no
            </button>
          </section>
        </div>
      )}
      {toast && (
        <div className="app-toast" role="status">
          <Check />
          <span>{toast}</span>
          <button onClick={() => setToast("")} aria-label="Cerrar">
            <X />
          </button>
        </div>
      )}
      <footer className="legal-footer">
        <div>
          <img
            src="/brand/site/thera-logo-vertical-footer.webp"
            alt="THERA Rutas y Aventura"
          />
        </div>
        <span>Prepárate mejor. Explora con confianza.</span>
        <nav>
          <a href="/privacidad">Privacidad</a>
          <a href="/terminos">Condiciones</a>
          {isAdmin && !isAndroidUi && (
            <button onClick={() => setActiveTab("admin")}>Administración</button>
          )}
        </nav>
      </footer>
    </main>
  );
}
